
// Portfolio Tracker v2.1
/* eslint-disable */
import { useState, useEffect, useRef, Component } from "react";
import { createClient } from "@supabase/supabase-js";

// ── Shared globals (colours, i18n, formatters) ────────────────────────────────
import { C, fmt$, fmtPct, formatDate, responsiveFontSize } from "./components/shared/globals.js";
import BestWorstCard from "./components/charts/BestWorstCard.jsx";
import WatchlistPage from "./components/charts/WatchlistPage.jsx";
import RetirementPage from "./components/charts/RetirementPage.jsx";
import DividendPage from "./components/charts/DividendPage.jsx";
import CryptoMarketsPage, { MarketTickerDetail } from "./components/charts/CryptoMarketsPage.jsx";
import PriceAnimationStudio from "./components/charts/PriceAnimationStudio.jsx";

// ── Chart components ──────────────────────────────────────────────────────────
import { ChartContainer, PortfolioChart } from "./components/charts/PortfolioChart.jsx";
import PriceTradeChart        from "./components/charts/PriceTradeChart.jsx";
import PriceHistoryChart      from "./components/charts/PriceHistoryChart.jsx";
import HeatmapCalendar        from "./components/charts/HeatmapCalendar.jsx";
import MonthlyChart           from "./components/charts/MonthlyChart.jsx";
import TradeCalendar          from "./components/charts/TradeCalendar.jsx";
import DCAChart               from "./components/charts/DCAChart.jsx";
import TradeHistoryChart      from "./components/charts/TradeHistoryChart.jsx";
import PnLChart               from "./components/charts/PnLChart.jsx";
import AvgCostChart           from "./components/charts/AvgCostChart.jsx";
import MonthlyBarsChart       from "./components/charts/MonthlyBarsChart.jsx";
import SharesAccumulatedChart from "./components/charts/SharesAccumulatedChart.jsx";
import ProfitDonut            from "./components/charts/ProfitDonut.jsx";
import TradesGrid             from "./components/charts/TradesGrid.jsx";
import UnderwaterChart        from "./components/charts/UnderwaterChart.jsx";
import { TargetPrice, AvgCalcCompact } from "./components/charts/TargetAvgCalc.jsx";
import RankingChart      from "./components/charts/RankingChart.jsx";
import FearGreedChart    from "./components/charts/FearGreedChart.jsx";
import BuyZoneHeatmap         from "./components/charts/BuyZoneHeatmap.jsx";
import PriceLadder            from "./components/charts/PriceLadder.jsx";
import CostBasisDistribution  from "./components/charts/CostBasisDistribution.jsx";
import AllocationBar          from "./components/charts/AllocationBar.jsx";
import MonthlyWeeklyLollipop  from "./components/charts/MonthlyWeeklyLollipop.jsx";
import PerformanceAllocationChart from "./components/charts/PerformanceAllocationChart.jsx";
import SharesPerPeriod        from "./components/charts/SharesPerPeriod.jsx";
import BubbleChart            from "./components/charts/BubbleChart.jsx";

// ── DEMO DATA ────────────────────────────────────────────────────────────────
const DEMO_ASSETS = [
  { id:"demo_aapl",  ticker:"AAPL",  name:"Apple Inc.",             yahooTicker:"AAPL"     },
  { id:"demo_nvda",  ticker:"NVDA",  name:"NVIDIA Corp.",           yahooTicker:"NVDA"     },
  { id:"demo_mstr",  ticker:"MSTR",  name:"Strategy (MicroStrategy)",yahooTicker:"MSTR"    },
  { id:"demo_tsla",  ticker:"TSLA",  name:"Tesla Inc.",             yahooTicker:"TSLA"     },
  { id:"demo_msft",  ticker:"MSFT",  name:"Microsoft Corp.",        yahooTicker:"MSFT"     },
  { id:"demo_btc",   ticker:"BTC",   name:"Bitcoin",                yahooTicker:"BTC-USD"  },
  { id:"demo_eth",   ticker:"ETH",   name:"Ethereum",               yahooTicker:"ETH-USD"  },
  { id:"demo_sol",   ticker:"SOL",   name:"Solana",                 yahooTicker:"SOL-USD"  },
  { id:"demo_rune",  ticker:"RUNE",  name:"THORChain",              yahooTicker:"RUNE-USD" },
  { id:"demo_bnb",   ticker:"BNB",   name:"BNB",                    yahooTicker:"BNB-USD"  },
];

function makeDemoEntries(assetId, pricePerShare, seed) {
  // Deterministic pseudo-random using seed so data is consistent per asset
  const rng = (n) => { let x = Math.sin(seed * 9301 + n * 49297 + 233) * 2999; return x - Math.floor(x); };
  const entries = [];
  const usedDates = new Set();
  for (let i = 0; i < 10; i++) {
    // Random month offset (0-9 months ago)
    const monthsAgo = 9 - i;
    const d = new Date();
    d.setMonth(d.getMonth() - monthsAgo);
    // Random day 1-28 unique per entry
    let day = Math.floor(rng(i * 3) * 27) + 1;
    while (usedDates.has(`${d.getFullYear()}-${d.getMonth()}-${day}`)) {
      day = (day % 28) + 1;
    }
    usedDates.add(`${d.getFullYear()}-${d.getMonth()}-${day}`);
    d.setDate(day);
    const date = d.toISOString().split("T")[0];
    // Random dollars between $10 and $100
    const dollars = +(10 + rng(i * 7 + 1) * 90).toFixed(2);
    // Price varies slightly around historical estimate
    const priceFactor = 1 - (monthsAgo * 0.015) + (rng(i * 5 + 2) * 0.08 - 0.04);
    const buyPrice = +(pricePerShare * priceFactor);
    const shares = buyPrice > 0 ? +(dollars / buyPrice).toFixed(8) : 0;
    entries.push({ id:`${assetId}_e${i}`, date, dollars, shares });
  }
  return entries.sort((a, b) => a.date.localeCompare(b.date));
}

const DEMO_ENTRIES_MAP = {
  demo_aapl: makeDemoEntries("demo_aapl", 195,   1),
  demo_nvda: makeDemoEntries("demo_nvda", 850,   2),
  demo_mstr: makeDemoEntries("demo_mstr", 290,   3),
  demo_tsla: makeDemoEntries("demo_tsla", 220,   4),
  demo_msft: makeDemoEntries("demo_msft", 380,   5),
  demo_btc:  makeDemoEntries("demo_btc",  52000, 6),
  demo_eth:  makeDemoEntries("demo_eth",  2800,  7),
  demo_sol:  makeDemoEntries("demo_sol",  140,   8),
  demo_rune: makeDemoEntries("demo_rune", 7.5,   9),
  demo_bnb:  makeDemoEntries("demo_bnb",  380,   10),
};

const DEMO_WATCHLIST = [
  { id:"dw1", ticker:"COIN",  name:"Coinbase Global",   yahoo_ticker:"COIN",     asset_type:"stock",  target_price:280,   alert_price:null, alert_direction:null, note:null, created_at:"2024-01-01" },
  { id:"dw2", ticker:"PLTR",  name:"Palantir Technologies", yahoo_ticker:"PLTR", asset_type:"stock",  target_price:45,    alert_price:null, alert_direction:null, note:null, created_at:"2024-01-02" },
  { id:"dw3", ticker:"XRP",   name:"XRP",               yahoo_ticker:"XRP-USD",  asset_type:"crypto", target_price:2.5,   alert_price:null, alert_direction:null, note:null, created_at:"2024-01-03" },
  { id:"dw4", ticker:"AVAX",  name:"Avalanche",         yahoo_ticker:"AVAX-USD", asset_type:"crypto", target_price:55,    alert_price:null, alert_direction:null, note:null, created_at:"2024-01-04" },
  { id:"dw5", ticker:"MARA",  name:"MARA Holdings",     yahoo_ticker:"MARA",     asset_type:"stock",  target_price:35,    alert_price:null, alert_direction:null, note:null, created_at:"2024-01-05" },
];



class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  componentDidCatch(e, info) { console.error("APP CRASH:", e, info); }
  render() {
    if (this.state.error) return (
      <div style={{ background:"#0a0a0a", minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", padding:40 }}>
        <div style={{ fontFamily:"Montserrat,sans-serif", color:"#ef4444", maxWidth:600 }}>
          <div style={{ fontSize:14, fontWeight:700, marginBottom:16 }}>APP ERROR — copy this and send to developer:</div>
          <pre style={{ fontSize:11, color:"#f97316", whiteSpace:"pre-wrap", wordBreak:"break-all", background:"#111", padding:16, borderRadius:8 }}>
            {this.state.error?.toString()}{"\n"}{this.state.error?.stack}
          </pre>
          <button onClick={() => window.location.reload()} style={{ marginTop:16, padding:"10px 24px", background:"#F28705", border:"none", borderRadius:8, color:"#000", fontFamily:"Montserrat,sans-serif", fontWeight:700, cursor:"pointer" }}>
            RELOAD
          </button>
        </div>
      </div>
    );
    return this.props.children;
  }
}

// ── Supabase ──────────────────────────────────────────────────────────────────
const SUPABASE_URL = "https://cbdzscdnatqlguphjzhi.supabase.co";
const SUPABASE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImNiZHpzY2RuYXRxbGd1cGhqemhpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQyNjkzMDcsImV4cCI6MjA4OTg0NTMwN30.b06UWzTQZnDK-wxG2dBzl3mxtQrSdJxr--z0Qxd7jSk";
const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

// ── Mobile detection hook ─────────────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

// ── Storage shim (localStorage fallback) ─────────────────────────────────────
const storage = {
  get: async (key) => {
    try {
      const value = localStorage.getItem(key);
      if (value === null) throw new Error('not found');
      return { key, value };
    } catch(e) { throw e; }
  },
  set: async (key, value) => {
    try {
      localStorage.setItem(key, value);
      return { key, value };
    } catch(e) { return null; }
  },
  delete: async (key) => {
    localStorage.removeItem(key);
    return { key, deleted: true };
  },
  list: async (prefix) => {
    const keys = Object.keys(localStorage).filter(k => !prefix || k.startsWith(prefix));
    return { keys };
  }
};
if (!window.storage) window.storage = storage;

const SEED_DATA = [];
const emptyForm = { date: "", dollars: "", shares: "" };
const _o = C.orange;


// ── CSV Import Compact (header) ───────────────────────────────────────────────
function CSVImportCompact({ onImport, ticker = "", t = (k) => k }) {
  const [open, setOpen]       = useState(false);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError]     = useState("");
  const [success, setSuccess] = useState(false);
  const fileRef = useRef(null);

  const splitLine = (line) => {
    if (line.includes("\t")) return line.split("\t").map(p => p.trim().replace(/^["']|["']$/g, ""));
    const cols = []; let cur = "", inQuote = false;
    for (let ci = 0; ci < line.length; ci++) {
      const ch = line[ci];
      if (ch === '"' || ch === "\'") { inQuote = !inQuote; }
      else if (ch === "," && !inQuote) { cols.push(cur.trim()); cur = ""; }
      else { cur += ch; }
    }
    cols.push(cur.trim());
    return cols;
  };

  const parseCSV = (text) => {
    const lines = text.trim().split(/\r?\n/).filter(l => l.trim());
    const results = [], errors = [];
    const isDate = (s) => /^\d{1,4}[-\/\.]\d{1,2}[-\/\.]\d{2,4}$/.test(s.trim());
    for (let i = 0; i < lines.length; i++) {
      const parts = splitLine(lines[i]);
      if (parts.length < 3) continue;
      if (!isDate(parts[0])) continue;
      const rawDate = parts[0], rawDollars = parts[1], rawShares = parts[2];
      const dollars = parseFloat(rawDollars.replace(/[$,\s]/g, ""));
      const shares  = parseFloat(rawShares.replace(/[$,\s]/g, ""));
      if (isNaN(dollars) || isNaN(shares)) { errors.push("Row " + (i+1) + ": invalid numbers"); continue; }
      let iso = null;
      const d1 = rawDate.match(/^(\d{1,2})[\/](\d{1,2})[\/](\d{4})$/);
      const d2 = rawDate.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
      const d3 = rawDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (d1) iso = d1[3] + "-" + d1[1].padStart(2,"0") + "-" + d1[2].padStart(2,"0");
      else if (d2) iso = d2[3] + "-" + d2[2].padStart(2,"0") + "-" + d2[1].padStart(2,"0");
      else if (d3) iso = rawDate;
      else { errors.push("Row " + (i+1) + ": bad date"); continue; }
      results.push({ id: Date.now() + i, date: iso, dollars, shares });
    }
    return { results, errors };
  };

  const handleFile = (file) => {
    if (!file) return;
    setError(""); setSuccess(false); setPreview(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const { results, errors } = parseCSV(e.target.result);
      if (!results.length) { setError(errors[0] || "No valid data"); return; }
      setPreview({ results, errors });
    };
    reader.readAsText(file);
  };

  const fmt$ = (v) => "$" + v.toLocaleString("en-US", { minimumFractionDigits:2, maximumFractionDigits:2 });

  const doImport = (replace) => {
    onImport(preview.results, replace);
    setPreview(null); setOpen(false);
    setSuccess(true); setTimeout(() => setSuccess(false), 3000);
  };

  return (
    <div style={{ position:"relative" }}>
      <button
        onClick={() => { setOpen(o => !o); setPreview(null); setError(""); }}
        style={{ background: success ? "rgba(90,173,39,0.1)" : "transparent",
          border: "1px solid " + (success ? "#5aad27" : open ? C.orange : "#2a2a2a"),
          color: success ? "#5aad27" : open ? C.orange : "#555",
          borderRadius: 7, padding: "6px 10px", fontFamily:"Montserrat,sans-serif",
          fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", cursor: "pointer",
          transition: "all 0.15s", display:"flex", alignItems:"center", gap:8 }}>
        {success ? "IMPORTED" : "IMPORT CSV"}
      </button>

      {/* Modal overlay */}
      {open && (
        <div
          onClick={e => { if (e.target === e.currentTarget) { setOpen(false); setPreview(null); setError(""); }}}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:500,
            display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
          <div style={{ background:"var(--bg2)", border:`1px solid ${C.orange}`, borderRadius:14,
            padding:"28px 28px 24px", width:"100%", maxWidth:520, position:"relative" }}>

            {/* Header */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:13, fontWeight:700,
                  letterSpacing:"0.15em", color:"var(--text)" }}>IMPORT CSV</div>
                {ticker && (
                  <div style={{ background:"rgba(242,135,5,0.15)", border:`1px solid ${C.orange}`,
                    borderRadius:5, padding:"2px 8px", fontFamily:"Montserrat,sans-serif",
                    fontSize:11, fontWeight:900, color:C.orange, letterSpacing:"0.08em" }}>
                    {ticker}
                  </div>
                )}
              </div>
              <button onClick={() => { setOpen(false); setPreview(null); setError(""); }}
                style={{ background:"transparent", border:"1px solid var(--border)", color:"var(--text4)",
                  borderRadius:6, width:28, height:28, cursor:"pointer", fontSize:14,
                  display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
            </div>

            {/* Asset context banner */}
            {ticker && (
              <div style={{ background:"rgba(242,135,5,0.06)", border:"1px solid rgba(242,135,5,0.25)",
                borderRadius:8, padding:"10px 14px", marginBottom:18,
                display:"flex", alignItems:"flex-start", gap:10 }}>
                <span style={{ fontSize:14, flexShrink:0, marginTop:1 }}>⚠️</span>
                <div>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700,
                    color:"var(--text)", marginBottom:3 }}>
                    Importing data for <span style={{ color:C.orange }}>{ticker}</span> only
                  </div>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, color:"var(--text4)", lineHeight:1.5 }}>
                    This will only affect <strong style={{ color:"var(--text2)" }}>{ticker}</strong>. Other assets will not be changed.
                  </div>
                </div>
              </div>
            )}

            {!preview ? (
              <div>
                {/* CSV format info */}
                <div style={{ background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:10,
                  padding:"16px 18px", marginBottom:20 }}>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700,
                    letterSpacing:"0.25em", color:"var(--text4)", marginBottom:12 }}>CSV FORMAT</div>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:12, color:"var(--text2)",
                    lineHeight:1.7, marginBottom:12 }}>
                    The file must have <strong style={{ color:"var(--text)" }}>3 columns</strong> in this order:
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:12 }}>
                    {[
                      { col:"Column 1", name:"Date", desc:"YYYY-MM-DD, DD/MM/YYYY or DD.MM.YYYY" },
                      { col:"Column 2", name:"Invested ($)", desc:"Amount in USD" },
                      { col:"Column 3", name:"Shares / Tokens", desc:"Quantity purchased" },
                    ].map(r => (
                      <div key={r.col} style={{ display:"flex", alignItems:"baseline", gap:10 }}>
                        <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700,
                          letterSpacing:"0.15em", color:"var(--text5)", flexShrink:0, minWidth:56 }}>{r.col}</span>
                        <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:900,
                          color:C.orange, flexShrink:0, minWidth:110 }}>{r.name}</span>
                        <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:10,
                          color:"var(--text4)" }}>{r.desc}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ background:"var(--bg2)", borderRadius:6, padding:"10px 14px",
                    fontFamily:"monospace", fontSize:11, color:"var(--text3)", lineHeight:1.8 }}>
                    <div style={{ color:"var(--text5)", marginBottom:4, fontFamily:"Montserrat,sans-serif", fontSize:8, letterSpacing:"0.2em" }}>EXAMPLE</div>
                    <div>2024-01-15, 500.00, 2.5000</div>
                    <div>2024-02-20, 250.00, 1.2000</div>
                    <div>2024-03-10, 1000.00, 4.8000</div>
                  </div>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, color:"var(--text5)",
                    marginTop:10 }}>
                    Headers are optional and will be skipped automatically. Supported separators: comma, semicolon, tab.
                  </div>
                </div>

                {/* Drop zone */}
                <div
                  onDragOver={e => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
                  onClick={() => fileRef.current && fileRef.current.click()}
                  style={{ border:"2px dashed " + (dragging ? C.orange : "var(--border)"),
                    borderRadius:10, padding:"24px 16px", display:"flex", flexDirection:"column",
                    alignItems:"center", gap:8, cursor:"pointer", transition:"border-color 0.15s",
                    background: dragging ? "rgba(242,135,5,0.04)" : "transparent" }}>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700,
                    color: dragging ? C.orange : "var(--text3)" }}>
                    {"Drag or click"}
                  </div>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text5)" }}>
                    .csv · .tsv · .txt
                  </div>
                </div>
                <input ref={fileRef} type="file" accept=".csv,.txt,.tsv"
                  style={{ display:"none" }} onChange={e => handleFile(e.target.files[0])} />
                {error && (
                  <div style={{ marginTop:10, fontSize:10, color:C.red,
                    fontFamily:"Montserrat,sans-serif", padding:"8px 10px",
                    background:"rgba(224,62,26,0.08)", borderRadius:6 }}>{error}</div>
                )}
              </div>
            ) : (
            <div>
              {/* Preview summary */}
              <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, color:"var(--text2)", marginBottom:16 }}>
                <span style={{ color:"#5aad27", fontWeight:700 }}>{preview.results.length} {"records"}</span>
                {preview.errors.length > 0 && (
                  <span style={{ color:C.red }}> · {preview.errors.length} {"skipped"}</span>
                )}
                <div style={{ fontSize:10, marginTop:4, color:"var(--text3)" }}>
                  Total: {fmt$(preview.results.reduce((s,r) => s+r.dollars, 0))}
                </div>
              </div>

              {/* Action buttons */}
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>

                {/* Replace */}
                <button onClick={() => doImport(true)}
                  style={{ background:"rgba(224,62,26,0.07)", border:"1px solid rgba(224,62,26,0.5)",
                    color:C.red, borderRadius:8, padding:"12px 16px", cursor:"pointer",
                    textAlign:"left", transition:"all 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "rgba(224,62,26,0.14)"; e.currentTarget.style.borderColor = C.red; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(224,62,26,0.07)"; e.currentTarget.style.borderColor = "rgba(224,62,26,0.5)"; }}>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:900,
                    letterSpacing:"0.08em", marginBottom:4 }}>
                    🔴 REPLACE {ticker ? `all ${ticker} data` : "all data"}
                  </div>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text4)",
                    fontWeight:400, lineHeight:1.5 }}>
                    Deletes all existing trades for {ticker || "this asset"} and imports the new file.
                    {" "}<strong style={{ color:"rgba(224,62,26,0.8)" }}>Cannot be undone.</strong>
                  </div>
                </button>

                {/* Append */}
                <button onClick={() => doImport(false)}
                  style={{ background:"rgba(90,173,39,0.06)", border:"1px solid rgba(90,173,39,0.4)",
                    color:"#5aad27", borderRadius:8, padding:"12px 16px", cursor:"pointer",
                    textAlign:"left", transition:"all 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "rgba(90,173,39,0.13)"; e.currentTarget.style.borderColor = "#5aad27"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(90,173,39,0.06)"; e.currentTarget.style.borderColor = "rgba(90,173,39,0.4)"; }}>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:900,
                    letterSpacing:"0.08em", marginBottom:4 }}>
                    🟢 ADD to {ticker || "this asset"}
                  </div>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text4)",
                    fontWeight:400, lineHeight:1.5 }}>
                    Keeps all existing trades for {ticker || "this asset"} and appends the new records.
                  </div>
                </button>

                <button onClick={() => setPreview(null)}
                  style={{ background:"transparent", border:"1px solid var(--border)", color:"var(--text3)",
                    borderRadius:7, padding:"8px 16px", fontFamily:"Montserrat,sans-serif",
                    fontSize:11, cursor:"pointer" }}>
                  ← Back
                </button>
              </div>
            </div>
          )}
          </div>
        </div>
      )}
    </div>
  );
}



// ── Target Price ──────────────────────────────────────────────────────────────

// ── Sortable TH ──────────────────────────────────────────────────────────────
function SortTh({ label, colKey, sortState, onSort }) {
  const active = sortState.key === colKey;
  return (
    <th onClick={() => onSort(colKey)} style={{ ...st.th, cursor:"pointer", userSelect:"none", color: active ? C.orange : "#333" }}>
      <span style={{ display:"flex", alignItems:"center", gap:4 }}>
        {label}
        <span style={{ fontSize:8, opacity: active ? 1 : 0.25 }}>
          {active && sortState.dir === "desc" ? "▼" : "▲"}
        </span>
      </span>
    </th>
  );
}


// ── Login Page ────────────────────────────────────────────────────────────────
function LoginModal() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [closed, setClosed] = useState(false);
  if (closed) return null;

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError("");
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin }
    });
    if (error) { setError(error.message); setLoading(false); }
  };

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:9999,
      background:"rgba(0,0,0,0.82)",
      backdropFilter:"blur(4px)",
      WebkitBackdropFilter:"blur(4px)",
      display:"flex", alignItems:"center", justifyContent:"center",
      padding:20, fontFamily:"Montserrat,sans-serif",
    }}>
      <div style={{
        maxWidth:460, width:"100%",
        background:"#0d0d0d",
        border:"1px solid rgba(242,135,5,0.4)",
        borderRadius:20,
        overflow:"hidden",
        boxShadow:"0 0 60px rgba(242,135,5,0.15), 0 24px 60px rgba(0,0,0,0.7)",
      }}>

        {/* Orange top bar */}
        <div style={{
          background:"linear-gradient(135deg, #F28705 0%, #e07000 100%)",
          padding:"14px 24px 22px",
          textAlign:"center",
        }}>
          {/* X close button */}
          <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:4 }}>
            <button onClick={() => setClosed(true)} style={{
              background:"rgba(0,0,0,0.2)", border:"none", borderRadius:"50%",
              width:30, height:30, cursor:"pointer", display:"flex",
              alignItems:"center", justifyContent:"center", color:"#fff",
              fontSize:16, fontWeight:700, lineHeight:1, transition:"background 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.background="rgba(0,0,0,0.35)"}
            onMouseLeave={e => e.currentTarget.style.background="rgba(0,0,0,0.2)"}
            >×</button>
          </div>

          {/* SVG Logo white */}
          <div style={{ display:"flex", justifyContent:"center", marginBottom:20 }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 33.9" style={{ height:80, width:"auto" }}>
              <defs><style>{`.lcl0{fill:#fff}.lcl1{fill:rgba(255,255,255,0.5)}`}</style></defs>
              <g>
                <path className="lcl0" d="M14,14.5c.2-.2.4-.4.7-.5.3-.1.6-.2.9-.2s.7,0,1.1.2.6.4.9.7l1.5-1.3c-.4-.5-.9-.9-1.5-1.1-.6-.3-1.3-.4-2-.4s-1.2.1-1.8.3c-.5.2-1,.5-1.4.9-.4.4-.7.8-.9,1.3-.2.5-.3,1.1-.3,1.7s.1,1.2.3,1.7c.2.5.5,1,.9,1.3.4.4.9.7,1.4.9.5.2,1.1.3,1.8.3s1.4-.1,2-.4c.6-.3,1.1-.6,1.5-1.1l-1.5-1.3c-.3.3-.6.5-.9.7-.3.2-.7.2-1.1.2s-.6,0-.9-.2c-.3-.1-.5-.3-.7-.5-.2-.2-.4-.5-.5-.7-.1-.3-.2-.6-.2-1s0-.7.2-1c.1-.3.3-.5.5-.7Z"/>
                <path className="lcl0" d="M24.8,14.3c-.5-.3-1.2-.4-1.9-.4s-1.3.1-1.8.4c-.5.3-1,.7-1.3,1.1-.3.5-.5,1.1-.5,1.7s.2,1.2.5,1.7c.3.5.7.9,1.3,1.2s1.2.4,1.8.4,1.3-.1,1.9-.4c.5-.3,1-.7,1.3-1.2.3-.5.5-1.1.5-1.7s-.2-1.2-.5-1.7c-.3-.5-.7-.9-1.3-1.1ZM24.1,17.9c-.1.2-.3.4-.5.5-.2.1-.4.2-.7.2s-.5,0-.7-.2c-.2-.1-.4-.3-.5-.5-.1-.2-.2-.5-.2-.8s0-.6.2-.8c.1-.2.3-.4.5-.5s.4-.2.7-.2.5,0,.7.2.4.3.5.5.2.5.2.8,0,.6-.2.8Z"/>
                <path className="lcl0" d="M37.1,14.1c-.4-.2-.8-.3-1.3-.3s-1.1.1-1.6.4c-.2.2-.4.3-.6.6-.1-.2-.3-.4-.5-.6-.4-.3-.9-.4-1.5-.4s-.9.1-1.4.3c-.2.1-.4.3-.5.4v-.7h-2.1v6.3h2.2v-3.1c0-.3,0-.6.1-.8,0-.2.2-.4.4-.5.2-.1.4-.2.6-.2s.5.1.7.3c.2.2.3.5.3,1v3.3h2.2v-3.1c0-.3,0-.6.1-.8,0-.2.2-.4.4-.5.2-.1.4-.2.6-.2s.5.1.7.3c.2.2.3.5.3,1v3.3h2.2v-3.6c0-.7-.1-1.2-.3-1.6-.2-.4-.5-.7-.9-.9Z"/>
                <path className="lcl0" d="M45.3,14.2c-.5-.3-1-.4-1.6-.4s-1,.1-1.4.4c-.2.1-.3.2-.4.4v-.6h-2.1v8.6h2.2v-2.8c.1.1.2.2.4.3.4.2.8.4,1.4.4s1.1-.1,1.6-.4c.5-.3.8-.6,1.1-1.1.3-.5.4-1.1.4-1.7s-.1-1.2-.4-1.7c-.3-.5-.6-.9-1.1-1.1ZM44.4,17.9c-.1.2-.3.4-.5.5-.2.1-.4.2-.7.2s-.5,0-.7-.2c-.2-.1-.4-.3-.5-.5-.1-.2-.2-.5-.2-.8s0-.6.2-.8c.1-.2.3-.4.5-.5.2-.1.4-.2.7-.2s.5,0,.7.2.4.3.5.5c.1.2.2.5.2.8s0,.6-.2.8Z"/>
                <path className="lcl0" d="M52.8,14.5c-.6-.5-1.4-.7-2.4-.7s-1,0-1.5.2-.9.3-1.3.5l.7,1.5c.2-.2.5-.3.8-.4.3,0,.6-.1.9-.1.5,0,.8,0,1,.3.2.2.3.4.3.7h-1.3c-.7,0-1.2,0-1.6.2-.4.2-.7.4-.9.7-.2.3-.3.6-.3,1s0,.7.3,1c.2.3.4.5.8.7.4.2.8.3,1.3.3s1-.1,1.3-.3c.2-.1.4-.3.5-.6v.8h2.1v-3.5c0-1-.3-1.7-.8-2.2ZM51,18.8c-.2.1-.4.2-.7.2s-.5,0-.6-.2c-.1-.1-.2-.3-.2-.5s0-.4.2-.5c.1-.1.4-.2.8-.2h1v.5c0,.2-.2.4-.4.6Z"/>
                <path className="lcl0" d="M59.5,17c0,.4,0,.6-.2.9-.1.2-.3.4-.4.5-.2.1-.4.2-.6.2-.3,0-.6-.1-.8-.3-.2-.2-.3-.6-.3-1.1v-3.2h-2.2v3.5c0,.7.1,1.2.3,1.6.2.4.5.7,1,1,.4.2.9.3,1.4.3s.9-.1,1.3-.3c.2-.1.4-.3.5-.4v.7h2.1v-6.3h-2.2v3Z"/>
                <path className="lcl0" d="M68.6,14.1c-.4-.2-.8-.3-1.3-.3s-1,.1-1.4.3c-.2.1-.4.3-.6.5v-.7h-2.1v6.3h2.2v-3.1c0-.4,0-.6.2-.9.1-.2.3-.4.5-.5.2-.1.4-.2.6-.2s.6.1.8.3c.2.2.3.5.3,1v3.3h2.2v-3.6c0-.7-.1-1.2-.3-1.6-.2-.4-.5-.7-.9-.9Z"/>
                <path className="lcl0" d="M75.9,14.5c-.1-.1-.2-.2-.4-.3-.4-.2-.8-.4-1.4-.4s-1.1.1-1.6.4c-.5.3-.8.6-1.1,1.1-.3.5-.4,1.1-.4,1.7s.1,1.3.4,1.7c.3.5.7.9,1.1,1.1.5.3,1,.4,1.6.4s1-.1,1.4-.4c.2-.1.3-.2.4-.4v.6h2.1v-9.6h-2.2v3.8ZM75.7,17.9c-.1.2-.3.4-.5.5-.2.1-.4.2-.7.2s-.5,0-.7-.2c-.2-.1-.4-.3-.5-.5-.1-.2-.2-.5-.2-.8s0-.6.2-.8.3-.4.5-.5.4-.2.7-.2.5,0,.7.2c.2.1.4.3.5.5s.2.5.2.8,0,.6-.2.8Z"/>
                <rect className="lcl0" x="79.5" y="13.9" width="2.2" height="6.3"/>
                <path className="lcl0" d="M88.2,14.5c-.6-.5-1.4-.7-2.4-.7s-1,0-1.5.2-.9.3-1.3.5l.7,1.5c.2-.2.5-.3.8-.4.3,0,.6-.1.9-.1.5,0,.8,0,1,.3.2.2.3.4.3.7h-1.3c-.7,0-1.2,0-1.6.2-.4.2-.7.4-.9.7-.2.3-.3.6-.3,1s0,.7.3,1c.2.3.4.5.8.7.4.2.8.3,1.3.3s1-.1,1.3-.3c.2-.1.4-.3.5-.6v.8h2.1v-3.5c0-1-.3-1.7-.8-2.2ZM86.4,18.8c-.2.1-.4.2-.7.2s-.5,0-.6-.2c-.1-.1-.2-.3-.2-.5s0-.4.2-.5c.1-.1.4-.2.8-.2h1v.5c0,.2-.2.4-.4.6Z"/>
              </g>
              <circle className="lcl1" cx="80.6" cy="12.1" r="1.4"/>
            </svg>
          </div>
          <div style={{ fontSize:28, fontWeight:900, color:"#fff",
            letterSpacing:"-0.02em", lineHeight:1.2, marginBottom:8 }}>
            Know exactly where<br/>your money stands.
          </div>
          <div style={{ fontSize:13, color:"rgba(255,255,255,0.75)", lineHeight:1.6 }}>
            Track stocks and crypto in real time.
          </div>
        </div>

        {/* Bottom section — dark */}
        <div style={{ padding:"28px 32px 24px" }}>

          {/* Feature pills */}
          <div style={{ display:"flex", justifyContent:"center", gap:24, marginBottom:28, flexWrap:"wrap" }}>
            {[
              { icon:"ph-trend-up",    label:"LIVE PRICES" },
              { icon:"ph-lock-simple", label:"SECURE & PRIVATE" },
              { icon:"ph-chart-bar",   label:"P&L TRACKING" },
            ].map(f => (
              <div key={f.label} style={{ display:"flex", alignItems:"center", gap:6,
                fontSize:9, fontWeight:700, letterSpacing:"0.12em", color:"#888" }}>
                <i className={`ph ${f.icon}`} style={{ fontSize:14, color:"#F28705" }}/>
                {f.label}
              </div>
            ))}
          </div>

          {/* Google button */}
          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            style={{
              width:"100%", padding:"16px 24px", borderRadius:12,
              background: loading ? "#1a1a1a" : "#fff",
              border:"none", cursor: loading ? "not-allowed" : "pointer",
              display:"flex", alignItems:"center", justifyContent:"center", gap:12,
              transition:"all 0.2s", marginBottom:16,
              boxShadow:"0 4px 16px rgba(0,0,0,0.3)",
            }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.background="#f0f0f0"; }}
            onMouseLeave={e => { if (!loading) e.currentTarget.style.background="#fff"; }}
          >
            {!loading && (
              <svg width="22" height="22" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            )}
            {loading && (
              <div style={{ width:20, height:20, border:"2px solid #ddd",
                borderTopColor:"#F28705", borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
            )}
            <span style={{ fontSize:15, fontWeight:700, color: loading ? "#888" : "#111", letterSpacing:"0.03em" }}>
              {loading ? "Signing in..." : "Continue with Google"}
            </span>
          </button>

          {error && (
            <div style={{ fontSize:12, color:"#E03E1A", marginBottom:12, textAlign:"center" }}>{error}</div>
          )}

          <div style={{ fontSize:10, color:"#3a3a3a", lineHeight:1.8, textAlign:"center" }}>
            By continuing you agree to our Terms of Service.<br/>
            Your data is encrypted and never shared.
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Portfolio Overview Page ───────────────────────────────────────────────────
// ── Trade History Chart ───────────────────────────────────────────────────────

// ── useDragSort — shared drag-to-reorder hook ────────────────────────────────
// dataAttr: the data-* attribute name used on each item element (e.g. "dashId" → data-dash-id)
// Returns: { dragState, dragOverId, onMouseDragStart }
function useDragSort(order, setOrder, dataAttr) {
  const [dragState, setDragState] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const dragStateRef = useRef(null);
  const dragOverIdRef = useRef(null);

  const selector = `[data-${dataAttr}]`;
  const attrKey   = dataAttr.replace(/-([a-z])/g, (_, c) => c.toUpperCase()); // dash-id → dashId

  const onMouseDragStart = (e, id) => {
    const el = document.querySelector(`[data-${dataAttr}="${id}"]`);
    const rect = el ? el.getBoundingClientRect() : { left:0, top:0, width:600, height:80 };
    const state = { id, offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top,
                    x: rect.left, y: rect.top, width: rect.width, height: rect.height };
    dragStateRef.current = state;
    setDragState(state);
  };

  useEffect(() => {
    const onMove = e => {
      if (!dragStateRef.current) return;
      const { id, offsetX, offsetY, width, height } = dragStateRef.current;
      const newX = e.clientX - offsetX;
      const newY = e.clientY - offsetY;
      dragStateRef.current = { ...dragStateRef.current, x: newX, y: newY };
      setDragState(s => s ? { ...s, x: newX, y: newY } : null);
      const els = document.querySelectorAll(selector);
      let found = null;
      els.forEach(el => {
        const elId = el.dataset[attrKey];
        if (elId === id) return;
        const r = el.getBoundingClientRect();
        const midY = newY + height / 2;
        if (midY >= r.top && midY <= r.bottom) found = elId;
      });
      setDragOverId(found);
      dragOverIdRef.current = found;
    };
    const onUp = () => {
      if (!dragStateRef.current) return;
      const { id } = dragStateRef.current;
      const overId = dragOverIdRef.current;
      if (overId && overId !== id) {
        setOrder(prev => {
          const arr = [...prev];
          const from = arr.indexOf(id);
          const to   = arr.indexOf(overId);
          if (from !== -1 && to !== -1) { arr.splice(from, 1); arr.splice(to, 0, id); }
          return arr;
        });
      }
      dragStateRef.current = null;
      dragOverIdRef.current = null;
      setDragState(null);
      setDragOverId(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);

  return { dragState, dragOverId, onMouseDragStart };
}

function DashCollapsible({ id, title, children, onMouseDragStart, isDragging, isDragOver }) {
  const [open, setOpen] = useState(true);
  const [showHandle, setShowHandle] = useState(false);
  const isMobileLocal = typeof window !== "undefined" && window.innerWidth < 768;
  return (
    <div
      data-dash-id={id}
      style={{
        marginBottom: isMobileLocal ? 12 : 24,
        opacity: isDragging ? 0.3 : 1,
        transition:"opacity 0.15s, box-shadow 0.15s",
        boxShadow: isDragOver ? `0 0 0 2px ${C.orange}, 0 8px 32px rgba(242,135,5,0.12)` : "none",
        borderRadius:12,
        position:"relative",
      }}
      onMouseEnter={() => setShowHandle(true)}
      onMouseLeave={() => setShowHandle(false)}
    >
      <div style={{
        display:"flex", justifyContent:"space-between", alignItems:"center",
        padding: isMobileLocal ? "10px 12px" : "12px 20px", background:"var(--bg2)",
        border:"1px solid var(--border)",
        borderRadius: open ? "10px 10px 0 0" : 10,
        borderBottom: open ? "none" : "1px solid var(--border)",
        cursor:"pointer", userSelect:"none", position:"relative",
      }} onClick={() => setOpen(o => !o)}>
        {/* Drag handle */}
        <div
          onMouseDown={e => { e.preventDefault(); e.stopPropagation(); if (onMouseDragStart) onMouseDragStart(e, id); }}
          onClick={e => e.stopPropagation()}
          style={{
            position:"absolute", left:0, top:0,
            width:36, height:"100%", zIndex:10,
            cursor: showHandle ? "grab" : "default",
            display:"flex", alignItems:"center", justifyContent:"center",
            opacity: showHandle ? 1 : 0, transition:"opacity 0.2s",
          }}
        >
          <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
            {[0,1,2].map(i => <div key={i} style={{ width:14, height:1.5, borderRadius:1, background:"var(--text4)", opacity:0.6 }}/>)}
          </div>
        </div>
        <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, letterSpacing:"0.3em", color:"#888", paddingLeft:28 }}>{title}</div>
        <div style={{
          width:20, height:20, borderRadius:"50%",
          background: open ? "rgba(242,135,5,0.12)" : "var(--bg3)",
          border:`1px solid ${open ? C.orange : "var(--border)"}`,
          display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
        }}>
          <span style={{ fontSize:9, color: open ? C.orange : "var(--text4)", display:"block",
            transform: open ? "rotate(0deg)" : "rotate(-90deg)", transition:"transform 0.3s" }}>▼</span>
        </div>
      </div>
      <div style={{
        display:"grid", gridTemplateRows: open ? "1fr" : "0fr",
        transition:"grid-template-rows 0.35s cubic-bezier(0.4,0,0.2,1)",
        background:"var(--bg2)", border: open ? "1px solid var(--border)" : "none",
        borderTop:"none", borderRadius: open ? "0 0 10px 10px" : 0,
      }}>
        <div style={{ overflow: "hidden" }}>
          <div style={{
            padding: isMobileLocal ? "12px 12px 14px" : "16px 20px 20px",
            opacity: open ? 1 : 0, transform: open ? "translateY(0)" : "translateY(-8px)",
            transition: open ? "opacity 0.3s ease 0.05s, transform 0.3s ease 0.05s" : "opacity 0.2s ease",
          }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
function PortfolioPage({ assets, user, dashHidden = {}, setDashHidden = () => {}, dashBanner = null, isMobile = false }) {
  const [assetData, setAssetData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("value");
  const [sortDir, setSortDir] = useState("desc");
  const [hoveredDot, setHoveredDot] = useState(null);
  const DEFAULT_ORDER = ["summary","best_worst","performance","trade_history","allocation_bar","monthly_invested","trade_calendar","dca_efficiency","bubble_chart","breakdown","ranking","fear_greed"];
  const [dashOrder, setDashOrder] = useState(DEFAULT_ORDER);
  const { dragState, dragOverId, onMouseDragStart: onDashMouseDragStart } =
    useDragSort(dashOrder, setDashOrder, "dash-id");



  useEffect(() => {
    if (assets.length === 0) return;
    (async () => {
      setLoading(true);
      const results = await Promise.all(assets.map(async asset => {
        // Demo mode: use DEMO_ENTRIES_MAP instead of Supabase
        let entries = [];
        if (!user) {
          entries = DEMO_ENTRIES_MAP[asset.id] || [];
        } else {
          const { data } = await supabase
            .from("entries")
            .select("dollars, shares, date")
            .eq("user_id", user.id)
            .eq("asset_id", asset.id)
            .order("date", { ascending: true });
          entries = data || [];
        }
        const totalDollars = entries.reduce((s, e) => s + e.dollars, 0);
        const totalShares = entries.reduce((s, e) => s + e.shares, 0);
        const avgPrice = totalShares > 0 ? totalDollars / totalShares : 0;

        // Monthly invested
        const now = new Date();
        const thisMonth = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`;
        const monthlyInvested = entries.filter(e => e.date?.startsWith(thisMonth)).reduce((s,e) => s + e.dollars, 0);

        // Fetch live price + daily change
        let livePrice = 0, prevClose = 0;
        try {
          const res = await fetch(`/api/mstr-price?ticker=${encodeURIComponent(asset.yahooTicker || asset.ticker)}&t=${Date.now()}`);
          if (res.ok) { const d = await res.json(); livePrice = d.price || 0; prevClose = d.prev || 0; }
        } catch {}

        const currentValue = totalShares * livePrice;
        const pnl = currentValue - totalDollars;
        const pnlPct = totalDollars > 0 ? (pnl / totalDollars) * 100 : 0;
        const dailyChange = prevClose > 0 ? ((livePrice - prevClose) / prevClose) * 100 : 0;
        const dailyChangeValue = totalShares * (livePrice - prevClose);

        return { ...asset, totalDollars, totalShares, avgPrice, livePrice, prevClose,
          currentValue, pnl, pnlPct, dailyChange, dailyChangeValue, monthlyInvested, entries };
      }));
      setAssetData(results);
      setLoading(false);
    })();
  }, [assets, user]);

  const fmt$ = v => new Intl.NumberFormat("en-US", { style:"currency", currency:"USD", minimumFractionDigits:0, maximumFractionDigits:0 }).format(v);
  const fmt$2 = v => new Intl.NumberFormat("en-US", { style:"currency", currency:"USD", minimumFractionDigits:2 }).format(v);
  const fmtPct = v => (v >= 0 ? "+" : "") + v.toFixed(2) + "%";
  const COLORS = [C.orange, "#60a5fa", "#a78bfa", C.green, "#f472b6", "#34d399", "#fbbf24", "#fb7185"];

  const totalInvested = assetData.reduce((s, a) => s + a.totalDollars, 0);
  const totalValue = assetData.reduce((s, a) => s + a.currentValue, 0);
  const totalPnl = totalValue - totalInvested;
  const totalPnlPct = totalInvested > 0 ? (totalPnl / totalInvested) * 100 : 0;
  const totalDailyChange = assetData.reduce((s, a) => s + a.dailyChangeValue, 0);
  const totalMonthly = assetData.reduce((s, a) => s + a.monthlyInvested, 0);
  const sortedData = [...assetData].sort((a, b) => {
    const map = { value: "currentValue", pnl: "pnl", pnlPct: "pnlPct", invested: "totalDollars", daily: "dailyChange" };
    const key = map[sortKey] || "currentValue";
    return sortDir === "desc" ? b[key] - a[key] : a[key] - b[key];
  });
  const best = [...assetData].sort((a, b) => b.pnlPct - a.pnlPct)[0];
  const worst = [...assetData].sort((a, b) => a.pnlPct - b.pnlPct)[0];

  // Pie chart
  const PIE_R = 70, PIE_CX = 90, PIE_CY = 90;
  const pieSlices = (() => {
    let angle = -Math.PI / 2;
    return sortedData.map((a, i) => {
      const pct = totalValue > 0 ? a.currentValue / totalValue : 0;
      const sweep = pct * 2 * Math.PI;
      const x1 = PIE_CX + PIE_R * Math.cos(angle);
      const y1 = PIE_CY + PIE_R * Math.sin(angle);
      angle += sweep;
      const x2 = PIE_CX + PIE_R * Math.cos(angle);
      const y2 = PIE_CY + PIE_R * Math.sin(angle);
      const large = sweep > Math.PI ? 1 : 0;
      return { path: `M ${PIE_CX} ${PIE_CY} L ${x1} ${y1} A ${PIE_R} ${PIE_R} 0 ${large} 1 ${x2} ${y2} Z`, color: COLORS[i % COLORS.length], pct, name: a.ticker };
    });
  })();



  // perfByDate needed by TradeHistoryChart
  const perfByDate = (() => {
    const dateMap = {};
    assetData.forEach(a => {
      let cumShares = 0, cumDollars = 0;
      (a.entries || []).forEach(e => {
        cumShares += e.shares;
        cumDollars += e.dollars;
        if (!dateMap[e.date]) dateMap[e.date] = {};
        dateMap[e.date][a.id] = { shares: cumShares, dollars: cumDollars };
      });
    });
    const dates = Object.keys(dateMap).sort();
    const lastState = {};
    return dates.map(date => {
      Object.keys(dateMap[date]).forEach(id => { lastState[id] = dateMap[date][id]; });
      let tv = 0, ti = 0;
      assetData.forEach(a => {
        const s = lastState[a.id];
        if (s) { tv += s.shares * a.livePrice; ti += s.dollars; }
      });
      return { date, value: tv, invested: ti };
    });
  })();

  const handleSort = key => {
    if (sortKey === key) setSortDir(d => d === "desc" ? "asc" : "desc");
    else { setSortKey(key); setSortDir("desc"); }
  };
  const SortBtn = ({ k, label }) => (
    <th onClick={() => handleSort(k)} style={{
      padding:"10px 16px", fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700,
      letterSpacing:"0.2em", color: sortKey === k ? C.orange : "var(--text4)",
      textAlign:"right", borderBottom:"1px solid var(--border)", whiteSpace:"nowrap",
      cursor:"pointer", userSelect:"none",
    }}>{label} {sortKey === k ? (sortDir === "desc" ? "↓" : "↑") : ""}</th>
  );

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:200 }}>
      <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, letterSpacing:"0.3em", color:"var(--text4)" }}>LOADING...</div>
    </div>
  );

  return (
    <div style={{ cursor: dragState ? "grabbing" : "default", userSelect: dragState ? "none" : "auto" }}>
      {/* Floating drag clone */}
      {dragState && (
        <div style={{
          position: "fixed",
          left: dragState.x,
          top: dragState.y,
          width: dragState.width,
          zIndex: 9999,
          pointerEvents: "none",
          opacity: 0.85,
          transform: "scale(1.02)",
          boxShadow: `0 16px 48px rgba(0,0,0,0.5), 0 0 0 2px ${C.orange}`,
          borderRadius: 12,
          background: "var(--bg2)",
          border: "1px solid var(--border)",
          padding: "12px 20px",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}>
          <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
            {[0,1,2].map(i => <div key={i} style={{ width:14, height:1.5, borderRadius:1, background:C.orange, opacity:0.8 }}/>)}
          </div>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, letterSpacing:"0.3em", color:C.orange }}>
            {dragState.id.replace(/_/g," ").toUpperCase()}
          </div>
        </div>
      )}
      {/* Hero image */}
      {dashBanner && (
        <div style={{ width:"100%", height: isMobile ? 130 : 220, borderRadius: isMobile ? 8 : 12, overflow:"hidden", marginBottom: isMobile ? 16 : 24, border:"1px solid var(--border)" }}>
          <img src={dashBanner} alt="" style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"center", display:"block" }}/>
        </div>
      )}

      {/* ── Dashboard sections — rendered in dashOrder for drag-to-reorder ── */}
      {(() => {
        const allDashSections = {
          summary: (
            <DashCollapsible id="summary" title="SUMMARY" onMouseDragStart={onDashMouseDragStart} isDragging={dragState?.id === "summary"} isDragOver={dragOverId === "summary"}>
      <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fit, minmax(120px, 1fr))", gap: isMobile ? 8 : 12 }}>
        {[
          { label:"TOTAL INVESTED", num: totalInvested,    fmt: v => fmt$(v),              color:"var(--text)" },
          { label:"TOTAL VALUE",    num: totalValue,       fmt: v => fmt$(v),              color: totalValue >= totalInvested ? C.green : C.red },
          { label:"TOTAL P&L",      num: totalPnl,         fmt: v => fmt$(v), sub: fmtPct(totalPnlPct), color: totalPnl >= 0 ? C.green : C.red },
          { label:"TODAY",          num: totalDailyChange, fmt: v => fmt$(v), sub: totalDailyChange >= 0 ? "▲ today" : "▼ today", color: totalDailyChange >= 0 ? C.green : C.red },
          { label:"THIS MONTH",     num: totalMonthly,     fmt: v => fmt$(v),              color: C.orange },
          { label:"ASSETS",         num: assetData.length, fmt: v => Math.round(v),        color:"var(--text)" },
        ].map(({ label, num, fmt: fmtFn, sub, color }) => (
          <div key={label} style={{ flex:1, minWidth:120, background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:10, padding:"12px 16px" }}>
            <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, letterSpacing:"0.25em", color:"var(--text4)", marginBottom:6 }}>{label}</div>
            <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:responsiveFontSize(String(fmtFn(num)), 15), fontWeight:700, color }}>
              <AnimatedNumber value={num} format={fmtFn} duration={900} />
            </div>
            {sub && <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, fontWeight:700, color, marginTop:2 }}>{sub}</div>}
          </div>
        ))}
      </div>
      </DashCollapsible>
          ),
          best_worst: best && worst && best.id !== worst.id ? (
            <DashCollapsible id="best_worst" title="BEST · WORST PERFORMER" onMouseDragStart={onDashMouseDragStart} isDragging={dragState?.id === "best_worst"} isDragOver={dragOverId === "best_worst"}>
        <div style={{ display:"flex", flexDirection: isMobile ? "column" : "row", gap:12 }}>
          {[
            { label:" BEST PERFORMER", asset: best, color: C.green },
            { label:" WORST PERFORMER", asset: worst, color: C.red },
          ].map(({ label, asset, color }) => (
            <div key={label} style={{ flex:1, background:"var(--bg2)", border:`1px solid ${color}22`, borderRadius:10, padding:"14px 18px" }}>
              <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, letterSpacing:"0.2em", color:"var(--text4)", marginBottom:8 }}>{label}</div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:22, fontWeight:900, color: C.orange }}>{asset.ticker}</span>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:18, fontWeight:700, color }}>{fmtPct(asset.pnlPct)}</div>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, color }}>{fmt$(asset.pnl)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </DashCollapsible>
          ) : null,
          performance: (
            <DashCollapsible id="performance" title="PORTFOLIO PERFORMANCE · ALLOCATION" onMouseDragStart={onDashMouseDragStart} isDragging={dragState?.id === "performance"} isDragOver={dragOverId === "performance"}>
              <PerformanceAllocationChart
                assetData={assetData}
                sortedData={sortedData}
                totalValue={totalValue}
                totalPnl={totalPnl}
                COLORS={COLORS}
                isMobile={isMobile}
              />
            </DashCollapsible>
          ),
          trade_history: (
            <DashCollapsible id="trade_history" title="TRADE HISTORY" onMouseDragStart={onDashMouseDragStart} isDragging={dragState?.id === "trade_history"} isDragOver={dragOverId === "trade_history"}>
      <TradeHistoryChart assetData={assetData} perfByDate={perfByDate} totalPnl={totalPnl} COLORS={COLORS} />

      </DashCollapsible>
          ),
          allocation_bar: assetData.length > 0 && totalValue > 0 ? (
            <DashCollapsible id="allocation_bar" title="ALLOCATION" onMouseDragStart={onDashMouseDragStart} isDragging={dragState?.id === "allocation_bar"} isDragOver={dragOverId === "allocation_bar"}>
        <div style={{ overflowX:"auto", WebkitOverflowScrolling:"touch" }}><AllocationBar assetData={assetData} totalValue={totalValue} COLORS={COLORS} /></div>
      </DashCollapsible>
          ) : null,
          monthly_invested: (() => {
            const monthMap = {};
            const weekMap  = {};
            assetData.forEach(a => {
              (a.entries || []).forEach(e => {
                const month = e.date?.slice(0, 7);
                if (month) monthMap[month] = (monthMap[month] || 0) + e.dollars;
                const d = new Date(e.date);
                const day = d.getDay();
                const diff = day === 0 ? -6 : 1 - day;
                const mon = new Date(d); mon.setDate(d.getDate() + diff);
                const wk = mon.toISOString().split("T")[0];
                if (wk) weekMap[wk] = (weekMap[wk] || 0) + e.dollars;
              });
            });
            const monthlyData = Object.keys(monthMap).sort().map(m => ({ month: m, dollars: monthMap[m] }));
            const weeklyData  = Object.keys(weekMap).sort().map(w => ({ week: w, dollars: weekMap[w] }));
            if (!monthlyData.length && !weeklyData.length) return null;
            return (
              <DashCollapsible id="monthly_invested" title="MONTHLY · WEEKLY INVESTED" onMouseDragStart={onDashMouseDragStart} isDragging={dragState?.id === "monthly_invested"} isDragOver={dragOverId === "monthly_invested"}>
            <MonthlyWeeklyLollipop monthlyData={monthlyData} weeklyData={weeklyData} />
          </DashCollapsible>
            );
          })(),
          trade_calendar: assetData.length > 0 ? (
            <DashCollapsible id="trade_calendar" title="TRADE CALENDAR" onMouseDragStart={onDashMouseDragStart} isDragging={dragState?.id === "trade_calendar"} isDragOver={dragOverId === "trade_calendar"}>
              <TradeCalendar mode="dashboard" assetData={assetData} />
            </DashCollapsible>
          ) : null,

          dca_efficiency: assetData.length > 0 ? (
            <DashCollapsible id="dca_efficiency" title="DCA EFFICIENCY" onMouseDragStart={onDashMouseDragStart} isDragging={dragState?.id === "dca_efficiency"} isDragOver={dragOverId === "dca_efficiency"}>
              <DCAChart assetData={assetData} COLORS={COLORS} />
            </DashCollapsible>
          ) : null,

          bubble_chart: (
            <DashCollapsible id="bubble_chart" title="BUBBLE CHART · P&L vs ALLOCATION" onMouseDragStart={onDashMouseDragStart} isDragging={dragState?.id === "bubble_chart"} isDragOver={dragOverId === "bubble_chart"}>
        <BubbleChart assetData={assetData} totalValue={totalValue} isMobile={isMobile} />
      </DashCollapsible>
          ),
          breakdown: (
            <DashCollapsible id="breakdown" title="ASSET BREAKDOWN" onMouseDragStart={onDashMouseDragStart} isDragging={dragState?.id === "breakdown"} isDragOver={dragOverId === "breakdown"}>
      <div style={{ background:"var(--bg3)", borderRadius:8, overflow:"hidden" }}>
        <div style={{ overflowX:"auto", WebkitOverflowScrolling:"touch" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ background:"var(--bg3)" }}>
                <th style={{ padding:"10px 16px", fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, letterSpacing:"0.2em", color:"var(--text4)", textAlign:"left", borderBottom:"1px solid var(--border)", whiteSpace:"nowrap" }}>ASSET</th>
                <th style={{ padding:"10px 16px", fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, letterSpacing:"0.2em", color:"var(--text4)", textAlign:"right", borderBottom:"1px solid var(--border)", whiteSpace:"nowrap" }}>QTY</th>
                <th style={{ padding:"10px 16px", fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, letterSpacing:"0.2em", color:"var(--text4)", textAlign:"right", borderBottom:"1px solid var(--border)", whiteSpace:"nowrap" }}>PRICE</th>
                <th style={{ padding:"10px 16px", fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, letterSpacing:"0.2em", color:"var(--text4)", textAlign:"right", borderBottom:"1px solid var(--border)", whiteSpace:"nowrap", cursor:"pointer", color: sortKey==="daily" ? C.orange : "var(--text4)" }} onClick={() => handleSort("daily")}>DAY % {sortKey==="daily" ? (sortDir==="desc"?"↓":"↑") : ""}</th>
                <SortBtn k="invested" label="INVESTED"/>
                <SortBtn k="value" label="VALUE"/>
                <SortBtn k="pnl" label="P&L $"/>
                <SortBtn k="pnlPct" label="P&L %"/>
                <th style={{ padding:"10px 16px", fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, letterSpacing:"0.2em", color:"var(--text4)", textAlign:"right", borderBottom:"1px solid var(--border)" }}>ALLOC</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((a, i) => {
                const allocation = totalValue > 0 ? (a.currentValue / totalValue) * 100 : 0;
                return (
                  <tr key={a.id} style={{ background: i%2===0 ? "var(--row-even)" : "var(--row-odd)", borderBottom:"1px solid var(--border)" }}>
                    <td style={{ padding:"12px 16px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <div style={{ width:8, height:8, borderRadius:2, background:COLORS[i % COLORS.length], flexShrink:0 }}/>
                        <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:13, fontWeight:900, color:C.orange }}>{a.ticker}</span>
                      </div>
                    </td>
                    <td style={{ padding:"12px 16px", fontFamily:"Montserrat,sans-serif", fontSize:12, color:"var(--text2)", textAlign:"right" }}>{a.totalShares.toFixed(4)}</td>
                    <td style={{ padding:"12px 16px", fontFamily:"Montserrat,sans-serif", fontSize:12, color:C.orange, fontWeight:700, textAlign:"right" }}>{a.livePrice > 0 ? fmt$2(a.livePrice) : "—"}</td>
                    <td style={{ padding:"12px 16px", fontFamily:"Montserrat,sans-serif", fontSize:12, fontWeight:700, color: a.dailyChange >= 0 ? C.green : C.red, textAlign:"right" }}>{a.livePrice > 0 ? fmtPct(a.dailyChange) : "—"}</td>
                    <td style={{ padding:"12px 16px", fontFamily:"Montserrat,sans-serif", fontSize:12, color:"var(--text)", textAlign:"right" }}>{fmt$(a.totalDollars)}</td>
                    <td style={{ padding:"12px 16px", fontFamily:"Montserrat,sans-serif", fontSize:12, fontWeight:700, color:"var(--text)", textAlign:"right" }}>{fmt$(a.currentValue)}</td>
                    <td style={{ padding:"12px 16px", fontFamily:"Montserrat,sans-serif", fontSize:12, fontWeight:700, color: a.pnl >= 0 ? C.green : C.red, textAlign:"right" }}>{fmt$(a.pnl)}</td>
                    <td style={{ padding:"12px 16px", fontFamily:"Montserrat,sans-serif", fontSize:12, fontWeight:700, color: a.pnlPct >= 0 ? C.green : C.red, textAlign:"right" }}>{fmtPct(a.pnlPct)}</td>
                    <td style={{ padding:"12px 16px", textAlign:"right" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, justifyContent:"flex-end" }}>
                        <div style={{ width:50, height:4, background:"var(--border)", borderRadius:2, overflow:"hidden" }}>
                          <div style={{ width:`${allocation}%`, height:"100%", background:COLORS[i % COLORS.length], borderRadius:2 }}/>
                        </div>
                        <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:"var(--text3)", minWidth:32, textAlign:"right" }}>{allocation.toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      </DashCollapsible>
          ),

          ranking: (
            <DashCollapsible id="ranking" title="ASSET RANKING" onMouseDragStart={onDashMouseDragStart} isDragging={dragState?.id === "ranking"} isDragOver={dragOverId === "ranking"}>
              <RankingChart assetData={assetData} />
            </DashCollapsible>
          ),

          fear_greed: (
            <DashCollapsible id="fear_greed" title="FEAR & GREED INDEX" onMouseDragStart={onDashMouseDragStart} isDragging={dragState?.id === "fear_greed"} isDragOver={dragOverId === "fear_greed"}>
              <FearGreedChart />
            </DashCollapsible>
          ),

        };
        return dashOrder.map(id => {
          if (dashHidden[id]) return null;
          const section = allDashSections[id];
          if (!section) return null;
          return <div key={id}>{section}</div>;
        });
      })()}
    </div>
  );
}

// ── Popular Assets List ───────────────────────────────────────────────────────
const POPULAR_STOCKS = [
  // ── Mega cap tech ──
  { ticker:"AAPL",  name:"Apple Inc." },
  { ticker:"MSFT",  name:"Microsoft Corp." },
  { ticker:"NVDA",  name:"NVIDIA Corp." },
  { ticker:"GOOGL", name:"Alphabet Inc." },
  { ticker:"AMZN",  name:"Amazon.com Inc." },
  { ticker:"META",  name:"Meta Platforms" },
  { ticker:"TSLA",  name:"Tesla Inc." },
  { ticker:"AVGO",  name:"Broadcom Inc." },
  { ticker:"TSM",   name:"Taiwan Semiconductor" },
  { ticker:"ORCL",  name:"Oracle Corp." },
  { ticker:"CRM",   name:"Salesforce Inc." },
  { ticker:"ADBE",  name:"Adobe Inc." },
  { ticker:"QCOM",  name:"Qualcomm Inc." },
  { ticker:"TXN",   name:"Texas Instruments" },
  { ticker:"AMD",   name:"Advanced Micro Devices" },
  { ticker:"INTC",  name:"Intel Corp." },
  { ticker:"ARM",   name:"Arm Holdings" },
  { ticker:"SMCI",  name:"Super Micro Computer" },
  { ticker:"AMAT",  name:"Applied Materials" },
  { ticker:"MU",    name:"Micron Technology" },
  // ── Finance ──
  { ticker:"JPM",   name:"JPMorgan Chase" },
  { ticker:"BAC",   name:"Bank of America" },
  { ticker:"WFC",   name:"Wells Fargo" },
  { ticker:"GS",    name:"Goldman Sachs" },
  { ticker:"MS",    name:"Morgan Stanley" },
  { ticker:"BLK",   name:"BlackRock Inc." },
  { ticker:"V",     name:"Visa Inc." },
  { ticker:"MA",    name:"Mastercard Inc." },
  { ticker:"AXP",   name:"American Express" },
  { ticker:"PYPL",  name:"PayPal Holdings" },
  { ticker:"SQ",    name:"Block Inc." },
  { ticker:"COIN",  name:"Coinbase Global" },
  { ticker:"HOOD",  name:"Robinhood Markets" },
  { ticker:"SOFI",  name:"SoFi Technologies" },
  { ticker:"SCHW",  name:"Charles Schwab" },
  // ── Bitcoin / Crypto stocks ──
  { ticker:"MSTR",  name:"Strategy (MicroStrategy)" },
  { ticker:"MARA",  name:"MARA Holdings" },
  { ticker:"RIOT",  name:"Riot Platforms" },
  { ticker:"CLSK",  name:"CleanSpark Inc." },
  { ticker:"HUT",   name:"Hut 8 Corp." },
  { ticker:"BTBT",  name:"Bit Digital" },
  { ticker:"CIFR",  name:"Cipher Mining" },
  { ticker:"IREN",  name:"Iris Energy" },
  // ── Growth / AI ──
  { ticker:"PLTR",  name:"Palantir Technologies" },
  { ticker:"RKLB",  name:"Rocket Lab USA" },
  { ticker:"IONQ",  name:"IonQ Inc." },
  { ticker:"RIVN",  name:"Rivian Automotive" },
  { ticker:"LCID",  name:"Lucid Group" },
  { ticker:"PATH",  name:"UiPath Inc." },
  { ticker:"AI",    name:"C3.ai Inc." },
  { ticker:"SOUN",  name:"SoundHound AI" },
  { ticker:"BBAI",  name:"BigBear.ai" },
  { ticker:"ARKG",  name:"ARK Genomic Revolution ETF" },
  { ticker:"ARKK",  name:"ARK Innovation ETF" },
  { ticker:"ARKW",  name:"ARK Next Generation ETF" },
  // ── Consumer ──
  { ticker:"NFLX",  name:"Netflix Inc." },
  { ticker:"DIS",   name:"Walt Disney Co." },
  { ticker:"SPOT",  name:"Spotify Technology" },
  { ticker:"UBER",  name:"Uber Technologies" },
  { ticker:"LYFT",  name:"Lyft Inc." },
  { ticker:"ABNB",  name:"Airbnb Inc." },
  { ticker:"BKNG",  name:"Booking Holdings" },
  { ticker:"EXPE",  name:"Expedia Group" },
  { ticker:"DASH",  name:"DoorDash Inc." },
  { ticker:"SHOP",  name:"Shopify Inc." },
  { ticker:"BABA",  name:"Alibaba Group" },
  { ticker:"JD",    name:"JD.com Inc." },
  { ticker:"PDD",   name:"PDD Holdings" },
  { ticker:"SNAP",  name:"Snap Inc." },
  { ticker:"PINS",  name:"Pinterest Inc." },
  { ticker:"RBLX",  name:"Roblox Corp." },
  { ticker:"U",     name:"Unity Software" },
  // ── Healthcare / Biotech ──
  { ticker:"JNJ",   name:"Johnson & Johnson" },
  { ticker:"UNH",   name:"UnitedHealth Group" },
  { ticker:"PFE",   name:"Pfizer Inc." },
  { ticker:"MRNA",  name:"Moderna Inc." },
  { ticker:"ABBV",  name:"AbbVie Inc." },
  { ticker:"LLY",   name:"Eli Lilly" },
  { ticker:"BMY",   name:"Bristol-Myers Squibb" },
  { ticker:"AMGN",  name:"Amgen Inc." },
  { ticker:"GILD",  name:"Gilead Sciences" },
  { ticker:"ISRG",  name:"Intuitive Surgical" },
  { ticker:"DXCM",  name:"DexCom Inc." },
  { ticker:"TDOC",  name:"Teladoc Health" },
  // ── Energy ──
  { ticker:"XOM",   name:"ExxonMobil Corp." },
  { ticker:"CVX",   name:"Chevron Corp." },
  { ticker:"COP",   name:"ConocoPhillips" },
  { ticker:"NEE",   name:"NextEra Energy" },
  { ticker:"ENPH",  name:"Enphase Energy" },
  { ticker:"FSLR",  name:"First Solar Inc." },
  { ticker:"PLUG",  name:"Plug Power Inc." },
  // ── Industrial / Other ──
  { ticker:"AAON",  name:"AAON Inc." },
  { ticker:"BA",    name:"Boeing Co." },
  { ticker:"CAT",   name:"Caterpillar Inc." },
  { ticker:"DE",    name:"Deere & Company" },
  { ticker:"LMT",   name:"Lockheed Martin" },
  { ticker:"RTX",   name:"Raytheon Technologies" },
  { ticker:"WMT",   name:"Walmart Inc." },
  { ticker:"COST",  name:"Costco Wholesale" },
  { ticker:"TGT",   name:"Target Corp." },
  { ticker:"AMZN",  name:"Amazon.com Inc." },
  { ticker:"NKE",   name:"Nike Inc." },
  { ticker:"SBUX",  name:"Starbucks Corp." },
  { ticker:"MCD",   name:"McDonald's Corp." },
  // ── ETFs ──
  { ticker:"SPY",   name:"S&P 500 ETF" },
  { ticker:"QQQ",   name:"Nasdaq 100 ETF" },
  { ticker:"IWM",   name:"Russell 2000 ETF" },
  { ticker:"DIA",   name:"Dow Jones ETF" },
  { ticker:"VTI",   name:"Vanguard Total Market ETF" },
  { ticker:"VOO",   name:"Vanguard S&P 500 ETF" },
  { ticker:"GLD",   name:"Gold ETF" },
  { ticker:"SLV",   name:"Silver ETF" },
  { ticker:"TLT",   name:"Treasury Bonds ETF" },
  { ticker:"BITO",  name:"Bitcoin Strategy ETF" },
  { ticker:"IBIT",  name:"iShares Bitcoin Trust" },
  { ticker:"FBTC",  name:"Fidelity Bitcoin ETF" },
  { ticker:"GBTC",  name:"Grayscale Bitcoin Trust" },
];

const POPULAR_CRYPTO = [
  // ── Top 20 by market cap ──
  { ticker:"BTC",   name:"Bitcoin",           yahooTicker:"BTC-USD" },
  { ticker:"ETH",   name:"Ethereum",           yahooTicker:"ETH-USD" },
  { ticker:"SOL",   name:"Solana",             yahooTicker:"SOL-USD" },
  { ticker:"BNB",   name:"BNB",                yahooTicker:"BNB-USD" },
  { ticker:"XRP",   name:"XRP",                yahooTicker:"XRP-USD" },
  { ticker:"DOGE",  name:"Dogecoin",           yahooTicker:"DOGE-USD" },
  { ticker:"ADA",   name:"Cardano",            yahooTicker:"ADA-USD" },
  { ticker:"TRX",   name:"TRON",               yahooTicker:"TRX-USD" },
  { ticker:"AVAX",  name:"Avalanche",          yahooTicker:"AVAX-USD" },
  { ticker:"SHIB",  name:"Shiba Inu",          yahooTicker:"SHIB-USD" },
  { ticker:"TON",   name:"Toncoin",            yahooTicker:"TON-USD" },
  { ticker:"LINK",  name:"Chainlink",          yahooTicker:"LINK-USD" },
  { ticker:"DOT",   name:"Polkadot",           yahooTicker:"DOT-USD" },
  { ticker:"MATIC", name:"Polygon",            yahooTicker:"MATIC-USD" },
  { ticker:"LTC",   name:"Litecoin",           yahooTicker:"LTC-USD" },
  { ticker:"BCH",   name:"Bitcoin Cash",       yahooTicker:"BCH-USD" },
  { ticker:"UNI",   name:"Uniswap",            yahooTicker:"UNI-USD" },
  { ticker:"NEAR",  name:"NEAR Protocol",      yahooTicker:"NEAR-USD" },
  { ticker:"ICP",   name:"Internet Computer",  yahooTicker:"ICP-USD" },
  { ticker:"ATOM",  name:"Cosmos",             yahooTicker:"ATOM-USD" },
  // ── DeFi ──
  { ticker:"AAVE",  name:"Aave",               yahooTicker:"AAVE-USD" },
  { ticker:"MKR",   name:"Maker",              yahooTicker:"MKR-USD" },
  { ticker:"CRV",   name:"Curve DAO",          yahooTicker:"CRV-USD" },
  { ticker:"COMP",  name:"Compound",           yahooTicker:"COMP-USD" },
  { ticker:"SNX",   name:"Synthetix",          yahooTicker:"SNX-USD" },
  { ticker:"LDO",   name:"Lido DAO",           yahooTicker:"LDO-USD" },
  { ticker:"1INCH", name:"1inch Network",      yahooTicker:"1INCH-USD" },
  { ticker:"CAKE",  name:"PancakeSwap",        yahooTicker:"CAKE-USD" },
  { ticker:"GMX",   name:"GMX",                yahooTicker:"GMX-USD" },
  { ticker:"DYDX",  name:"dYdX",               yahooTicker:"DYDX-USD" },
  // ── Layer 2 / Scaling ──
  { ticker:"ARB",   name:"Arbitrum",           yahooTicker:"ARB-USD" },
  { ticker:"OP",    name:"Optimism",           yahooTicker:"OP-USD" },
  { ticker:"IMX",   name:"Immutable X",        yahooTicker:"IMX-USD" },
  { ticker:"STRK",  name:"Starknet",           yahooTicker:"STRK-USD" },
  { ticker:"MANTA", name:"Manta Network",      yahooTicker:"MANTA-USD" },
  // ── Layer 1 alternatives ──
  { ticker:"SUI",   name:"Sui",                yahooTicker:"SUI-USD" },
  { ticker:"APT",   name:"Aptos",              yahooTicker:"APT-USD" },
  { ticker:"SEI",   name:"Sei Network",        yahooTicker:"SEI-USD" },
  { ticker:"INJ",   name:"Injective",          yahooTicker:"INJ-USD" },
  { ticker:"ALGO",  name:"Algorand",           yahooTicker:"ALGO-USD" },
  { ticker:"EGLD",  name:"MultiversX",         yahooTicker:"EGLD-USD" },
  { ticker:"FTM",   name:"Fantom",             yahooTicker:"FTM-USD" },
  { ticker:"HBAR",  name:"Hedera",             yahooTicker:"HBAR-USD" },
  { ticker:"VET",   name:"VeChain",            yahooTicker:"VET-USD" },
  { ticker:"EOS",   name:"EOS",                yahooTicker:"EOS-USD" },
  { ticker:"XLM",   name:"Stellar",            yahooTicker:"XLM-USD" },
  { ticker:"XTZ",   name:"Tezos",              yahooTicker:"XTZ-USD" },
  { ticker:"FLOW",  name:"Flow",               yahooTicker:"FLOW-USD" },
  { ticker:"ZIL",   name:"Zilliqa",            yahooTicker:"ZIL-USD" },
  { ticker:"ONE",   name:"Harmony",            yahooTicker:"ONE-USD" },
  // ── Exchange tokens ──
  { ticker:"OKB",   name:"OKB",                yahooTicker:"OKB-USD" },
  { ticker:"KCS",   name:"KuCoin Token",       yahooTicker:"KCS-USD" },
  { ticker:"HT",    name:"Huobi Token",        yahooTicker:"HT-USD" },
  { ticker:"GT",    name:"Gate Token",         yahooTicker:"GT-USD" },
  // ── Meme coins ──
  { ticker:"PEPE",  name:"Pepe",               yahooTicker:"PEPE-USD" },
  { ticker:"FLOKI", name:"FLOKI",              yahooTicker:"FLOKI-USD" },
  { ticker:"BONK",  name:"Bonk",               yahooTicker:"BONK-USD" },
  { ticker:"WIF",   name:"dogwifhat",          yahooTicker:"WIF-USD" },
  { ticker:"POPCAT",name:"Popcat",             yahooTicker:"POPCAT-USD" },
  // ── AI / Data ──
  { ticker:"FET",   name:"Fetch.ai",           yahooTicker:"FET-USD" },
  { ticker:"AGIX",  name:"SingularityNET",     yahooTicker:"AGIX-USD" },
  { ticker:"OCEAN", name:"Ocean Protocol",     yahooTicker:"OCEAN-USD" },
  { ticker:"RNDR",  name:"Render Network",     yahooTicker:"RNDR-USD" },
  { ticker:"TAO",   name:"Bittensor",          yahooTicker:"TAO-USD" },
  { ticker:"WLD",   name:"Worldcoin",          yahooTicker:"WLD-USD" },
  // ── Gaming / Metaverse ──
  { ticker:"AXS",   name:"Axie Infinity",      yahooTicker:"AXS-USD" },
  { ticker:"SAND",  name:"The Sandbox",        yahooTicker:"SAND-USD" },
  { ticker:"MANA",  name:"Decentraland",       yahooTicker:"MANA-USD" },
  { ticker:"ENJ",   name:"Enjin Coin",         yahooTicker:"ENJ-USD" },
  { ticker:"GALA",  name:"Gala Games",         yahooTicker:"GALA-USD" },
  { ticker:"ILV",   name:"Illuvium",           yahooTicker:"ILV-USD" },
  // ── Privacy ──
  { ticker:"XMR",   name:"Monero",             yahooTicker:"XMR-USD" },
  { ticker:"ZEC",   name:"Zcash",              yahooTicker:"ZEC-USD" },
  { ticker:"DASH",  name:"Dash",               yahooTicker:"DASH-USD" },
  // ── Infrastructure ──
  { ticker:"FIL",   name:"Filecoin",           yahooTicker:"FIL-USD" },
  { ticker:"AR",    name:"Arweave",            yahooTicker:"AR-USD" },
  { ticker:"GRT",   name:"The Graph",          yahooTicker:"GRT-USD" },
  { ticker:"BAT",   name:"Basic Attention",    yahooTicker:"BAT-USD" },
  { ticker:"THETA", name:"Theta Network",      yahooTicker:"THETA-USD" },
  { ticker:"CHZ",   name:"Chiliz",             yahooTicker:"CHZ-USD" },
  // ── THORChain ecosystem ──
  { ticker:"RUNE",  name:"THORChain",          yahooTicker:"RUNE-USD" },
  { ticker:"CACAO", name:"Maya Protocol",      yahooTicker:"CACAO-USD" },
  // ── Stablecoins (for reference) ──
  { ticker:"USDT",  name:"Tether",             yahooTicker:"USDT-USD" },
  { ticker:"USDC",  name:"USD Coin",           yahooTicker:"USDC-USD" },
  { ticker:"DAI",   name:"Dai",                yahooTicker:"DAI-USD" },
];

const POPULAR_ASSETS = [...POPULAR_STOCKS, ...POPULAR_CRYPTO.map(c => ({ ticker: c.yahooTicker, name: c.name }))];

// ── Custom Date Picker ────────────────────────────────────────────────────────
function CustomDatePicker({ value, onChange, style = {} }) {
  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}`;

  // Use today as default if no value
  const effective = value || todayStr;
  const [day,   setDay]   = useState(() => { const p = effective.split("-"); return p[2] ? parseInt(p[2]) : now.getDate(); });
  const [month, setMonth] = useState(() => { const p = effective.split("-"); return p[1] ? parseInt(p[1]) : now.getMonth()+1; });
  const [year,  setYear]  = useState(() => { const p = effective.split("-"); return p[0] ? parseInt(p[0]) : now.getFullYear(); });

  // Sync when value changes externally
  useEffect(() => {
    const src = value || todayStr;
    const p = src.split("-");
    if (p[0]) setYear(parseInt(p[0]));
    if (p[1]) setMonth(parseInt(p[1]));
    if (p[2]) setDay(parseInt(p[2]));
  }, [value]);

  // Emit when any part changes
  const emit = (d, m, y) => {
    const dStr = String(d).padStart(2,"0");
    const mStr = String(m).padStart(2,"0");
    onChange(`${y}-${mStr}-${dStr}`);
  };

  // Initialize with today on mount if no value
  useEffect(() => {
    if (!value) emit(now.getDate(), now.getMonth()+1, now.getFullYear());
  }, []);

  const years = [];
  for (let y = now.getFullYear(); y >= 2010; y--) years.push(y);

  const daysInMonth = new Date(year, month, 0).getDate();
  const days = Array.from({length: daysInMonth}, (_, i) => i + 1);

  const sel = {
    background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:8,
    color:"var(--text)", fontFamily:"Montserrat,sans-serif", fontSize:12, fontWeight:700,
    padding:"10px 10px", cursor:"pointer", outline:"none", appearance:"none",
    WebkitAppearance:"none", width:"100%", ...style,
  };

  return (
    <div style={{ display:"flex", gap:8 }}>
      <select value={day} onChange={e => { const v = parseInt(e.target.value); setDay(v); emit(v, month, year); }}
        style={{ ...sel, flex:1 }}
        onFocus={e => e.target.style.borderColor = "#F28705"}
        onBlur={e => e.target.style.borderColor = "var(--border)"}
      >
        {days.map(d => <option key={d} value={d}>{String(d).padStart(2,"0")}</option>)}
      </select>

      <select value={month} onChange={e => { const v = parseInt(e.target.value); setMonth(v); emit(day, v, year); }}
        style={{ ...sel, flex:2 }}
        onFocus={e => e.target.style.borderColor = "#F28705"}
        onBlur={e => e.target.style.borderColor = "var(--border)"}
      >
        {MONTHS.map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
      </select>

      <select value={year} onChange={e => { const v = parseInt(e.target.value); setYear(v); emit(day, month, v); }}
        style={{ ...sel, flex:2 }}
        onFocus={e => e.target.style.borderColor = "#F28705"}
        onBlur={e => e.target.style.borderColor = "var(--border)"}
      >
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
}


// ── Collapsible Draggable Wrapper ─────────────────────────────────────────────
function CollapsibleDraggable({ id, section, onMouseDragStart }) {
  const [showHandle, setShowHandle] = useState(false);

  return (
    <div
      data-section-id={id}
      style={{ position:"relative" }}
      onMouseEnter={() => setShowHandle(true)}
      onMouseLeave={() => setShowHandle(false)}
    >
      {/* Drag handle — appears on hover, left side of header */}
      <div
        onMouseDown={e => { e.preventDefault(); e.stopPropagation(); if (onMouseDragStart) onMouseDragStart(e, id); }}
        onClick={e => e.stopPropagation()}
        style={{
          position:"absolute", top:0, left:0,
          width:36, height:46, zIndex:10,
          cursor: showHandle ? "grab" : "default",
          borderRadius:"10px 0 0 0",
          display:"flex", alignItems:"center", justifyContent:"center",
          opacity: showHandle ? 1 : 0,
          transition:"opacity 0.2s ease",
        }}
      >
        <div style={{ display:"flex", flexDirection:"column", gap:3, pointerEvents:"none" }}>
          {[0,1,2].map(i => (
            <div key={i} style={{ width:14, height:1.5, borderRadius:1, background:"var(--text4)", opacity:0.6 }}/>
          ))}
        </div>
      </div>
      {section}
    </div>
  );
}

// ── Collapsible Section ───────────────────────────────────────────────────────
function Collapsible({ id, title, children, defaultOpen = true, noOverflow = false }) {
  const storageKey = `collapse_${id}`;
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved !== null ? saved === "true" : defaultOpen;
    } catch { return defaultOpen; }
  });

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(storageKey, String(next)); } catch {}
  };

  return (
    <div style={{ marginBottom:24 }}>
      {/* Header */}
      <div
        onClick={toggle}
        style={{
          display:"flex", justifyContent:"space-between", alignItems:"center",
          cursor:"pointer", userSelect:"none",
          padding:"12px 20px",
          background:"var(--bg2)",
          border:"1px solid var(--border)",
          borderRadius: open ? "10px 10px 0 0" : 10,
          borderBottom: open ? "none" : "1px solid var(--border)",
          transition:"border-radius 0.3s ease, border-bottom 0.3s ease",
        }}
      >
        <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, letterSpacing:"0.3em", color:"#888", paddingLeft:28 }}>
          {title}
        </div>
        <div style={{
          width:20, height:20, borderRadius:"50%",
          background: open ? "rgba(242,135,5,0.12)" : "var(--bg3)",
          border:`1px solid ${open ? C.orange : "var(--border)"}`,
          display:"flex", alignItems:"center", justifyContent:"center",
          transition:"all 0.3s ease", flexShrink:0,
        }}>
          <span style={{
            fontSize:9, color: open ? C.orange : "var(--text4)",
            transition:"transform 0.3s ease",
            display:"block",
            transform: open ? "rotate(0deg)" : "rotate(-90deg)",
          }}>▼</span>
        </div>
      </div>

      {/* Content — grid animation trick for smooth height */}
      <div style={{
        display:"grid",
        gridTemplateRows: open ? "1fr" : "0fr",
        transition:"grid-template-rows 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
        background:"var(--bg2)",
        border: open ? "1px solid var(--border)" : "none",
        borderTop:"none",
        borderRadius: open ? "0 0 10px 10px" : 0,
        transition: open
          ? "grid-template-rows 0.35s cubic-bezier(0.4,0,0.2,1), border-color 0.35s ease"
          : "grid-template-rows 0.3s cubic-bezier(0.4,0,0.2,1), border-color 0.3s ease",
      }}>
        <div style={{ overflow: "hidden" }}>
          <div style={{
            padding:"16px 20px 20px",
            opacity: open ? 1 : 0,
            transform: open ? "translateY(0)" : "translateY(-8px)",
            transition: open
              ? "opacity 0.3s ease 0.05s, transform 0.3s ease 0.05s"
              : "opacity 0.2s ease, transform 0.2s ease",
          }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

function AnimatedNumber({ value, format, duration = 800 }) {
  const [display, setDisplay] = useState(0);
  const fromRef = useRef(0);
  const rafRef  = useRef(null);

  useEffect(() => {
    const from = fromRef.current;
    const to   = value;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    let start = null;

    const step = (ts) => {
      if (!start) start = ts;
      const p    = Math.min((ts - start) / duration, 1);
      const ease = p < 0.5 ? 2*p*p : -1+(4-2*p)*p;
      setDisplay(from + (to - from) * ease);
      if (p < 1) rafRef.current = requestAnimationFrame(step);
      else { setDisplay(to); fromRef.current = to; }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [value]);

  return <span>{format(display)}</span>;
}

// ── Ticker Bar ────────────────────────────────────────────────────────────────
function TickerBar({ entries, currentPrice, totalShares, totalDollars, portfolioValue, portfolioPnl, portfolioPct }) {
  const hasCp = currentPrice > 0;
  const avgPrice = totalShares > 0 ? totalDollars / totalShares : 0;
  const distBE = hasCp && avgPrice > 0 ? ((currentPrice - avgPrice) / avgPrice) * 100 : null;

  const items = [
    { label:"PRICE",      value: hasCp ? `$${currentPrice.toLocaleString("en-US", {minimumFractionDigits:2, maximumFractionDigits:2})}` : "—", color: C.orange },
    { label:"SHARES",     value: totalShares.toFixed(4),                                                                                       color: "var(--text)" },
    { label:"INVESTED",   value: `$${totalDollars.toLocaleString("en-US", {minimumFractionDigits:2, maximumFractionDigits:2})}`,               color: "var(--text)" },
    { label:"VALUE",      value: hasCp ? `$${portfolioValue.toLocaleString("en-US", {minimumFractionDigits:2, maximumFractionDigits:2})}` : "—", color: hasCp ? (portfolioPnl >= 0 ? C.green : C.red) : "var(--text)" },
    { label:"P&L $",      value: hasCp ? `${portfolioPnl >= 0 ? "+" : ""}$${Math.abs(portfolioPnl).toLocaleString("en-US", {minimumFractionDigits:2, maximumFractionDigits:2})}` : "—", color: hasCp ? (portfolioPnl >= 0 ? C.green : C.red) : "var(--text)" },
    { label:"P&L %",      value: hasCp ? `${portfolioPct >= 0 ? "+" : ""}${portfolioPct.toFixed(2)}%` : "—", color: hasCp ? (portfolioPnl >= 0 ? C.green : C.red) : "var(--text)" },
    { label:"AVG PRICE",  value: avgPrice > 0 ? `$${avgPrice.toFixed(2)}` : "—",                                                              color: C.orange },
    { label:"VS B/E",     value: distBE !== null ? `${distBE >= 0 ? "+" : ""}${distBE.toFixed(2)}%` : "—",                                    color: distBE !== null ? (distBE >= 0 ? C.green : C.red) : "var(--text)" },
  ];

  // Duplicate items for seamless loop
  const all = [...items, ...items];

  return (
    <div style={{ borderBottom:"1px solid var(--border)", background:"var(--bg2)",
      overflow:"hidden", height:34, display:"flex", alignItems:"center", flexShrink:0 }}>
      <div style={{ display:"flex", animation:"tickerScroll 24s linear infinite", whiteSpace:"nowrap" }}>
        {all.map((item, i) => (
          <div key={i} style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"0 28px" }}>
            <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700,
              letterSpacing:"0.2em", color:"var(--text4)" }}>{item.label}</span>
            <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700,
              color:item.color }}>{item.value}</span>
            <span style={{ color:"var(--border2)", fontSize:10, paddingLeft:20 }}>·</span>
          </div>
        ))}
      </div>
    </div>
  );
}
// ── Goals Page ────────────────────────────────────────────────────────────────
function GoalsPage({ totalShares, totalDollars, currentPrice, portfolioValue, portfolioPnl }) {
  const STORAGE_KEY = "mstr_goals_v1";
  const [goals, setGoals]       = useState([]);
  const [loaded, setLoaded]     = useState(false);
  const [newName, setNewName]   = useState("");
  const [newTarget, setNewTarget] = useState("");
  const [newType, setNewType]   = useState("value"); // "value" | "pnl" | "price"

  useEffect(() => {
    window.storage.get(STORAGE_KEY).then(res => {
      if (res?.value) setGoals(JSON.parse(res.value));
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const persist = async (g) => {
    await window.storage.set(STORAGE_KEY, JSON.stringify(g));
  };

  const addGoal = async () => {
    const t = parseFloat(newTarget);
    if (!newName.trim() || !t || t <= 0) return;
    const g = [...goals, { id: Date.now(), name: newName.trim(), target: t, type: newType, createdAt: new Date().toISOString().split("T")[0] }];
    setGoals(g); await persist(g);
    setNewName(""); setNewTarget("");
  };

  const removeGoal = async (id) => {
    const g = goals.filter(g => g.id !== id);
    setGoals(g); await persist(g);
  };

  const getCurrentValue = (type) => {
    if (type === "value") return portfolioValue;
    if (type === "pnl")   return portfolioPnl;
    if (type === "price") return currentPrice;
    return 0;
  };

  const getLabel = (type) => {
    if (type === "value") return "Portfolio Value";
    if (type === "pnl")   return "Total P&L";
    if (type === "price") return "Price";
    return "";
  };

  // Price needed for portfolio value goal
  const priceNeeded = (goal) => {
    if (goal.type === "value"  && totalShares > 0) return goal.target / totalShares;
    if (goal.type === "pnl"    && totalShares > 0) return (goal.target + totalDollars) / totalShares;
    if (goal.type === "price") return goal.target;
    return null;
  };

  if (!loaded) return null;

  const hasCp = currentPrice > 0;

  return (
    <div>
      {/* Add goal form */}
      <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:10, padding:"22px 24px", marginBottom:24 }}>
        <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, letterSpacing:"0.3em", color:"#888", marginBottom:18 }}>ADD GOAL</div>
        <div style={{ display:"flex", gap:12, flexWrap:"wrap", alignItems:"flex-end" }}>
          {/* Name */}
          <div style={{ display:"flex", flexDirection:"column", gap:6, flex:2, minWidth:160 }}>
            <label style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, letterSpacing:"0.2em", color:"var(--text3)" }}>NAME</label>
            <input value={newName} onChange={e => setNewName(e.target.value)}
              placeholder="e.g. First 100k"
              style={{ background:"var(--bg4)", border:"1px solid var(--border)", borderRadius:7, padding:"10px 14px",
                color:"var(--text)", fontFamily:"Montserrat,sans-serif", fontSize:13, outline:"none", colorScheme:"dark" }}/>
          </div>
          {/* Type */}
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            <label style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, letterSpacing:"0.2em", color:"var(--text3)" }}>TYPE</label>
            <div style={{ display:"flex", gap:6 }}>
              {[{v:"value",l:"Portfolio $"},{v:"pnl",l:"P&L $"},{v:"price",l:"Price"}].map(({v,l}) => (
                <button key={v} onClick={() => setNewType(v)}
                  style={{ padding:"10px 14px", borderRadius:7, border:`1px solid ${newType===v ? C.orange : "#1c1c1c"}`,
                    background: newType===v ? "rgba(242,135,5,0.1)" : "transparent",
                    color: newType===v ? C.orange : "#555",
                    fontFamily:"Montserrat,sans-serif", fontSize:10, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          {/* Target */}
          <div style={{ display:"flex", flexDirection:"column", gap:6, flex:1, minWidth:120 }}>
            <label style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, letterSpacing:"0.2em", color:"var(--text3)" }}>TARGET ($)</label>
            <input type="number" value={newTarget} onChange={e => setNewTarget(e.target.value)}
              placeholder="100000"
              style={{ background:"var(--bg4)", border:"1px solid var(--border)", borderRadius:7, padding:"10px 14px",
                color:"var(--text)", fontFamily:"Montserrat,sans-serif", fontSize:13, outline:"none", colorScheme:"dark" }}/>
          </div>
          <button onClick={addGoal} disabled={!newName.trim() || !newTarget}
            style={{ padding:"10px 24px", background: (!newName.trim()||!newTarget) ? "#111" : C.orange,
              color: (!newName.trim()||!newTarget) ? "#333" : "#000",
              border:"none", borderRadius:7, fontFamily:"Montserrat,sans-serif",
              fontSize:11, fontWeight:700, letterSpacing:"0.15em", cursor: (!newName.trim()||!newTarget) ? "not-allowed" : "pointer",
              whiteSpace:"nowrap", alignSelf:"flex-end", height:42 }}>
            + ADD GOAL
          </button>
        </div>
      </div>

      {/* Goals list */}
      {goals.length === 0 ? (
        <div style={{ textAlign:"center", padding:"60px 0", fontFamily:"Montserrat,sans-serif", fontSize:11, color:"var(--text5)", letterSpacing:"0.2em" }}>
          NO GOALS YET · ADD ONE ABOVE
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          {goals.map(goal => {
            const current = getCurrentValue(goal.type);
            const pct     = current > 0 && goal.target > 0 ? Math.min((current / goal.target) * 100, 100) : 0;
            const reached = current >= goal.target;
            const needed  = priceNeeded(goal);
            const distPct = needed && currentPrice > 0 ? ((needed - currentPrice) / currentPrice) * 100 : null;

            return (
              <div key={goal.id} style={{ background:"var(--bg2)", border:`1px solid ${reached ? C.green : "#1c1c1c"}`,
                borderRadius:10, padding:"22px 24px", position:"relative" }}>

                {/* Delete */}
                <button onClick={() => removeGoal(goal.id)}
                  style={{ position:"absolute", top:14, right:14, background:"transparent", border:"none",
                    color:"var(--text5)", cursor:"pointer", fontSize:14, fontFamily:"Montserrat,sans-serif",
                    lineHeight:1, padding:4 }}
                  onMouseEnter={e => e.target.style.color=C.red}
                  onMouseLeave={e => e.target.style.color="#2a2a2a"}></button>

                {/* Header */}
                <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16 }}>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:16, fontWeight:900, color: reached ? C.green : "#F2F0E4" }}>
                    {reached ? " " : ""}{goal.name}
                  </div>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, letterSpacing:"0.2em",
                    color:"var(--text4)", padding:"3px 8px", border:"1px solid var(--border)", borderRadius:4 }}>
                    {getLabel(goal.type)}
                  </div>
                </div>

                {/* Progress bar */}
                <div style={{ marginBottom:14 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                    <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, color:"var(--text2)" }}>
                      {hasCp ? fmt$(current) : "—"} <span style={{ color:"var(--text5)" }}>/ {fmt$(goal.target)}</span>
                    </span>
                    <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:13, fontWeight:700, color: reached ? C.green : C.orange }}>
                      {hasCp ? pct.toFixed(1) + "%" : "—"}
                    </span>
                  </div>
                  <div style={{ height:6, background:"var(--bg3)", borderRadius:3, overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${pct}%`, background: reached ? C.green : C.orange,
                      borderRadius:3, transition:"width 0.8s cubic-bezier(0.4,0,0.2,1)" }}/>
                  </div>
                </div>

                {/* Details row */}
                <div style={{ display:"flex", gap:28, flexWrap:"wrap" }}>
                  <div>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, letterSpacing:"0.2em", color:"var(--text4)", marginBottom:4 }}>REMAINING</div>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:14, fontWeight:700,
                      color: reached ? C.green : "#F2F0E4" }}>
                      {hasCp ? (reached ? "REACHED " : fmt$(goal.target - current)) : "—"}
                    </div>
                  </div>
                  {needed !== null && goal.type !== "price" && (
                    <div>
                      <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, letterSpacing:"0.2em", color:"var(--text4)", marginBottom:4 }}>PRICE NEEDED</div>
                      <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:14, fontWeight:700, color: C.orange }}>
                        {fmt$(needed)}
                      </div>
                    </div>
                  )}
                  {distPct !== null && !reached && goal.type !== "price" && (
                    <div>
                      <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, letterSpacing:"0.2em", color:"var(--text4)", marginBottom:4 }}>FROM CURRENT PRICE</div>
                      <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:14, fontWeight:700,
                        color: distPct <= 0 ? C.green : "#888" }}>
                        {distPct >= 0 ? "+" : ""}{distPct.toFixed(1)}%
                      </div>
                    </div>
                  )}
                  <div>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, letterSpacing:"0.2em", color:"var(--text4)", marginBottom:4 }}>CREATED</div>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:13, color:"var(--text3)" }}>
                      {formatDate(goal.createdAt)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── DCA Slider Field ──────────────────────────────────────────────────────────
function DCASliderField({ label, value, onChange, min, max, step, suffix }) {
  const pct = ((parseFloat(value)||0) - min) / (max - min) * 100;
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
      <div style={{ display:"flex", justifyContent:"space-between" }}>
        <label style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, letterSpacing:"0.25em", color:"var(--text4)" }}>{label}</label>
        <div style={{ display:"flex", alignItems:"center", gap:4, background:"var(--bg4)", border:`1px solid ${C.orange}`, borderRadius:6, padding:"2px 8px" }}>
          <input
            type="number" min={min} max={max} step={step}
            value={value}
            onChange={e => onChange(e.target.value)}
            style={{ background:"transparent", border:"none", outline:"none", color:C.orange, fontFamily:"Montserrat,sans-serif", fontSize:13, fontWeight:700, width:70, textAlign:"right", colorScheme:"dark" }}
          />
          <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:C.orange }}>{suffix}</span>
        </div>
      </div>
      <div style={{ position:"relative", height:6, background:"var(--border)", borderRadius:3 }}>
        <div style={{ position:"absolute", left:0, width:`${Math.min(100,Math.max(0,pct))}%`, height:"100%", background:C.orange, borderRadius:3 }}/>
        {/* Fake thumb dot */}
        <div style={{
          position:"absolute",
          left:`${Math.min(100,Math.max(0,pct))}%`,
          top:"50%",
          transform:"translate(-50%, -50%)",
          width:14, height:14,
          borderRadius:"50%",
          background:C.orange,
          border:"2px solid var(--bg2)",
          boxShadow:`0 0 0 2px ${C.orange}55`,
          pointerEvents:"none",
          zIndex:1,
        }}/>
        <input type="range" min={min} max={max} step={step} value={parseFloat(value)||min}
          onChange={e => onChange(e.target.value)}
          style={{ position:"absolute", top:"50%", transform:"translateY(-50%)", left:0, width:"100%", cursor:"pointer", background:"transparent", zIndex:2 }}/>
      </div>
    </div>
  );
}

// ── ADMIN PAGE ─────────────────────────────────────────────────────────────
function AdminPage({ supabase, onBannerChange }) {
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [stats, setStats]         = useState({ total: 0, withAssets: 0, totalAssets: 0 });
  const [isFree, setIsFree]       = useState(false);
  const [toggling, setToggling]   = useState(false);
  const [toggleErr, setToggleErr] = useState(null);
  const [banner, setBanner]       = useState(null);
  const [bannerSaving, setBannerSaving] = useState(false);
  const [bannerErr, setBannerErr] = useState(null);
  const [bannerDrag, setBannerDrag] = useState(false);
  const bannerRef = useRef(null);
  const [pendingBanner, setPendingBanner] = useState(null);
  const [assetActivity, setAssetActivity] = useState([]);
  const [activitySort, setActivitySort]   = useState({ key:"last_trade", dir:"desc" });
  const [adminTab, setAdminTab]   = useState("dashboard"); // "dashboard" | "animations"

  useEffect(() => {
    (async () => {
      try {
        const [rpcRes, settingRes, bannerRes, activityRes] = await Promise.all([
          supabase.rpc("get_admin_stats"),
          supabase.from("app_settings").select("value").eq("key", "is_free").maybeSingle(),
          supabase.from("app_settings").select("value").eq("key", "dashboard_banner").maybeSingle(),
          supabase.rpc("get_asset_activity"),
        ]);
        if (!rpcRes.error) {
          const userList = (rpcRes.data || []).map(u => ({ ...u, asset_count: Number(u.asset_count), trade_count: Number(u.trade_count) }));
          setUsers(userList);
          setStats({ total: userList.length, withAssets: userList.filter(u => u.trade_count > 0).length, totalAssets: userList.reduce((s, u) => s + u.asset_count, 0) });
        }
        setIsFree(settingRes?.data?.value === "true");
        if (bannerRes?.data?.value) setBanner(bannerRes.data.value);
        if (!activityRes.error) setAssetActivity(activityRes.data || []);
      } catch(e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  const toggleFree = async () => {
    if (toggling) return;
    setToggling(true);
    setToggleErr(null);
    const newVal = !isFree;
    const { error: updErr } = await supabase.from("app_settings").update({ value: String(newVal) }).eq("key", "is_free");
    if (updErr) {
      const { error: insErr } = await supabase.from("app_settings").insert({ key: "is_free", value: String(newVal) });
      if (insErr) { setToggleErr(insErr.message); setToggling(false); return; }
    }
    setIsFree(newVal);
    setToggling(false);
  };

  const saveBanner = async (base64) => {
    setBannerSaving(true); setBannerErr(null);
    try {
      console.log("[Banner] Starting save, base64 length:", base64.length);

      // Step 1: Check if row exists
      const { data: existing, error: selectErr } = await supabase
        .from("app_settings").select("key").eq("key", "dashboard_banner").maybeSingle();

      if (selectErr) {
        console.error("[Banner] Select error:", selectErr);
        setBannerErr("❌ Read error: " + selectErr.message + " | code: " + selectErr.code);
        setBannerSaving(false); return;
      }

      console.log("[Banner] Existing row:", existing);

      let saveError;
      if (existing) {
        console.log("[Banner] Updating existing row...");
        const { error } = await supabase.from("app_settings")
          .update({ value: base64 }).eq("key", "dashboard_banner");
        saveError = error;
      } else {
        console.log("[Banner] Inserting new row...");
        const { error } = await supabase.from("app_settings")
          .insert({ key: "dashboard_banner", value: base64 });
        saveError = error;
      }

      if (saveError) {
        console.error("[Banner] Save error:", saveError);
        setBannerErr("❌ Save failed: " + saveError.message + " | code: " + saveError.code + (saveError.hint ? " | hint: " + saveError.hint : ""));
        setBannerSaving(false); return;
      }

      // Step 3: Verify it was actually saved
      const { data: verify, error: verifyErr } = await supabase
        .from("app_settings").select("value").eq("key", "dashboard_banner").maybeSingle();

      if (verifyErr || !verify?.value) {
        setBannerErr("⚠️ Saved but verify failed — value not found in DB. " + (verifyErr?.message || ""));
        setBannerSaving(false); return;
      }

      console.log("[Banner] ✅ Saved and verified! length:", verify.value.length);
      setBanner(base64);
      setPendingBanner(null);
      setBannerSaving(false);
      if (onBannerChange) onBannerChange(base64);
      setBannerErr("✅ Banner saved successfully!");
    } catch (e) {
      console.error("[Banner] Unexpected error:", e);
      setBannerErr("❌ Unexpected error: " + e.message);
      setBannerSaving(false);
    }
  };

  const removeBanner = async () => {
    setBannerSaving(true); setBannerErr(null);
    const { error } = await supabase.from("app_settings").delete().eq("key", "dashboard_banner");
    if (error) { setBannerErr(error.message); setBannerSaving(false); return; }
    setBanner(null);
    setPendingBanner(null);
    setBannerSaving(false);
    if (onBannerChange) onBannerChange(null);
  };

  const handleBannerFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { setBannerErr("Only image files are supported."); return; }
    if (file.size > 5 * 1024 * 1024) { setBannerErr("File is too large. Max 5MB."); return; }
    setBannerErr(null);
    console.log("[Banner] Reading file:", file.name, file.size, file.type);
    const reader = new FileReader();
    reader.onload = (e) => {
      console.log("[Banner] File read complete, length:", e.target.result?.length);
      setPendingBanner(e.target.result);
    };
    reader.onerror = (e) => {
      console.error("[Banner] FileReader error:", e);
      setBannerErr("Could not read file.");
    };
    reader.readAsDataURL(file);
  };

  const C2 = { orange:"#F28705", green:"#22c55e", red:"#E03E1A", text:"var(--text)", text2:"var(--text2)", text3:"var(--text3)", border:"var(--border)", bg2:"var(--bg2)", bg3:"var(--bg3)" };

  if (loading) return <div style={{ padding:40, textAlign:"center", color:C2.text3, fontFamily:"Montserrat,sans-serif", fontSize:12 }}>Loading admin data...</div>;

  return (
    <div style={{ padding:"24px 0", fontFamily:"Montserrat,sans-serif" }}>
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.3em", color:C2.text3, marginBottom:8 }}>ADMIN</div>
        <div style={{ fontSize:22, fontWeight:900, color:C2.text }}>Dashboard</div>
      </div>

      {/* Tab navigation */}
      <div style={{ display:"flex", gap:6, marginBottom:24, borderBottom:"1px solid var(--border)", paddingBottom:0 }}>
        {[
          { id:"dashboard",  label:"Dashboard",         icon:"ph-gauge" },
          { id:"animations", label:"Share Animations",  icon:"ph-film-strip" },
        ].map(tab => (
          <button key={tab.id} onClick={() => setAdminTab(tab.id)} style={{
            background:"transparent", border:"none",
            borderBottom:`2px solid ${adminTab === tab.id ? C2.orange : "transparent"}`,
            padding:"8px 16px 10px", cursor:"pointer", transition:"all 0.15s",
            display:"flex", alignItems:"center", gap:7, marginBottom:-1,
          }}>
            <i className={`ph ${tab.icon}`} style={{ fontSize:15, color: adminTab === tab.id ? C2.orange : C2.text3 }}/>
            <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:12, fontWeight:700,
              color: adminTab === tab.id ? C2.orange : C2.text3, letterSpacing:"0.04em" }}>
              {tab.label}
            </span>
          </button>
        ))}
      </div>

      {/* Animations tab */}
      {adminTab === "animations" && <PriceAnimationStudio />}

      {/* Dashboard tab */}
      {adminTab === "dashboard" && <>

      <div style={{ background:C2.bg2, border:"1px solid " + (isFree ? C2.green : C2.border), borderRadius:10, padding:"20px 24px", marginBottom:24, display:"flex", alignItems:"center", justifyContent:"space-between", gap:24, transition:"border-color 0.25s" }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:9, fontWeight:800, letterSpacing:"0.25em", color:C2.text3, marginBottom:6 }}>ACCESS MODE</div>
          <div style={{ fontSize:15, fontWeight:900, color: isFree ? C2.green : C2.text, transition:"color 0.25s" }}>{isFree ? "FREE — всички имат достъп" : "PAID — изисква се абонамент"}</div>
          <div style={{ fontSize:10, color:C2.text3, marginTop:4 }}>{isFree ? "Stripe проверката е изключена. Всеки може да използва app-а." : "Stripe проверката е активна. Само платили потребители имат достъп."}</div>
          {toggleErr && <div style={{ marginTop:6, fontSize:10, color:C2.red }}>Грешка: {toggleErr}</div>}
        </div>
        <div onClick={toggleFree} style={{ width:56, height:28, borderRadius:14, flexShrink:0, background: isFree ? C2.green : "var(--bg3)", border:"1px solid " + (isFree ? C2.green : C2.border), position:"relative", cursor: toggling ? "wait" : "pointer", transition:"background 0.25s, border-color 0.25s", opacity: toggling ? 0.6 : 1 }}>
          <div style={{ position:"absolute", top:3, left: isFree ? 31 : 3, width:20, height:20, borderRadius:"50%", background: isFree ? "#fff" : "var(--text4)", transition:"left 0.25s, background 0.25s" }}/>
        </div>
      </div>

      <div style={{ display:"flex", gap:12, marginBottom:28, flexWrap:"wrap" }}>
        {[{ label:"TOTAL USERS", value: stats.total, color: C2.orange }, { label:"ACTIVE USERS", value: stats.withAssets, color: C2.green }, { label:"TOTAL ASSETS TRACKED", value: stats.totalAssets, color: C2.text }].map(s => (
          <div key={s.label} style={{ background:C2.bg2, border:"1px solid var(--border)", borderRadius:10, padding:"16px 20px", minWidth:160 }}>
            <div style={{ fontSize:8, fontWeight:700, letterSpacing:"0.2em", color:C2.text3, marginBottom:8 }}>{s.label}</div>
            <div style={{ fontSize:28, fontWeight:900, color:s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* ── DASHBOARD BANNER ─────────────────────────────────────────── */}
      <div style={{ background:C2.bg2, border:"1px solid var(--border)", borderRadius:10, padding:"20px 24px", marginBottom:24 }}>
        <div style={{ fontSize:9, fontWeight:800, letterSpacing:"0.25em", color:C2.text3, marginBottom:16 }}>DASHBOARD BANNER</div>

        {/* Current live banner */}
        {banner && !pendingBanner && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:9, color:C2.text3, marginBottom:8, letterSpacing:"0.1em" }}>CURRENT BANNER</div>
            <div style={{ width:"100%", height:120, borderRadius:8, overflow:"hidden", border:"1px solid var(--border)", marginBottom:10 }}>
              <img src={banner} alt="banner" style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"center", display:"block" }} />
            </div>
            <button onClick={removeBanner} disabled={bannerSaving}
              style={{ background:"rgba(224,62,26,0.07)", border:"1px solid rgba(224,62,26,0.4)", color:C2.red,
                borderRadius:7, padding:"7px 14px", fontFamily:"Montserrat,sans-serif", fontSize:10,
                fontWeight:700, cursor: bannerSaving ? "wait" : "pointer", opacity: bannerSaving ? 0.6 : 1 }}>
              🗑 Remove banner
            </button>
          </div>
        )}

        {/* Pending preview — selected but not yet uploaded */}
        {pendingBanner && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:9, color:C2.orange, marginBottom:8, letterSpacing:"0.1em" }}>NEW BANNER — NOT UPLOADED YET</div>
            <div style={{ width:"100%", height:120, borderRadius:8, overflow:"hidden",
              border:`1px solid ${C2.orange}`, marginBottom:12 }}>
              <img src={pendingBanner} alt="preview" style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"center", display:"block" }} />
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={() => saveBanner(pendingBanner)} disabled={bannerSaving}
                style={{ background:C2.orange, border:"none", color:"#000",
                  borderRadius:7, padding:"9px 20px", fontFamily:"Montserrat,sans-serif", fontSize:10,
                  fontWeight:800, cursor: bannerSaving ? "wait" : "pointer", opacity: bannerSaving ? 0.6 : 1,
                  letterSpacing:"0.1em" }}>
                {bannerSaving ? "Uploading…" : "⬆ UPLOAD BANNER"}
              </button>
              <button onClick={() => { setPendingBanner(null); setBannerErr(null); }}
                disabled={bannerSaving}
                style={{ background:"transparent", border:"1px solid var(--border)", color:C2.text3,
                  borderRadius:7, padding:"9px 14px", fontFamily:"Montserrat,sans-serif", fontSize:10,
                  fontWeight:700, cursor:"pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {!banner && !pendingBanner && (
          <div style={{ fontSize:10, color:C2.text3, marginBottom:14 }}>No custom banner set — using the default embedded image.</div>
        )}

        {/* Drop zone — always visible */}
        <div
          onDragOver={e => { e.preventDefault(); setBannerDrag(true); }}
          onDragLeave={() => setBannerDrag(false)}
          onDrop={e => { e.preventDefault(); setBannerDrag(false); handleBannerFile(e.dataTransfer.files[0]); }}
          onClick={() => bannerRef.current && bannerRef.current.click()}
          style={{ border:"2px dashed " + (bannerDrag ? C2.orange : "var(--border)"),
            borderRadius:10, padding:"22px 16px", display:"flex", flexDirection:"column",
            alignItems:"center", gap:6, cursor:"pointer", transition:"border-color 0.15s",
            background: bannerDrag ? "rgba(242,135,5,0.04)" : "transparent",
            opacity: bannerSaving ? 0.5 : 1, marginTop: (banner || pendingBanner) ? 14 : 0 }}>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700,
            color: bannerDrag ? C2.orange : C2.text3 }}>
            {pendingBanner ? "Choose different image" : banner ? "Choose new banner image" : "Choose banner image"}
          </div>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text5)" }}>
            Drag & drop or click · JPG / PNG / WEBP · max 300 KB
          </div>
        </div>
        <input ref={bannerRef} type="file" accept="image/*" style={{ display:"none" }}
          onChange={e => { handleBannerFile(e.target.files[0]); e.target.value = ""; }} />

        {bannerErr && (
          <div style={{ marginTop:10, fontSize:11, fontWeight:700, fontFamily:"Montserrat,sans-serif",
            padding:"10px 14px", borderRadius:8,
            color: bannerErr.startsWith("✅") ? C2.green : C2.red,
            background: bannerErr.startsWith("✅") ? "rgba(34,197,94,0.08)" : "rgba(224,62,26,0.1)",
            border: `1px solid ${bannerErr.startsWith("✅") ? "rgba(34,197,94,0.3)" : "rgba(224,62,26,0.3)"}`,
          }}>
            {bannerErr}
          </div>
        )}
      </div>

      <div style={{ background:C2.bg2, border:"1px solid var(--border)", borderRadius:10, overflow:"hidden" }}>
        <div style={{ padding:"12px 16px", borderBottom:"1px solid var(--border)", fontSize:9, fontWeight:800, letterSpacing:"0.2em", color:C2.text3 }}>REGISTERED USERS</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ background:"var(--bg3)" }}>
                {["EMAIL","ASSETS","TRADES","REGISTERED","LAST LOGIN"].map(h => (
                  <th key={h} style={{ padding:"10px 16px", fontSize:8, fontWeight:700, letterSpacing:"0.15em", color:C2.text3, textAlign:"left", borderBottom:"1px solid var(--border)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr><td colSpan={5} style={{ padding:20, textAlign:"center", color:C2.text3, fontSize:12 }}>No users found</td></tr>
              ) : users.map((u, i) => (
                <tr key={u.id} style={{ borderBottom:"1px solid var(--border)", background: i%2===0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                  <td style={{ padding:"10px 16px", fontSize:11, fontWeight:600, color:C2.text }}>{u.email || "—"}</td>
                  <td style={{ padding:"10px 16px", fontSize:12, fontWeight:700, color:C2.orange }}>{u.asset_count}</td>
                  <td style={{ padding:"10px 16px", fontSize:12, fontWeight:500, color:C2.text2 }}>{u.trade_count}</td>
                  <td style={{ padding:"10px 16px", fontSize:11, fontWeight:500, color:C2.text3 }}>{u.created_at?.slice(0,10) || "—"}</td>
                  <td style={{ padding:"10px 16px", fontSize:11, fontWeight:500, color:C2.text3 }}>{u.last_sign_in_at?.slice(0,10) || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── ASSET ACTIVITY ── */}
      <div style={{ background:C2.bg2, border:"1px solid var(--border)", borderRadius:10, overflow:"hidden", marginTop:24 }}>
        <div style={{ padding:"12px 16px", borderBottom:"1px solid var(--border)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ fontSize:9, fontWeight:800, letterSpacing:"0.2em", color:C2.text3 }}>ASSET ACTIVITY</div>
          <div style={{ fontSize:9, color:C2.text3 }}>{assetActivity.length} assets</div>
        </div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ background:"var(--bg3)" }}>
                {[
                  { key:"asset_id",       label:"TICKER" },
                  { key:"asset_type",     label:"TYPE" },
                  { key:"user_email",     label:"USER" },
                  { key:"trade_count",    label:"TRADES" },
                  { key:"total_invested", label:"INVESTED" },
                  { key:"last_trade",     label:"LAST TRADE" },
                ].map(({ key, label }) => (
                  <th key={key}
                    onClick={() => setActivitySort(s => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }))}
                    style={{ padding:"10px 16px", fontSize:8, fontWeight:700, letterSpacing:"0.15em",
                      color: activitySort.key === key ? C2.orange : C2.text3,
                      textAlign:"left", borderBottom:"1px solid var(--border)", cursor:"pointer", whiteSpace:"nowrap" }}>
                    {label} {activitySort.key === key ? (activitySort.dir === "desc" ? "↓" : "↑") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {assetActivity.length === 0 ? (
                <tr><td colSpan={6} style={{ padding:20, textAlign:"center", color:C2.text3, fontSize:12 }}>No data</td></tr>
              ) : [...assetActivity].sort((a, b) => {
                  const av = a[activitySort.key] ?? "";
                  const bv = b[activitySort.key] ?? "";
                  const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
                  return activitySort.dir === "desc" ? -cmp : cmp;
                }).map((row, i) => (
                <tr key={i} style={{ borderBottom:"1px solid var(--border)", background: i%2===0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                  <td style={{ padding:"10px 16px", fontSize:12, fontWeight:900, color:C2.orange }}>{row.asset_id}</td>
                  <td style={{ padding:"10px 16px", fontSize:9, fontWeight:700, letterSpacing:"0.1em",
                    color: row.asset_type === "crypto" ? "#60a5fa" : C2.text2 }}>
                    {(row.asset_type || "—").toUpperCase()}
                  </td>
                  <td style={{ padding:"10px 16px", fontSize:11, color:C2.text }}>{row.user_email || "—"}</td>
                  <td style={{ padding:"10px 16px", fontSize:12, fontWeight:700, color:C2.text2 }}>{row.trade_count}</td>
                  <td style={{ padding:"10px 16px", fontSize:11, fontWeight:700, color:C2.text }}>
                    {row.total_invested != null ? "$" + Number(row.total_invested).toLocaleString("en-US", { minimumFractionDigits:0, maximumFractionDigits:0 }) : "—"}
                  </td>
                  <td style={{ padding:"10px 16px", fontSize:11, color:C2.text3 }}>{row.last_trade || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </>}
    </div>
  );
}
function PortfolioTrackerInner() {
  // ── Auth ─────────────────────────────────────────────────────────────────────
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
      if (session?.user) setForceLoginModal(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "INITIAL_SESSION") return;
      if (event === "TOKEN_REFRESHED") return; // don't reload data on token refresh
      setUser(session?.user ?? null);
      setAuthLoading(false);
    });
    return () => subscription.unsubscribe();
  }, []);

  // ── Asset management ────────────────────────────────────────────────────────
  const [assets, setAssets]             = useState([]);           // list of { id, ticker, name }
  const [sidebarPrices, setSidebarPrices] = useState({});          // { [assetId]: price }
  const [activeAssetId, setActiveAssetId] = useState(() => {
    try { return localStorage.getItem("compaundia_asset_id") || null; } catch { return null; }
  });
  const [expandedAssetId, setExpandedAssetId] = useState(null);
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [deleteAssetConfirm, setDeleteAssetConfirm] = useState(null);
  const [assetsExpanded, setAssetsExpanded] = useState(true);
  const [newTicker, setNewTicker]       = useState("");
  const [newName, setNewAssetName]      = useState("");
  const [newAssetType, setNewAssetType] = useState("stock"); // "stock" | "crypto"
  const [userPlan, setUserPlan]         = useState("free"); // "free" | "pro" | "unlimited"
  const [isFreeMode, setIsFreeMode]     = useState(false);
  const [dashBanner, setDashBanner]     = useState(null);
  const [portfolioTotalValue, setPortfolioTotalValue] = useState(0);

  useEffect(() => {
    if (!assets || assets.length === 0) { setPortfolioTotalValue(0); return; }
    (async () => {
      try {
        const results = await Promise.all(assets.map(async (asset) => {
          let entries = [];
          if (user) {
            const { data } = await supabase.from("entries").select("dollars, shares")
              .eq("user_id", user.id).eq("asset_id", asset.id);
            entries = data || [];
          }
          const totalShares = entries.reduce((s, e) => s + e.shares, 0);
          if (totalShares === 0) return 0;
          let livePrice = 0;
          try {
            const res = await fetch(`/api/mstr-price?ticker=${encodeURIComponent(asset.yahooTicker || asset.ticker)}&t=${Date.now()}`);
            if (res.ok) { const d = await res.json(); livePrice = d.price || 0; }
          } catch {}
          return totalShares * livePrice;
        }));
        setPortfolioTotalValue(results.reduce((s, v) => s + v, 0));
      } catch {}
    })();
  }, [assets, user]);
  const [mobileDrawer, setMobileDrawer] = useState(null); // null | "assets" | "more"
  const [showPaywall, setShowPaywall]   = useState(false);

  // ── Per-asset data ───────────────────────────────────────────────────────── 
  const [entries, setEntries]           = useState([]);
  const [form, setForm]                 = useState(emptyForm);
  const [editId, setEditId]             = useState(null);
  const [editForm, setEditForm]         = useState(emptyForm);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [clearConfirm, setClearConfirm]   = useState(false);
  const [showAllTrades, setShowAllTrades] = useState(false);
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [chartReady, setChartReady] = useState(false);
  const [tradeTickerInput, setTradeTickerInput] = useState("");
  const [showTickerDropdown, setShowTickerDropdown] = useState(false);
  const [tradeType, setTradeType] = useState("stock");
  const [liveSearchResults, setLiveSearchResults] = useState([]);
  const [liveSearchLoading, setLiveSearchLoading] = useState(false);
  const searchDebounceRef = useRef(null);
  const [sortState, setSortState]       = useState({ key:"date", dir:"asc" });

  const DEFAULT_SECTION_ORDER = [
    "price_history", "best_worst", "trades_grid", "price_trade_chart", "trade_calendar",
    "weekly_chart", "pnl_shares", "monthly_breakdown", "target_avg", "shares_per_month_week", "shares_cards", "underwater", "buy_zone"
  ];
  const [sectionOrder, setSectionOrder] = useState(DEFAULT_SECTION_ORDER);
  const [hiddenSections, setHiddenSections] = useState({});
  const [showSectionsMenu, setShowSectionsMenu] = useState(false);
  const [dashHidden, setDashHidden]           = useState({});
  const [showDashMenu, setShowDashMenu]        = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    if (!showDashMenu) return;
    const handler = (e) => {
      if (!e.target.closest("[data-sections-menu]")) setShowDashMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showDashMenu]);

  useEffect(() => {
    if (!showDashMenu) return;
    const handler = (e) => { if (!e.target.closest("[data-sections-menu]")) setShowDashMenu(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showDashMenu]);

  useEffect(() => {
    if (!showSectionsMenu) return;
    const handler = (e) => {
      if (!e.target.closest("[data-sections-menu]")) setShowSectionsMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showSectionsMenu]);
  const { dragState: sectionDragState, dragOverId, onMouseDragStart: onSectionDragStart } =
    useDragSort(sectionOrder, setSectionOrder, "section-id");
  const isDragging = sectionDragState?.id ?? null;
  const prevOrderRef = useRef(DEFAULT_SECTION_ORDER);
  const [period, setPeriod]             = useState("ALL");
  const [manualPrice, setManualPrice]   = useState("");
  const [isManual, setIsManual]         = useState(false);
  const [priceLoading, setPriceLoading] = useState(true);
  const [activePage, setActivePage] = useState(() => {
    try { return localStorage.getItem("compaundia_page") || "portfolio"; } catch { return "portfolio"; }
  });
  const [marketsSelected, setMarketsSelected] = useState(null); // { symbol, name, mode }

  // Browser back button support for markets detail
  useEffect(() => {
    const handler = () => {
      setMarketsSelected(null);
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  const openMarketTicker = (t) => {
    window.history.pushState({ marketsDetail: true }, "");
    setMarketsSelected(t);
  };

  const closeMarketTicker = () => {
    setMarketsSelected(null);
    // If we pushed a state, go back to clean it up
    if (window.history.state?.marketsDetail) window.history.back();
  };
  const navigateTo = (page) => {
    setActivePage(page);
    if (marketsSelected) {
      setMarketsSelected(null);
      if (window.history.state?.marketsDetail) window.history.back();
    }
    try { localStorage.setItem("compaundia_page", page); } catch {}
  };

  const persistAssetId = (id) => {
    setActiveAssetId(id);
    try {
      if (id) localStorage.setItem("compaundia_asset_id", id);
      else    localStorage.removeItem("compaundia_asset_id");
    } catch {}
  };
  const [sidebarOpen, setSidebarOpen]   = useState(true);
  const [theme, setTheme]               = useState("dark");
  const isMobile                        = useIsMobile();

  const priceInitialized = useRef(false);

  const [forceLoginModal, setForceLoginModal] = useState(false);

  // Load banner for everyone including demo users
  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase
          .from("app_settings").select("value").eq("key", "dashboard_banner").maybeSingle();
        if (data?.value) setDashBanner(data.value);
      } catch {}
    })();
  }, []);

  // Set demo asset on load
  useEffect(() => {
    if (!user && !authLoading) {
      persistAssetId(DEMO_ASSETS[0].id);
      requestAnimationFrame(() => requestAnimationFrame(() => setChartReady(true)));
    }
  }, [user, authLoading]);

  const isDemo = !user;
  const showLoginModal = forceLoginModal;
  const effectiveAssets = isDemo ? DEMO_ASSETS : assets;
  const effectiveEntries = isDemo
    ? (activeAssetId ? (DEMO_ENTRIES_MAP[activeAssetId] || []) : [])
    : entries;

  // Auto-fetch price for active asset
  const fetchLivePrice = async () => {
    if (isManual) return;
    const _allAssets = user ? assets : DEMO_ASSETS;
    const activeAsset = _allAssets.find(a => a.id === activeAssetId);
    const ticker = activeAsset?.yahooTicker || activeAsset?.ticker || "MSTR";
    if (!priceInitialized.current) setPriceLoading(true);
    try {
      // Try Vercel API route first
      const res = await fetch(`/api/mstr-price?ticker=${encodeURIComponent(ticker)}&t=${Date.now()}`);
      if (res.ok) {
        const data = await res.json();
        if (data.price && data.price > 0) {
          setManualPrice(prev => prev === data.price.toFixed(2) ? prev : data.price.toFixed(2));
          if (activeAssetId) setSidebarPrices(prev => ({ ...prev, [activeAssetId]: data.price }));
          priceInitialized.current = true;
          setPriceLoading(false);
          return;
        }
      }
      // Fallback to allorigins
      const url = `https://query1.finance.yahoo.com/v8/finance/chart/${encodeURIComponent(ticker)}?interval=1m&range=1d`;
      const res2 = await fetch(`https://api.allorigins.win/get?url=${encodeURIComponent(url)}&t=${Date.now()}`);
      if (!res2.ok) throw new Error("fetch error");
      const outer = await res2.json();
      const data2 = JSON.parse(outer.contents);
      const price = data2?.chart?.result?.[0]?.meta?.regularMarketPrice;
      if (price && price > 0) {
        setManualPrice(prev => prev === price.toFixed(2) ? prev : price.toFixed(2));
        priceInitialized.current = true;
        setPriceLoading(false);
        return;
      }
      // No price found — switch to manual mode, load saved price if exists
      const saved = localStorage.getItem(`manual_price_${activeAssetId}`);
      if (saved) setManualPrice(saved);
      setIsManual(true);
    } catch(e) {
      // On error — switch to manual mode
      const saved = localStorage.getItem(`manual_price_${activeAssetId}`);
      if (saved) setManualPrice(saved);
      setIsManual(true);
    }
    priceInitialized.current = true;
    setPriceLoading(false);
  };

  useEffect(() => {
    if (!isManual) {
      fetchLivePrice();
      const interval = setInterval(fetchLivePrice, 60000);
      return () => clearInterval(interval);
    }
  }, [isManual, activeAssetId]);

  const activeAsset = (user ? assets : DEMO_ASSETS).find(a => a.id === activeAssetId) || null;


  const ASSETS_KEY  = "portfolio_tracker_assets_v1";
  const entriesKey  = (id) => `portfolio_asset_entries_${id}`;

  // ── Load everything on mount ────────────────────────────────────────────────
  const MSTR_ASSET = { id: "asset_mstr", ticker: "MSTR", name: "Strategy (MicroStrategy)", yahooTicker: "MSTR" };

  const loadAsset = async (id) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("entries")
        .select("*")
        .eq("user_id", user.id)
        .eq("asset_id", id)
        .order("date", { ascending: true });
      if (error) throw error;
      setEntries(data ? data.map(e => ({ id:e.id, date:e.date, dollars:e.dollars, shares:e.shares })) : []);
    } catch(e) {
      console.error("loadAsset error", e);
      setEntries([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        setLoading(true);

        // Load user plan + global free mode
        const [{ data: planData }, { data: freeSetting }, { data: bannerSetting }] = await Promise.all([
          supabase.from("user_settings").select("plan").eq("user_id", user.id).maybeSingle(),
          supabase.from("app_settings").select("value").eq("key", "is_free").maybeSingle(),
          supabase.from("app_settings").select("value").eq("key", "dashboard_banner").maybeSingle(),
        ]);
        if (planData?.plan) {
          setUserPlan(planData.plan);
        } else {
          await supabase.from("user_settings").upsert({ user_id: user.id, plan: "free" }, { onConflict: "user_id" });
          setUserPlan("free");
        }
        if (freeSetting?.value === "true") setIsFreeMode(true);
        if (bannerSetting?.value) setDashBanner(bannerSetting.value);
        const { data, error } = await supabase
          .from("entries")
          .select("asset_id, yahoo_ticker")
          .eq("user_id", user.id);

        if (error) throw error;

        // Build asset list from unique asset_ids
        const assetIds = [...new Set((data || []).map(e => e.asset_id).filter(Boolean))];
        let assetList = [];
        if (assetIds.length > 0) {
          assetIds.forEach(id => {
            if (!assetList.find(a => a.id === id)) {
              const withoutPrefix = id.replace(/^asset_/, "");
              const parts = withoutPrefix.split("_");
              if (parts.length > 1 && /^\d+$/.test(parts[parts.length - 1])) parts.pop();
              const ticker = id === "asset_mstr" ? "MSTR" : parts.join("").toUpperCase();
              const name = id === "asset_mstr" ? "Strategy (MicroStrategy)" : ticker;
              const entry = (data || []).find(e => e.asset_id === id && e.yahoo_ticker);
              const cryptoMatch = POPULAR_CRYPTO.find(c => c.ticker === ticker);
              const yahooTicker = entry?.yahoo_ticker || cryptoMatch?.yahooTicker || ticker;
              assetList.push({ id, ticker, name, yahooTicker });
            }
          });
        }
        setAssets(assetList);

        // Fetch prices for all assets for sidebar display (background, no await)
        Promise.all(assetList.map(async a => {
          try {
            const res = await fetch(`/api/mstr-price?ticker=${encodeURIComponent(a.yahooTicker || a.ticker)}&t=${Date.now()}`);
            if (res.ok) { const d = await res.json(); return [a.id, d.price || 0]; }
          } catch {}
          return [a.id, 0];
        })).then(results => {
          setSidebarPrices(Object.fromEntries(results));
        });

        // Restore last visited asset from localStorage, fallback to first
        const savedId   = (() => { try { return localStorage.getItem("compaundia_asset_id"); } catch { return null; } })();
        const savedPage = (() => { try { return localStorage.getItem("compaundia_page") || "portfolio"; } catch { return "portfolio"; } })();
        const targetId  = assetList.find(a => a.id === savedId)?.id || assetList[0]?.id || null;
        persistAssetId(targetId);
        navigateTo(savedPage === "portfolio" ? "portfolio" : savedPage);

        // Load target asset entries
        const loadId = targetId;
        if (loadId) {
          const { data: firstData } = await supabase
            .from("entries")
            .select("*")
            .eq("user_id", user.id)
            .eq("asset_id", loadId)
            .order("date", { ascending: true });
          setEntries(firstData ? firstData.map(e => ({ id:e.id, date:e.date, dollars:e.dollars, shares:e.shares })) : []);
          await loadAsset(loadId);
        } else {
          setEntries([]);
        }
        requestAnimationFrame(() => requestAnimationFrame(() => setChartReady(true)));

      } catch(e) {
        console.error("load error", e);
        setAssets([]);
        persistAssetId(null);
        navigateTo("portfolio");
        setEntries([]);
      }
      setLoading(false);
    })();
  }, [user?.id]);

  // Switch asset
  const switchAsset = async (id) => {
    if (id === activeAssetId && activePage !== "portfolio") return;
    setEntries([]);
    setManualPrice("");
    setPriceLoading(true);
    priceInitialized.current = false;
    setIsManual(false);
    setPeriod("ALL");
    persistAssetId(id);
    navigateTo("portfolio");
    setMobileDrawer(null);
    setChartReady(false);
    requestAnimationFrame(() => requestAnimationFrame(() => setChartReady(true)));
    await loadAsset(id);
  };

  const persist = async (data) => {
    if (!activeAssetId) return;
    setSaving(true);
    try { await window.storage.set(entriesKey(activeAssetId), JSON.stringify(data)); } catch (_) {}
    setSaving(false);
  };

  const persistAssets = async (list) => {
    await window.storage.set(ASSETS_KEY, JSON.stringify(list));
  };

  const createAsset = async () => {
    if (!newTicker.trim()) return;

    // Check plan limits
    const limit = isFreeMode ? Infinity : userPlan === "unlimited" ? Infinity : userPlan === "pro" ? 10 : 1;
    console.log("PLAN CHECK:", { userPlan, isFreeMode, limit, assetsLength: assets.length, willBlock: assets.length >= limit });
    if (assets.length >= limit) {
      setShowAssetModal(false);
      setShowPaywall(true);
      return;
    }

    const id = `asset_${newTicker.trim().toLowerCase()}_${Date.now()}`;
    const asset = { id, ticker: newTicker.trim().toUpperCase(), name: newName.trim() || newTicker.trim().toUpperCase() };
    const updated = [...assets, asset];
    setAssets(updated);
    setEntries([]);
    setManualPrice("");
    setPriceLoading(true);
    setPeriod("ALL");
    persistAssetId(id);
    setNewTicker(""); setNewAssetName(""); setShowAssetModal(false);
  };

  const deleteAsset = async (id) => {
    await supabase.from("entries").delete().eq("user_id", user.id).eq("asset_id", id);
    const updated = assets.filter(a => a.id !== id);
    setAssets(updated);
    if (updated.length === 0) {
      persistAssetId(null);
      setEntries([]);
    } else if (activeAssetId === id) {
      persistAssetId(updated[0].id);
      await loadAsset(updated[0].id);
    }
  };

  const addEntry = async () => {
    if (!form.date || !form.dollars || !form.shares || !user) return;
    setSaving(true);
    const { data, error } = await supabase.from("entries").insert({
      user_id: user.id,
      date: form.date,
      dollars: parseFloat(form.dollars),
      shares: parseFloat(form.shares),
    }).select().single();
    if (!error && data) {
      const updated = [...entries, { id:data.id, date:data.date, dollars:data.dollars, shares:data.shares }]
        .sort((a,b) => a.date.localeCompare(b.date));
      setEntries(updated);
      setForm(emptyForm);
    }
    setSaving(false);
  };

  const startEdit = (e) => { setEditId(e.id); setEditForm({ date:e.date, dollars:e.dollars, shares:e.shares }); };
  const saveEdit = async () => {
    if (!user) return;
    const { error } = await supabase.from("entries").update({
      date: editForm.date,
      dollars: parseFloat(editForm.dollars),
      shares: parseFloat(editForm.shares),
    }).eq("id", editId).eq("user_id", user.id);
    if (!error) {
      const updated = entries.map(e => e.id===editId
        ? {...e, date:editForm.date, dollars:parseFloat(editForm.dollars), shares:parseFloat(editForm.shares)}
        : e).sort((a,b) => a.date.localeCompare(b.date));
      setEntries(updated);
      setEditId(null);
    }
  };

  const deleteEntry = async (id) => {
    if (!user) return;
    await supabase.from("entries").delete().eq("id", id).eq("user_id", user.id);
    setEntries(entries.filter(e => e.id !== id));
    setDeleteConfirm(null);
  };

  const importEntries = async (newEntries, replace = false) => {
    if (!user) return;
    if (replace) {
      await supabase.from("entries").delete().eq("user_id", user.id).eq("asset_id", activeAssetId);
    }
    const toInsert = newEntries.map(e => ({ user_id: user.id, asset_id: activeAssetId, date: e.date, dollars: e.dollars, shares: e.shares }));
    const { data } = await supabase.from("entries").insert(toInsert).select();
    if (data) {
      const mapped = data.map(e => ({ id:e.id, date:e.date, dollars:e.dollars, shares:e.shares }));
      const all = replace ? mapped : [...entries, ...mapped];
      setEntries(all.sort((a,b) => a.date.localeCompare(b.date)));
    }
  };

  const handleSort = (key) => {
    setSortState(prev => ({ key, dir: prev.key===key && prev.dir==="asc" ? "desc" : "asc" }));
  };

  // ── Auth guards ──────────────────────────────────────────────────────────────


  if (authLoading) return (
    <div style={{ minHeight:"100vh", background:"#0a0a0a", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, letterSpacing:"0.3em", color:"#333" }}>
        LOADING...
      </div>
    </div>
  );



  const manualVal = parseFloat(manualPrice);
  const cp        = priceLoading ? 0 : (manualVal > 0 ? manualVal : 0);
  const hasCp          = cp > 0;

  // Period filter
  const periodDays = { "1M":30, "3M":90, "6M":180, "1Y":365, "ALL":null };
  const filteredEntries = (() => {
    const days = periodDays[period];
    if (!days) return effectiveEntries;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    const cutoffStr = cutoff.toISOString().split("T")[0];
    return effectiveEntries.filter(e => e.date >= cutoffStr);
  })();

  const totalDollars   = filteredEntries.reduce((s,e) => s+e.dollars, 0);
  const totalShares    = filteredEntries.reduce((s,e) => s+e.shares, 0);
  const avgPrice       = totalShares > 0 ? totalDollars / totalShares : 0;
  const portfolioValue = totalShares * cp;
  const portfolioPnl   = portfolioValue - totalDollars;
  const portfolioPct   = totalDollars > 0 ? (portfolioPnl / totalDollars) * 100 : 0;
  const distBE         = hasCp && avgPrice > 0 ? ((cp - avgPrice) / avgPrice) * 100 : null;
  const pnlCol = (v) => v >= 0 ? '#5aad27' : C.red;

  // ── Best / Worst ─────────────────────────────────────────────────────────
  const entriesWithPnl = hasCp ? filteredEntries.map(e => ({
    ...e,
    pnlPct: e.dollars > 0 ? ((e.shares * cp - e.dollars) / e.dollars) * 100 : 0,
    pnl: e.shares * cp - e.dollars,
    buyPrice: e.shares > 0 ? e.dollars / e.shares : 0,
  })) : [];
  const bestEntry  = entriesWithPnl.length ? entriesWithPnl.reduce((a,b) => a.pnlPct > b.pnlPct ? a : b) : null;
  const worstEntry = entriesWithPnl.length ? entriesWithPnl.reduce((a,b) => a.pnlPct < b.pnlPct ? a : b) : null;
  const winCount   = entriesWithPnl.filter(e => e.pnl >= 0).length;
  const lossCount  = entriesWithPnl.filter(e => e.pnl < 0).length;

  // ── Crypto asset detection ───────────────────────────────────────────────
  const isCryptoAsset = activeAsset
    ? (POPULAR_CRYPTO.some(c => c.ticker === activeAsset.ticker || c.yahooTicker === activeAsset.ticker)
      || (activeAsset.yahooTicker || "").endsWith("-USD"))
    : false;
  const sharesLabel = isCryptoAsset ? "Tokens" : "Shares";
  const SHARES_LABEL = isCryptoAsset ? "TOKENS" : "SHARES";

  // ── Monthly data ──────────────────────────────────────────────────────────
  const monthlyData = filteredEntries.reduce((acc, e) => {
    const key = e.date.slice(0, 7);
    if (!acc[key]) acc[key] = { month: key, dollars: 0, shares: 0 };
    acc[key].dollars += e.dollars;
    acc[key].shares  += e.shares;
    return acc;
  }, {});
  const monthlyArr = Object.values(monthlyData).sort((a,b) => a.month.localeCompare(b.month));

  // Weekly data: group by ISO week (Mon-Sun), key = "YYYY-Www"
  const weeklyData = filteredEntries.reduce((acc, e) => {
    const d = new Date(e.date);
    // Get Monday of the week
    const day = d.getDay(); // 0=Sun
    const diff = (day === 0 ? -6 : 1 - day);
    const mon = new Date(d); mon.setDate(d.getDate() + diff);
    const key = mon.toISOString().split("T")[0]; // "YYYY-MM-DD" of Monday
    if (!acc[key]) acc[key] = { week: key, dollars: 0, shares: 0 };
    acc[key].dollars += e.dollars;
    acc[key].shares  += e.shares;
    return acc;
  }, {});
  const weeklyArr = Object.values(weeklyData).sort((a,b) => a.week.localeCompare(b.week));


  const sortedEntries = [...filteredEntries].sort((a, b) => {
    const bpA = a.shares > 0 ? a.dollars/a.shares : 0;
    const bpB = b.shares > 0 ? b.dollars/b.shares : 0;
    const pnlA = hasCp ? a.shares*cp - a.dollars : 0;
    const pnlB = hasCp ? b.shares*cp - b.dollars : 0;
    const pctA = a.dollars > 0 ? (pnlA/a.dollars)*100 : 0;
    const pctB = b.dollars > 0 ? (pnlB/b.dollars)*100 : 0;
    const map = { date:[a.date,b.date], dollars:[a.dollars,b.dollars], shares:[a.shares,b.shares], buyPrice:[bpA,bpB], pnl:[pnlA,pnlB], pnlPct:[pctA,pctB] };
    const [av, bv] = map[sortState.key] || [a.date,b.date];
    if (av < bv) return sortState.dir==="asc" ? -1 : 1;
    if (av > bv) return sortState.dir==="asc" ?  1 : -1;
    return 0;
  });

  const iF = (e) => { e.target.style.borderColor = C.orange; };
  const iB = (e) => { e.target.style.borderColor = C.blackBorder; };

  const NAV = [
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700;900&display=swap');
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}
        @keyframes tickerScroll{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
        @keyframes dotPop{from{transform:scale(0)}to{transform:scale(1)}}
        *{box-sizing:border-box;margin:0;padding:0;}
        html, body { background: #000 !important; }
        input[type=date]::-webkit-calendar-picker-indicator{filter:invert(0.5) sepia(1) saturate(5) hue-rotate(5deg);}
        input::placeholder{color:var(--placeholder);font-family:Montserrat,sans-serif;}
        ::-webkit-scrollbar{width:5px;height:5px;}
        ::-webkit-scrollbar-track{background:var(--bg);}
        ::-webkit-scrollbar-thumb{background:var(--border2);border-radius:3px;}
        tbody tr:hover td{background:var(--hover)!important;}
        input[type=range]{-webkit-appearance:none;appearance:none;background:transparent;cursor:pointer;}
        input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;width:1px;height:1px;background:transparent;border:none;box-shadow:none;}
        input[type=range]::-moz-range-thumb{width:1px;height:1px;background:transparent;border:none;}
        input[type=range]::-webkit-slider-runnable-track{background:transparent;height:6px;}
        input[type=range]::-moz-range-track{background:transparent;height:6px;}
        @media (max-width: 767px) {
          .mobile-cards { grid-template-columns: 1fr 1fr !important; }
          .mobile-hide { display: none !important; }
          .mobile-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
          /* Bigger touch targets */
          button { min-height: 36px; }
          /* Prevent horizontal overflow */
          .mobile-page { overflow-x: hidden; }
          /* Tables always scroll on mobile */
          table { font-size: 12px !important; }
          th, td { padding: 10px 12px !important; white-space: nowrap; }
        }
        @keyframes slideUp {
          from { transform: translateY(100%); opacity:0; }
          to   { transform: translateY(0); opacity:1; }
        }
        .theme-dark{
          --logo-filter: invert(1);
          --bg:#0a0a0a; --bg2:#161616; --bg3:#101010; --bg4:#0d0d0d;
          --border:#2c2c2c; --border2:#383838; --text:#F2F0E4; --text2:#c0bdb0;
          --text3:#8a8680; --text4:#6a6660; --text5:#4a4640;
          --placeholder:#3a3630; --hover:rgba(242,135,5,0.06);
          --row-even:#0a0a0a; --row-odd:#131313;
          --sidebar:#080808;
        }
        .theme-light{
          --logo-filter: none;
          --bg:#eef2f7; --bg2:#ffffff; --bg3:#e6ecf4; --bg4:#f5f7fb;
          --border:#c8d6e8; --border2:#a8bcd4; --text:#1a2332; --text2:#2d4a6e;
          --text3:#4a6fa5; --text4:#6b8bb5; --text5:#9bb0cc;
          --placeholder:#9bb0cc; --hover:rgba(74,111,165,0.06);
          --row-even:#ffffff; --row-odd:#f0f4f9;
          --sidebar:#dce6f2;
        }
      `}</style>

      <div className={`theme-${theme}`} style={{ display:"flex", minHeight:"100vh", background:"var(--bg)", color:"var(--text)", transition:"background 0.2s, color 0.2s" }}>

        {/* ── SIDEBAR (desktop only) ── */}
        <link rel="stylesheet" href="https://unpkg.com/@phosphor-icons/web@2.1.1/src/regular/style.css"/>
        <div style={{
          width: isMobile ? 0 : sidebarOpen ? 200 : 64,
          minHeight:"100vh", background:"var(--sidebar)",
          borderRight: isMobile ? "none" : "1px solid var(--border)",
          display: isMobile ? "none" : "flex", flexDirection:"column",
          transition:"width 0.22s cubic-bezier(0.4,0,0.2,1)",
          overflow:"hidden", flexShrink:0, position:"sticky",
          top:0, alignSelf:"flex-start", height:"100vh",
        }}>
          {/* Logo + Toggle — fixed height so button never jumps */}
          <div style={{ display:"flex", alignItems:"center", justifyContent: sidebarOpen ? "space-between" : "center", padding:"0 12px", height:72, flexShrink:0 }}>
            {sidebarOpen && (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 33.9" style={{ height:53, width:"auto" }}>
                <defs><style>{`.cl0{fill:var(--text)}.cl1{fill:#f26622}`}</style></defs>
                <g>
                  <path className="cl0" d="M14,14.5c.2-.2.4-.4.7-.5.3-.1.6-.2.9-.2s.7,0,1.1.2.6.4.9.7l1.5-1.3c-.4-.5-.9-.9-1.5-1.1-.6-.3-1.3-.4-2-.4s-1.2.1-1.8.3c-.5.2-1,.5-1.4.9-.4.4-.7.8-.9,1.3-.2.5-.3,1.1-.3,1.7s.1,1.2.3,1.7c.2.5.5,1,.9,1.3.4.4.9.7,1.4.9.5.2,1.1.3,1.8.3s1.4-.1,2-.4c.6-.3,1.1-.6,1.5-1.1l-1.5-1.3c-.3.3-.6.5-.9.7-.3.2-.7.2-1.1.2s-.6,0-.9-.2c-.3-.1-.5-.3-.7-.5-.2-.2-.4-.5-.5-.7-.1-.3-.2-.6-.2-1s0-.7.2-1c.1-.3.3-.5.5-.7Z"/>
                  <path className="cl0" d="M24.8,14.3c-.5-.3-1.2-.4-1.9-.4s-1.3.1-1.8.4c-.5.3-1,.7-1.3,1.1-.3.5-.5,1.1-.5,1.7s.2,1.2.5,1.7c.3.5.7.9,1.3,1.2s1.2.4,1.8.4,1.3-.1,1.9-.4c.5-.3,1-.7,1.3-1.2.3-.5.5-1.1.5-1.7s-.2-1.2-.5-1.7c-.3-.5-.7-.9-1.3-1.1ZM24.1,17.9c-.1.2-.3.4-.5.5-.2.1-.4.2-.7.2s-.5,0-.7-.2c-.2-.1-.4-.3-.5-.5-.1-.2-.2-.5-.2-.8s0-.6.2-.8c.1-.2.3-.4.5-.5s.4-.2.7-.2.5,0,.7.2.4.3.5.5.2.5.2.8,0,.6-.2.8Z"/>
                  <path className="cl0" d="M37.1,14.1c-.4-.2-.8-.3-1.3-.3s-1.1.1-1.6.4c-.2.2-.4.3-.6.6-.1-.2-.3-.4-.5-.6-.4-.3-.9-.4-1.5-.4s-.9.1-1.4.3c-.2.1-.4.3-.5.4v-.7h-2.1v6.3h2.2v-3.1c0-.3,0-.6.1-.8,0-.2.2-.4.4-.5.2-.1.4-.2.6-.2s.5.1.7.3c.2.2.3.5.3,1v3.3h2.2v-3.1c0-.3,0-.6.1-.8,0-.2.2-.4.4-.5.2-.1.4-.2.6-.2s.5.1.7.3c.2.2.3.5.3,1v3.3h2.2v-3.6c0-.7-.1-1.2-.3-1.6-.2-.4-.5-.7-.9-.9Z"/>
                  <path className="cl0" d="M45.3,14.2c-.5-.3-1-.4-1.6-.4s-1,.1-1.4.4c-.2.1-.3.2-.4.4v-.6h-2.1v8.6h2.2v-2.8c.1.1.2.2.4.3.4.2.8.4,1.4.4s1.1-.1,1.6-.4c.5-.3.8-.6,1.1-1.1.3-.5.4-1.1.4-1.7s-.1-1.2-.4-1.7c-.3-.5-.6-.9-1.1-1.1ZM44.4,17.9c-.1.2-.3.4-.5.5-.2.1-.4.2-.7.2s-.5,0-.7-.2c-.2-.1-.4-.3-.5-.5-.1-.2-.2-.5-.2-.8s0-.6.2-.8c.1-.2.3-.4.5-.5.2-.1.4-.2.7-.2s.5,0,.7.2.4.3.5.5c.1.2.2.5.2.8s0,.6-.2.8Z"/>
                  <path className="cl0" d="M52.8,14.5c-.6-.5-1.4-.7-2.4-.7s-1,0-1.5.2-.9.3-1.3.5l.7,1.5c.2-.2.5-.3.8-.4.3,0,.6-.1.9-.1.5,0,.8,0,1,.3.2.2.3.4.3.7h-1.3c-.7,0-1.2,0-1.6.2-.4.2-.7.4-.9.7-.2.3-.3.6-.3,1s0,.7.3,1c.2.3.4.5.8.7.4.2.8.3,1.3.3s1-.1,1.3-.3c.2-.1.4-.3.5-.6v.8h2.1v-3.5c0-1-.3-1.7-.8-2.2ZM51,18.8c-.2.1-.4.2-.7.2s-.5,0-.6-.2c-.1-.1-.2-.3-.2-.5s0-.4.2-.5c.1-.1.4-.2.8-.2h1v.5c0,.2-.2.4-.4.6Z"/>
                  <path className="cl0" d="M59.5,17c0,.4,0,.6-.2.9-.1.2-.3.4-.4.5-.2.1-.4.2-.6.2-.3,0-.6-.1-.8-.3-.2-.2-.3-.6-.3-1.1v-3.2h-2.2v3.5c0,.7.1,1.2.3,1.6.2.4.5.7,1,1,.4.2.9.3,1.4.3s.9-.1,1.3-.3c.2-.1.4-.3.5-.4v.7h2.1v-6.3h-2.2v3Z"/>
                  <path className="cl0" d="M68.6,14.1c-.4-.2-.8-.3-1.3-.3s-1,.1-1.4.3c-.2.1-.4.3-.6.5v-.7h-2.1v6.3h2.2v-3.1c0-.4,0-.6.2-.9.1-.2.3-.4.5-.5.2-.1.4-.2.6-.2s.6.1.8.3c.2.2.3.5.3,1v3.3h2.2v-3.6c0-.7-.1-1.2-.3-1.6-.2-.4-.5-.7-.9-.9Z"/>
                  <path className="cl0" d="M75.9,14.5c-.1-.1-.2-.2-.4-.3-.4-.2-.8-.4-1.4-.4s-1.1.1-1.6.4c-.5.3-.8.6-1.1,1.1-.3.5-.4,1.1-.4,1.7s.1,1.3.4,1.7c.3.5.7.9,1.1,1.1.5.3,1,.4,1.6.4s1-.1,1.4-.4c.2-.1.3-.2.4-.4v.6h2.1v-9.6h-2.2v3.8ZM75.7,17.9c-.1.2-.3.4-.5.5-.2.1-.4.2-.7.2s-.5,0-.7-.2c-.2-.1-.4-.3-.5-.5-.1-.2-.2-.5-.2-.8s0-.6.2-.8.3-.4.5-.5.4-.2.7-.2.5,0,.7.2c.2.1.4.3.5.5s.2.5.2.8,0,.6-.2.8Z"/>
                  <rect className="cl0" x="79.5" y="13.9" width="2.2" height="6.3"/>
                  <path className="cl0" d="M88.2,14.5c-.6-.5-1.4-.7-2.4-.7s-1,0-1.5.2-.9.3-1.3.5l.7,1.5c.2-.2.5-.3.8-.4.3,0,.6-.1.9-.1.5,0,.8,0,1,.3.2.2.3.4.3.7h-1.3c-.7,0-1.2,0-1.6.2-.4.2-.7.4-.9.7-.2.3-.3.6-.3,1s0,.7.3,1c.2.3.4.5.8.7.4.2.8.3,1.3.3s1-.1,1.3-.3c.2-.1.4-.3.5-.6v.8h2.1v-3.5c0-1-.3-1.7-.8-2.2ZM86.4,18.8c-.2.1-.4.2-.7.2s-.5,0-.6-.2c-.1-.1-.2-.3-.2-.5s0-.4.2-.5c.1-.1.4-.2.8-.2h1v.5c0,.2-.2.4-.4.6Z"/>
                </g>
                <circle className="cl1" cx="80.6" cy="12.1" r="1.4"/>
              </svg>
            )}
            <button onClick={() => setSidebarOpen(o => !o)} style={{
              background:"var(--bg3)", border:"1px solid var(--border)",
              borderRadius:7, width:28, height:28, cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center",
              transition:"all 0.2s", flexShrink:0, color:"var(--text3)", padding:0,
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = C.orange; e.currentTarget.style.color = C.orange; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.color = "var(--text3)"; }}>
              {sidebarOpen ? (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M7.5 2L3.5 6L7.5 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M4.5 2L8.5 6L4.5 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          </div>

          {/* Nav */}
          <nav style={{ display:"flex", flexDirection:"column", gap:2, padding:"0 8px", flex:1, overflowY:"auto" }}>
            <button onClick={() => { navigateTo("portfolio"); persistAssetId(null); }} style={{
              width:"100%", background: activePage === "portfolio" && !activeAssetId ? "rgba(242,135,5,0.08)" : "transparent",
              border:"none", borderRadius:8, padding:"10px 12px",
              cursor:"pointer", display:"flex", alignItems:"center",
              gap:10, transition:"all 0.15s", marginBottom:2,
            }}>
              <i className="ph ph-house" style={{ fontSize:18, flexShrink:0, width:18, color: activePage === "portfolio" && !activeAssetId ? C.orange : "var(--text3)" }}/>
              <span style={{ fontFamily:"Montserrat,sans-serif", fontWeight:400, color: activePage === "portfolio" && !activeAssetId ? C.orange : "var(--text3)", letterSpacing:"0.05em", fontSize:13, overflow:"hidden", whiteSpace:"nowrap", maxWidth: sidebarOpen ? 120 : 0, opacity: sidebarOpen ? 1 : 0, transition:"max-width 0.2s, opacity 0.15s" }}>Dashboard</span>
            </button>

            <div>
              <button onClick={() => { if (effectiveAssets.length > 0) { navigateTo("portfolio"); persistAssetId(effectiveAssets[0].id); } setAssetsExpanded(e => !e); }} style={{
                width:"100%", background: activeAssetId ? "rgba(242,135,5,0.08)" : "transparent",
                border:"none", borderRadius:8, padding:"10px 12px",
                cursor:"pointer", display:"flex", alignItems:"center",
                gap:10, transition:"all 0.15s", marginBottom:2,
              }}>
                <i className="ph ph-chart-line" style={{ fontSize:18, flexShrink:0, width:18, color: activeAssetId ? C.orange : "var(--text3)" }}/>
                <span style={{ fontFamily:"Montserrat,sans-serif", fontWeight:400, color: activeAssetId ? C.orange : "var(--text3)", letterSpacing:"0.05em", fontSize:13, overflow:"hidden", whiteSpace:"nowrap", maxWidth: sidebarOpen ? 120 : 0, opacity: sidebarOpen ? 1 : 0, transition:"max-width 0.2s, opacity 0.15s", flex:1 }}>Portfolio</span>
                <span style={{ fontSize:8, color:"var(--text5)", transition:"transform 0.2s, max-width 0.2s, opacity 0.15s", transform: assetsExpanded ? "rotate(0deg)" : "rotate(-90deg)", display:"block", maxWidth: sidebarOpen ? 20 : 0, opacity: sidebarOpen ? 1 : 0, overflow:"hidden" }}>▼</span>
              </button>
              <div style={{
                maxHeight: assetsExpanded || !sidebarOpen ? `${effectiveAssets.length * 30}px` : "0px",
                overflow:"hidden", transition:"max-height 0.3s cubic-bezier(0.4,0,0.2,1)",
                paddingLeft: sidebarOpen ? 8 : 0,
              }}>
                {effectiveAssets.map(asset => {
                  const isActive = asset.id === activeAssetId;
                  const sPrice   = isActive && cp > 0 ? cp : (sidebarPrices[asset.id] || 0);
                  const fmtSidebarPrice = v => {
                    if (!v) return null;
                    if (v < 0.01)  return "$" + v.toFixed(4);
                    if (v < 1)     return "$" + v.toFixed(3);
                    if (v < 1000)  return "$" + v.toFixed(2);
                    if (v < 10000) return "$" + v.toLocaleString("en-US", { minimumFractionDigits:0, maximumFractionDigits:0 });
                    return "$" + (v/1000).toFixed(0) + "k";
                  };
                  return (
                    <div key={asset.id}>
                      <button onClick={() => { if (isDemo) { setChartReady(false); persistAssetId(asset.id); navigateTo("portfolio"); requestAnimationFrame(() => requestAnimationFrame(() => setChartReady(true))); } else { switchAsset(asset.id); } }} style={{
                        width:"100%", background: isActive ? "rgba(242,135,5,0.08)" : "transparent",
                        border:"none", borderRadius:8, padding:"4px 10px",
                        cursor:"pointer", display:"flex", alignItems:"center",
                        gap:6, transition:"all 0.15s",
                      }}>
                        <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color: isActive ? C.orange : "var(--text2)", letterSpacing:"0.05em", flexShrink:0 }}>{asset.ticker.slice(0,5)}</span>
                        <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text4)", overflow:"hidden", whiteSpace:"nowrap", maxWidth: sidebarOpen ? 60 : 0, opacity: sidebarOpen ? 1 : 0, transition:"max-width 0.2s, opacity 0.15s" }}>
                          {(() => {
                            const match = [...POPULAR_STOCKS, ...POPULAR_CRYPTO].find(p => p.ticker === asset.ticker);
                            const displayName = match?.name || (asset.name !== asset.ticker ? asset.name : null);
                            if (!displayName) return null;
                            return displayName.length > 10 ? displayName.slice(0,10) + "…" : displayName;
                          })()}
                        </span>
                        {fmtSidebarPrice(sPrice) && (
                          <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:600, color: isActive ? C.orange : "var(--text4)", whiteSpace:"nowrap", marginLeft:"auto", overflow:"hidden", maxWidth: sidebarOpen ? 50 : 0, opacity: sidebarOpen ? 1 : 0, transition:"max-width 0.2s, opacity 0.15s" }}>
                            {fmtSidebarPrice(sPrice)}
                          </span>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <button onClick={() => { navigateTo("watchlist"); persistAssetId(null); }} style={{
              width:"100%", background: activePage === "watchlist" ? "rgba(242,135,5,0.08)" : "transparent",
              border:"none", borderRadius:8, padding:"10px 12px",
              cursor:"pointer", display:"flex", alignItems:"center",
              gap:10, transition:"all 0.15s", marginBottom:2,
            }}>
              <i className="ph ph-eye" style={{ fontSize:18, flexShrink:0, width:18, color: activePage === "watchlist" ? C.orange : "var(--text3)" }}/>
              <span style={{ fontFamily:"Montserrat,sans-serif", fontWeight:400, color: activePage === "watchlist" ? C.orange : "var(--text3)", letterSpacing:"0.05em", fontSize:13, overflow:"hidden", whiteSpace:"nowrap", maxWidth: sidebarOpen ? 120 : 0, opacity: sidebarOpen ? 1 : 0, transition:"max-width 0.2s, opacity 0.15s" }}>Watchlist</span>
            </button>

            <button onClick={() => { navigateTo("markets"); persistAssetId(null); }} style={{
              width:"100%", background: activePage === "markets" ? "rgba(242,135,5,0.08)" : "transparent",
              border:"none", borderRadius:8, padding:"10px 12px",
              cursor:"pointer", display:"flex", alignItems:"center",
              gap:10, transition:"all 0.15s", marginBottom:2,
            }}>
              <i className="ph ph-chart-bar" style={{ fontSize:18, flexShrink:0, width:18, color: activePage === "markets" ? C.orange : "var(--text3)" }}/>
              <span style={{ fontFamily:"Montserrat,sans-serif", fontWeight:400, color: activePage === "markets" ? C.orange : "var(--text3)", letterSpacing:"0.05em", fontSize:13, overflow:"hidden", whiteSpace:"nowrap", maxWidth: sidebarOpen ? 120 : 0, opacity: sidebarOpen ? 1 : 0, transition:"max-width 0.2s, opacity 0.15s" }}>Markets</span>
            </button>

            <button onClick={() => { navigateTo("retirement"); persistAssetId(null); }} style={{
              width:"100%", background: activePage === "retirement" ? "rgba(242,135,5,0.08)" : "transparent",
              border:"none", borderRadius:8, padding:"10px 12px",
              cursor:"pointer", display:"flex", alignItems:"center",
              gap:10, transition:"all 0.15s", marginBottom:2,
            }}>
              <i className="ph ph-shooting-star" style={{ fontSize:18, flexShrink:0, width:18, color: activePage === "retirement" ? C.orange : "var(--text3)" }}/>
              <span style={{ fontFamily:"Montserrat,sans-serif", fontWeight:400, color: activePage === "retirement" ? C.orange : "var(--text3)", letterSpacing:"0.05em", fontSize:13, overflow:"hidden", whiteSpace:"nowrap", maxWidth: sidebarOpen ? 120 : 0, opacity: sidebarOpen ? 1 : 0, transition:"max-width 0.2s, opacity 0.15s" }}>Retirement</span>
            </button>

            <button onClick={() => { navigateTo("dividends"); persistAssetId(null); }} style={{
              width:"100%", background: activePage === "dividends" ? "rgba(242,135,5,0.08)" : "transparent",
              border:"none", borderRadius:8, padding:"10px 12px",
              cursor:"pointer", display:"flex", alignItems:"center",
              gap:10, transition:"all 0.15s", marginBottom:2,
            }}>
              <i className="ph ph-currency-circle-dollar" style={{ fontSize:18, flexShrink:0, width:18, color: activePage === "dividends" ? C.orange : "var(--text3)" }}/>
              <span style={{ fontFamily:"Montserrat,sans-serif", fontWeight:400, color: activePage === "dividends" ? C.orange : "var(--text3)", letterSpacing:"0.05em", fontSize:13, overflow:"hidden", whiteSpace:"nowrap", maxWidth: sidebarOpen ? 120 : 0, opacity: sidebarOpen ? 1 : 0, transition:"max-width 0.2s, opacity 0.15s" }}>Dividends</span>
            </button>
          </nav>

          <div style={{ marginTop:"auto", padding:"12px 8px", borderTop:"1px solid var(--border)", display:"flex", flexDirection:"column", gap:6 }}>
            {sidebarOpen && user && (
              <div style={{ marginBottom:4, padding:"8px 10px", background:"var(--bg3)", borderRadius:8, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:"var(--text4)", letterSpacing:"0.1em", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:120 }}>{user.email?.split("@")[0]}</div>
                <button onClick={() => supabase.auth.signOut()} style={{ background:"transparent", border:"1px solid var(--border)", color:"var(--text4)", borderRadius:5, padding:"3px 8px", cursor:"pointer", fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, letterSpacing:"0.1em", transition:"all 0.15s", flexShrink:0 }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = C.red; e.currentTarget.style.color = C.red; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.color = "var(--text4)"; }}>LOGOUT</button>
              </div>
            )}
            {(user?.email === "jeliodimitrov@gmail.com" || user?.user_metadata?.email === "jeliodimitrov@gmail.com") && (
              <button onClick={() => { navigateTo("admin"); persistAssetId(null); }} style={{
                width:"100%", background: activePage === "admin" ? "rgba(242,135,5,0.1)" : "transparent",
                border:"1px solid " + (activePage === "admin" ? C.orange : "var(--border)"),
                borderRadius:8, padding:"8px", cursor:"pointer",
                display:"flex", alignItems:"center", justifyContent:"center",
                gap: sidebarOpen ? 10 : 0, color: activePage === "admin" ? C.orange : "var(--text3)", transition:"all 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = C.orange}
              onMouseLeave={e => { if (activePage !== "admin") e.currentTarget.style.borderColor = "var(--border)"; }}>
                <i className="ph ph-gear" style={{ fontSize:16, flexShrink:0 }}/>
                {sidebarOpen && <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, letterSpacing:"0.1em", whiteSpace:"nowrap" }}>ADMIN</span>}
              </button>
            )}
            <button onClick={() => setTheme(t => t === "dark" ? "light" : "dark")} style={{
              width:"100%", background:"transparent", border:"1px solid var(--border)",
              borderRadius:8, padding:"8px", cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center",
              gap: sidebarOpen ? 10 : 0, color:"var(--text3)", transition:"all 0.15s",
            }}>
              <i className={"ph " + (theme === "dark" ? "ph-sun" : "ph-moon")} style={{ fontSize:16, flexShrink:0 }}/>
              {sidebarOpen && <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, letterSpacing:"0.1em", whiteSpace:"nowrap" }}>{theme === "dark" ? "LIGHT MODE" : "DARK MODE"}</span>}
            </button>
          </div>
        </div>

        {/* ── MAIN CONTENT ── */}
        <div className={isMobile ? "mobile-page" : ""} style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden", minHeight:"100vh" }}>

          <div style={{ flex:1, padding: isMobile ? "14px 12px 88px" : "36px 28px 60px", overflow:"auto", background:"var(--bg)" }}>

          {/* ── EMPTY STATE ── */}
          {assets.length === 0 && !loading && (
            <div style={{
              display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
              minHeight:"60vh", gap: isMobile ? 16 : 24, padding: isMobile ? "0 8px" : 0,
            }}>
              <div style={{
                background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:16,
                padding:"48px 40px", maxWidth:420, width:"100%", textAlign:"center",
                boxShadow:"0 4px 40px rgba(0,0,0,0.3)",
              }}>
                <div style={{ fontSize:48, marginBottom:20 }}>📈</div>
                <div style={{
                  fontFamily:"Montserrat,sans-serif", fontSize:18, fontWeight:900,
                  color:"var(--text)", letterSpacing:"0.02em", marginBottom:12,
                }}>
                  Start tracking your portfolio
                </div>
                <div style={{
                  fontFamily:"Montserrat,sans-serif", fontSize:13, color:"var(--text3)",
                  lineHeight:1.7, marginBottom:32,
                }}>
                  Add your first asset — a stock, crypto or anything you invest in — and start tracking your performance.
                </div>
                <button onClick={() => setShowTradeModal(true)} style={{
                  background:C.orange, color:"#000", border:"none", borderRadius:10,
                  padding:"14px 32px", fontFamily:"Montserrat,sans-serif",
                  fontSize:12, fontWeight:800, letterSpacing:"0.2em", cursor:"pointer",
                  width:"100%", transition:"opacity 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
                onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
                  + ADD FIRST ASSET
                </button>
              </div>
            </div>
          )}

          {/* DEMO BANNER */}
          {isDemo && (
            <div style={{
              margin:"0 0 16px 0",
              padding:"12px 18px",
              background:"rgba(242,135,5,0.08)",
              border:"1px solid rgba(242,135,5,0.3)",
              borderRadius:10,
              display:"flex", alignItems:"center", justifyContent:"space-between",
              flexWrap:"wrap", gap:10,
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <i className="ph ph-eye" style={{ fontSize:16, color:"#F28705" }}/>
                <div>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, fontWeight:800,
                    color:"#F28705", letterSpacing:"0.1em" }}>DEMO MODE</div>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, color:"#c0bdb0", marginTop:1 }}>
                    You're viewing demo data — Sign in to track your portfolio
                  </div>
                </div>
              </div>
              <button onClick={() => setForceLoginModal(true)}
                style={{
                  background:"#F28705", border:"none", borderRadius:8,
                  padding:"8px 16px", fontFamily:"Montserrat,sans-serif",
                  fontSize:9, fontWeight:800, letterSpacing:"0.15em",
                  color:"#000", cursor:"pointer",
                }}>
                SIGN IN
              </button>
            </div>
          )}

          {/* HERO IMAGE */}
          {false && activeAssetId && assets.length > 0 && (
            <div style={{
              width:"100%", height:220, borderRadius:12,
              overflow:"hidden", marginBottom:24,
              border:"1px solid var(--border)",
            }}>
              <img
                src="data:image/jpeg;base64,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"
                alt=""
                style={{
                  width:"100%", height:"100%",
                  objectFit:"contain", objectPosition:"left center",
                  display:"block",
                }}
              />
            </div>
          )}

          {/* HEADER */}
          <div style={{ ...st.header, display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div>
              {activePage === "watchlist" ? (
                <div style={st.tickerRow}>
                  <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:22, fontWeight:900, letterSpacing:"0.02em", color:"var(--text)" }}>
                    Watchlist
                  </span>
                </div>
              ) : isMobile ? (
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <button onClick={() => persistAssetId(null)}
                    style={{ background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:7,
                      width:34, height:34, display:"flex", alignItems:"center", justifyContent:"center",
                      cursor:"pointer", flexShrink:0, color:"var(--text3)" }}>
                    <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
                      <path d="M7.5 2L3.5 6L7.5 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <div>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:18, fontWeight:900, letterSpacing:"0.01em", color:"var(--text)", lineHeight:1 }}>
                      {activeAsset?.ticker || "—"}
                    </div>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, letterSpacing:"0.15em", color:"var(--text4)", marginTop:2 }}>
                      PORTFOLIO
                    </div>
                  </div>
                  {saving && <span style={st.saving}>{"saving…"}</span>}
                </div>
              ) : (
                <div style={st.tickerRow}>
                  <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:22, fontWeight:900, letterSpacing:"0.02em", color:"var(--text)" }}>
                    {activeAsset?.ticker || "—"}
                  </span>
                  <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:22, fontWeight:400, letterSpacing:"0.02em", color:"var(--text4)", marginLeft:4 }}>
                    {activePage === "retirement" ? "Retirement Plan" : activeAssetId ? "Portfolio" : "Dashboard"}
                  </span>
                  {saving && <span style={st.saving}>{"saving…"}</span>}
                </div>
              )}
            </div>
            <div style={{ display:"flex", gap: isMobile ? 6 : 10, alignItems:"center", flexWrap: isMobile ? "wrap" : "nowrap" }}>
              {/* ── SECTIONS button — context-aware ── */}
              {activePage !== "watchlist" && !activeAssetId ? (
                /* Dashboard sections */
                <div style={{ position:"relative" }} data-sections-menu="true">
                  <button
                    onClick={() => setShowDashMenu(m => !m)}
                    style={{
                      background: showDashMenu ? "rgba(242,135,5,0.08)" : "transparent",
                      border:`1px solid ${showDashMenu ? C.orange : "var(--border)"}`,
                      color: showDashMenu ? C.orange : "var(--text3)",
                      borderRadius:7, padding: isMobile ? "8px 10px" : "6px 10px",
                      fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700,
                      letterSpacing:"0.1em", cursor:"pointer", transition:"all 0.15s",
                      display:"flex", alignItems:"center", gap:6,
                      minHeight: isMobile ? 36 : "auto",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = C.orange; e.currentTarget.style.color = C.orange; }}
                    onMouseLeave={e => { if (!showDashMenu) { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.color = "var(--text3)"; } }}
                  >
                    <i className={showDashMenu ? "ph ph-eye" : "ph ph-eye-slash"} style={{ fontSize:15 }}/>
                    {!isMobile && <span>SECTIONS</span>}
                  </button>
                  {showDashMenu && (
                    <div style={{
                      position:"absolute", top:"calc(100% + 6px)", right:0, zIndex:400,
                      background:"var(--bg2)", border:`1px solid ${C.orange}`,
                      borderRadius:8, padding:"4px 0", minWidth:200,
                      boxShadow:"0 8px 24px rgba(0,0,0,0.5)",
                    }}>
                      <div style={{ padding:"10px 16px 8px", borderBottom:"1px solid var(--border)", marginBottom:4 }}>
                        <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, letterSpacing:"0.25em", color:"var(--text4)" }}>DASHBOARD SECTIONS</div>
                      </div>
                      {[
                        { id:"summary",       label:"Summary" },
                        { id:"best_worst",    label:"Best · Worst" },
                        { id:"performance",   label:"Performance · Allocation" },
                        { id:"trade_history", label:"Trade History" },
                        { id:"allocation_bar",label:"Allocation Bar" },
                        { id:"monthly_invested", label:"Monthly · Weekly Invested" },
                        { id:"trade_calendar",  label:"Trade Calendar" },
                        { id:"dca_efficiency",  label:"DCA Efficiency" },
                        { id:"bubble_chart",    label:"Bubble Chart" },
                        { id:"breakdown",     label:"Asset Breakdown" },
                      { id:"ranking",       label:"Asset Ranking" },
                      { id:"fear_greed",    label:"Fear & Greed Index" },
                      ].map(({ id, label }) => {
                        const visible = !dashHidden[id];
                        return (
                          <div key={id}
                            onClick={() => setDashHidden(h => ({ ...h, [id]: visible }))}
                            style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                              padding:"6px 14px", cursor:"pointer", transition:"background 0.1s" }}
                            onMouseEnter={e => e.currentTarget.style.background = "rgba(242,135,5,0.08)"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                          >
                            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                              <i className={visible ? "ph ph-eye" : "ph ph-eye-slash"}
                                style={{ fontSize:13, color: visible ? C.orange : "var(--text5)", flexShrink:0 }}/>
                              <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700,
                                letterSpacing:"0.05em", color: visible ? "var(--text2)" : "var(--text5)" }}>
                                {label}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                      <div style={{ borderTop:"1px solid var(--border)", margin:"3px 0 1px" }}/>
                      <div onClick={() => setDashHidden({})}
                        style={{ padding:"6px 14px", cursor:"pointer", fontFamily:"Montserrat,sans-serif",
                          fontSize:8, fontWeight:700, letterSpacing:"0.12em", color:C.orange, textAlign:"center",
                          display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(242,135,5,0.08)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        <i className="ph ph-eye" style={{ fontSize:13 }}/> SHOW ALL
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Asset page sections */
                <div style={{ position:"relative" }} data-sections-menu="true">
                  <button
                    onClick={() => setShowSectionsMenu(m => !m)}
                    style={{
                      background: showSectionsMenu ? "rgba(242,135,5,0.08)" : "transparent",
                      border:`1px solid ${showSectionsMenu ? C.orange : "var(--border)"}`,
                      color: showSectionsMenu ? C.orange : "var(--text3)",
                      borderRadius:7, padding: isMobile ? "8px 10px" : "6px 10px",
                      fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700,
                      letterSpacing:"0.1em", cursor:"pointer", transition:"all 0.15s",
                      display:"flex", alignItems:"center", gap:6,
                      minHeight: isMobile ? 36 : "auto",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = C.orange; e.currentTarget.style.color = C.orange; }}
                    onMouseLeave={e => { if (!showSectionsMenu) { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.color = "var(--text3)"; } }}
                  >
                    <i className={showSectionsMenu ? "ph ph-eye" : "ph ph-eye-slash"} style={{ fontSize:15 }}/>
                    {!isMobile && <span>SECTIONS</span>}
                  </button>
                  {showSectionsMenu && (
                    <div style={{
                      position:"absolute", top:"calc(100% + 6px)", right:0, zIndex:400,
                      background:"var(--bg2)", border:`1px solid ${C.orange}`,
                      borderRadius:8, padding:"4px 0", minWidth:220,
                      boxShadow:"0 8px 24px rgba(0,0,0,0.5)",
                    }}>
                      <div style={{ padding:"10px 16px 8px", borderBottom:"1px solid var(--border)", marginBottom:4 }}>
                        <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, letterSpacing:"0.25em", color:"var(--text4)" }}>ASSET SECTIONS</div>
                      </div>
                      {[
                        { id:"price_history",         label:"Price History" },
                        { id:"best_worst",            label:"Best · Worst Trade" },
                        { id:"trades_grid",           label:"Trades Grid" },
                        { id:"price_trade_chart",     label:"Price & Trade Chart" },
                        { id:"weekly_chart",          label:"Weekly Chart" },
                        { id:"pnl_shares",            label:`P&L · ${sharesLabel}` },
                        { id:"monthly_breakdown",     label:"Monthly Breakdown" },
                        { id:"target_avg",            label:"Target Price · Avg" },
                        { id:"shares_per_month_week", label:"Shares Lollipop" },
                        { id:"shares_cards",          label:"Shares Per Month/Week" },
                        { id:"underwater",            label:"Underwater Chart" },
                        { id:"buy_zone",              label:"Buy Zone · Price Ladder" },
                      ].map(({ id, label }) => {
                        const visible = !hiddenSections[id];
                        return (
                          <div key={id}
                            onClick={() => setHiddenSections(h => ({ ...h, [id]: visible }))}
                            style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                              padding:"6px 14px", cursor:"pointer", transition:"background 0.1s" }}
                            onMouseEnter={e => e.currentTarget.style.background = "rgba(242,135,5,0.08)"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                          >
                            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                              <i className={visible ? "ph ph-eye" : "ph ph-eye-slash"}
                                style={{ fontSize:13, color: visible ? C.orange : "var(--text5)", flexShrink:0 }}/>
                              <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700,
                                letterSpacing:"0.05em", color: visible ? "var(--text2)" : "var(--text5)" }}>
                                {label}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                      <div style={{ borderTop:"1px solid var(--border)", margin:"3px 0 1px" }}/>
                      <div onClick={() => setHiddenSections({})}
                        style={{ padding:"6px 14px", cursor:"pointer", fontFamily:"Montserrat,sans-serif",
                          fontSize:8, fontWeight:700, letterSpacing:"0.12em", color:C.orange, textAlign:"center",
                          display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(242,135,5,0.08)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        <i className="ph ph-eye" style={{ fontSize:13 }}/> SHOW ALL
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ADD TRADE with hover dropdown */}
              {activePage !== "watchlist" && <div style={{ position:"relative" }}
                onMouseEnter={e => { if (!isDemo) e.currentTarget.querySelector(".add-trade-menu").style.display = "block"; }}
                onMouseLeave={e => { if (!isDemo) e.currentTarget.querySelector(".add-trade-menu").style.display = "none"; }}
                onClick={() => { if (isDemo) setForceLoginModal(true); }}
              >
                <button
                  style={{
                    background:`rgba(242,135,5,0.12)`, border:`1px solid ${C.orange}`,
                    color:C.orange, borderRadius:7, padding:"6px 10px",
                    fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700,
                    letterSpacing:"0.1em", cursor:"pointer", transition:"all 0.15s",
                    display:"flex", alignItems:"center", gap:5,
                  }}
                >
                  <span style={{ fontSize:12, fontWeight:900, lineHeight:1 }}>+</span>
                  <span style={{ display: isMobile ? "none" : "inline" }}>ADD TRADE</span>
                </button>

                {/* Hover dropdown — paddingTop bridges the gap */}
                <div className="add-trade-menu" style={{
                  display:"none", position:"absolute", top:"100%", right:0,
                  paddingTop:8, zIndex:300,
                }}>
                  <div style={{
                    background:"var(--bg2)", border:`1px solid ${C.orange}`,
                    borderRadius:8, overflow:"hidden", minWidth:200,
                    boxShadow:"0 8px 24px rgba(0,0,0,0.4)",
                  }}>
                  {/* Add current asset trade */}
                  <div
                    onClick={() => {
                      const isCrypto = POPULAR_CRYPTO.some(c => c.ticker === activeAsset?.ticker || c.yahooTicker === activeAsset?.ticker);
                      setShowTradeModal(true);
                      setTradeTickerInput(activeAsset?.ticker || "");
                      setTradeType(isCrypto ? "crypto" : "stock");
                    }}
                    style={{ padding:"12px 16px", cursor:"pointer", borderBottom:`1px solid var(--border)`,
                      transition:"background 0.1s", display:"flex", alignItems:"center", gap:10 }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(242,135,5,0.08)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:13, fontWeight:900, color:C.orange }}>+</span>
                    <div>
                      <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:"var(--text)" }}>
                        Add {activeAsset?.ticker} trade
                      </div>
                      <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text4)", marginTop:2 }}>
                        Add to current asset
                      </div>
                    </div>
                  </div>

                  {/* Add other trade */}
                  <div
                    onClick={() => {
                      setShowTradeModal(true);
                      setTradeTickerInput("");
                      setTradeType(null);
                    }}
                    style={{ padding:"12px 16px", cursor:"pointer",
                      transition:"background 0.1s", display:"flex", alignItems:"center", gap:10 }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(242,135,5,0.08)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:13, fontWeight:700, color:"var(--text3)" }}>+</span>
                    <div>
                      <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:"var(--text)" }}>
                        Add other trade
                      </div>
                      <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text4)", marginTop:2 }}>
                        Choose a different asset
                      </div>
                    </div>
                  </div>
                  </div>
                </div>
              </div>}
              {activePage !== "watchlist" && !isDemo && <CSVImportCompact onImport={importEntries} ticker={activeAsset?.ticker || ""} />}
              {effectiveAssets.length > 0 && activeAssetId && (
                <button
                  onClick={() => isDemo ? setForceLoginModal(true) : setShowDeleteModal(true)}
                  style={{
                    background:"transparent", border:`1px solid rgba(224,62,26,0.4)`,
                    color:"rgba(224,62,26,0.7)", borderRadius:7, padding:"6px 10px",
                    fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700,
                    letterSpacing:"0.1em", cursor:"pointer", transition:"all 0.15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = "rgba(224,62,26,0.1)"; e.currentTarget.style.borderColor = C.red; e.currentTarget.style.color = C.red; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "rgba(224,62,26,0.4)"; e.currentTarget.style.color = "rgba(224,62,26,0.7)"; }}
                >
                  DELETE
                </button>
              )}
            </div>
          </div>

        {/* ── DASHBOARD PAGE ── */}
        {effectiveAssets.length > 0 && activeAssetId && activePage === "portfolio" && <>
        {/* Floating drag clone for sections */}
        {sectionDragState && (
          <div style={{
            position:"fixed", left: sectionDragState.x, top: sectionDragState.y,
            width: sectionDragState.width, zIndex:9999, pointerEvents:"none",
            opacity:0.85, transform:"scale(1.02)",
            boxShadow:`0 16px 48px rgba(0,0,0,0.5), 0 0 0 2px ${C.orange}`,
            borderRadius:12, background:"var(--bg2)", border:"1px solid var(--border)",
            padding:"12px 20px", display:"flex", alignItems:"center", gap:12,
          }}>
            <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
              {[0,1,2].map(i => <div key={i} style={{ width:14, height:1.5, borderRadius:1, background:C.orange, opacity:0.8 }}/>)}
            </div>
            <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, letterSpacing:"0.3em", color:C.orange }}>
              {sectionDragState.id.replace(/_/g," ").toUpperCase()}
            </div>
          </div>
        )}
        <div style={{...st.topRow, cursor: sectionDragState ? "grabbing" : "default", userSelect: sectionDragState ? "none" : "auto", flexDirection: isMobile ? "column" : "row", minHeight: isMobile ? "auto" : 220, gap: isMobile ? 10 : 16, marginBottom: isMobile ? 14 : 24}}>
          <div style={{...st.priceBlock, minWidth: isMobile ? "auto" : 230, width: isMobile ? "100%" : "auto", flexDirection: isMobile ? "row" : "column", flexWrap: isMobile ? "wrap" : "nowrap", gap: isMobile ? 10 : 0, padding: isMobile ? "14px 16px" : "22px 26px"}}>
            <div style={st.priceLabel}>{"LIVE PRICE"}</div>

            {/* Price input */}
            <div style={{ display:"flex", alignItems:"center", gap:6, background:"var(--bg4)",
              border:`1px solid ${priceLoading ? "var(--border)" : isManual ? C.orange : C.green}`,
              borderRadius:7, padding:"8px 12px", marginBottom:6 }}>
              <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:16, fontWeight:700,
                color: priceLoading ? "var(--text5)" : isManual ? C.orange : C.green }}>$</span>
              {priceLoading ? (
                <div style={{
                  width:100, height:28, borderRadius:5,
                  background:"var(--border)",
                  animation:"pulse 1.2s ease-in-out infinite",
                }}/>
              ) : isManual ? (
                <input
                  type="number"
                  step="1"
                  placeholder="0.00"
                  value={manualPrice}
                  onFocus={() => setIsManual(true)}
                  onChange={e => {
                    setManualPrice(e.target.value);
                    if (activeAssetId) localStorage.setItem(`manual_price_${activeAssetId}`, e.target.value);
                    setIsManual(true);
                  }}
                  style={{ background:"transparent", border:"none", outline:"none", color:"var(--text)", fontFamily:"Montserrat,sans-serif", fontSize:26, fontWeight:900, width:130, colorScheme:"dark" }}
                />
              ) : (
                <div
                  onClick={() => setIsManual(true)}
                  style={{ cursor:"text", fontFamily:"Montserrat,sans-serif", fontSize:26, fontWeight:900, color:"var(--text)", minWidth:100 }}
                >
                  <AnimatedNumber value={parseFloat(manualPrice)||0} format={v => `${v.toFixed(2)}`} duration={600} />
                </div>
              )}
            </div>

            {/* Status indicator */}
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
              <div style={{ width:6, height:6, borderRadius:"50%",
                background: priceLoading ? "var(--text5)" : isManual ? C.orange : C.green,
                boxShadow: !priceLoading && !isManual ? `0 0 6px ${C.green}` : "none",
                animation: priceLoading ? "pulse 1.2s ease-in-out infinite" : !isManual ? "pulse 2s ease-in-out infinite" : "none" }}/>
              <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700,
                color: priceLoading ? "var(--text5)" : isManual ? C.orange : C.green, letterSpacing:"0.15em" }}>
                {priceLoading ? "LOADING..." : isManual ? "MANUAL" : "LIVE"}
              </span>
              {!priceLoading && isManual && (
                <button onClick={() => { setIsManual(false); fetchLivePrice(); }} style={{
                  background:"transparent", border:`1px solid ${C.green}`, color:C.green,
                  borderRadius:5, padding:"2px 8px", cursor:"pointer",
                  fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, letterSpacing:"0.1em",
                }}>↺ LIVE</button>
              )}
            </div>

            {entries.length > 0 && (
              <div style={st.beBox}>
                <div style={st.beLabel}>BREAK-EVEN</div>
                <div style={st.beVal}>{fmt$(avgPrice)}</div>
                {distBE !== null && (
                  <div style={{ fontSize:11, fontWeight:700, marginTop:4, fontFamily:"Montserrat,sans-serif", color:pnlCol(distBE) }}>
                    {distBE >= 0 ? `+${distBE.toFixed(1)}% above B/E` : `${distBE.toFixed(1)}% to B/E`}
                  </div>
                )}
              </div>
            )}


          </div>

          <ChartContainer label={activeAsset?.ticker ? `${activeAsset.ticker} PORTFOLIO VALUE` : "PORTFOLIO VALUE"}>
            {(width) => chartReady && width > 0 && <PortfolioChart key={`${activeAssetId}-${width}`} entries={effectiveEntries} currentPrice={cp} containerWidth={width}/>}
          </ChartContainer>

        </div>

        {/* ATH */}

        {/* PERIOD FILTER + CARDS */}
        {entries.length > 0 && (
          <>
          {/* Period buttons */}
          <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
            {["1M","3M","6M","1Y","ALL"].map(p => (
              <button key={p} onClick={() => setPeriod(p)} style={{
                padding:"6px 16px", borderRadius:6, cursor:"pointer",
                fontFamily:"Montserrat,sans-serif", fontSize:10, fontWeight:700, letterSpacing:"0.1em",
                background: period===p ? C.orange : "transparent",
                color:      period===p ? "#000"   : "var(--text3)",
                border:     period===p ? `1px solid ${C.orange}` : "1px solid var(--border)",
                transition:"all 0.15s",
              }}>{p}</button>
            ))}
            {period !== "ALL" && (
              <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, color:"var(--text4)",
                alignSelf:"center", marginLeft:4 }}>
                {filteredEntries.length} of {effectiveEntries.length} trades
              </span>
            )}
          </div>
          <div style={{...st.cards, display: isMobile ? "grid" : "flex", gridTemplateColumns: isMobile ? "1fr 1fr" : undefined}}>
            <div style={st.card}>
              <div style={st.clabel}>Invested</div>
              <div style={{...st.cval, fontSize:responsiveFontSize(fmt$(totalDollars), 15)}}><AnimatedNumber value={totalDollars} format={v => fmt$(v)} /></div>
            </div>
            <div style={st.card}>
              <div style={st.clabel}>{sharesLabel}</div>
              <div style={{...st.cval, fontSize:responsiveFontSize(totalShares.toFixed(4), 15)}}><AnimatedNumber value={totalShares} format={v => v.toFixed(4)} /></div>
            </div>
            <div style={st.card}>
              <div style={st.clabel}>Avg Price</div>
              <div style={{...st.cval, color:C.orange, fontSize:responsiveFontSize(fmt$(avgPrice), 15)}}><AnimatedNumber value={avgPrice} format={v => fmt$(v)} /></div>
            </div>
            {hasCp && (
              <div style={st.card}>
                <div style={st.clabel}>Portfolio Value</div>
                <div style={{...st.cval, fontSize:responsiveFontSize(fmt$(portfolioValue), 15)}}><AnimatedNumber value={portfolioValue} format={v => fmt$(v)} /></div>
              </div>
            )}
            {hasCp && (
              <div style={{...st.card, borderColor:portfolioPnl>=0?"rgba(90,173,39,0.5)":"rgba(242,68,5,0.5)", background:portfolioPnl>=0?"rgba(90,173,39,0.06)":"rgba(224,62,26,0.06)"}}>
                <div style={st.clabel}>Total P&L</div>
                <div style={{...st.cval, color:pnlCol(portfolioPnl), fontSize:responsiveFontSize((portfolioPnl>=0?"+":"")+fmt$(portfolioPnl), 15)}}>
                  <AnimatedNumber value={portfolioPnl} format={v => (v>=0?"+":"")+fmt$(v)} />
                </div>
                <div style={{fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:pnlCol(portfolioPct), marginTop:3}}>
                  <AnimatedNumber value={portfolioPct} format={v => (v>=0?"+":"")+v.toFixed(2)+"%"} />
                </div>
              </div>
            )}
          </div>
          </>
        )}

        {/* DRAGGABLE DASHBOARD SECTIONS */}
        {(() => {
          const allSections = {
            price_history: (
              <Collapsible id="price_history" title="PRICE HISTORY" noOverflow={true}>
                <PriceHistoryChart
                  ticker={activeAsset?.yahooTicker || activeAsset?.ticker || ""}
                  currentPrice={cp}
                  isMobile={isMobile}
                />
              </Collapsible>
            ),

            best_worst: hasCp && effectiveEntries.length > 1 ? (
              <Collapsible id="best_worst" title="BEST · WORST TRADE">
                <BestWorstCard best={bestEntry} worst={worstEntry} winCount={winCount} lossCount={lossCount} totalCount={entriesWithPnl.length} />
              </Collapsible>
            ) : null,

            trades_grid: effectiveEntries.length > 0 && hasCp ? (
              <Collapsible id="trades_grid" title="TRADES GRID">
                <div style={{ display:"flex", gap:16, alignItems:"flex-start" }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <TradesGrid entries={filteredEntries} currentPrice={cp} />
                  </div>
                  <ProfitDonut entries={filteredEntries} currentPrice={cp} />
                </div>
              </Collapsible>
            ) : null,

            price_trade_chart: effectiveEntries.length > 0 && hasCp ? (
              <Collapsible id="price_trade_chart" title="PRICE · TRADES">
                <div style={{ height:260 }}>
                  <PriceTradeChart entries={effectiveEntries} currentPrice={cp}/>
                </div>
              </Collapsible>
            ) : null,

            trade_calendar: effectiveEntries.length > 0 ? (
              <Collapsible id="trade_calendar" title="TRADE CALENDAR">
                <TradeCalendar entries={effectiveEntries} currentPrice={cp}/>
              </Collapsible>
            ) : null,

            weekly_chart: effectiveEntries.length > 0 ? (
              <Collapsible id="weekly_chart" title="INVESTED BY WEEK · BY MONTH">
                <div style={{ display:"flex", gap:16 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, letterSpacing:"0.25em", color:"var(--text4)", marginBottom:10 }}>BY WEEK</div>
                    <MonthlyChart data={weeklyArr} />
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, letterSpacing:"0.25em", color:"var(--text4)", marginBottom:10 }}>BY MONTH</div>
                    <MonthlyChart data={monthlyArr} />
                  </div>
                </div>
              </Collapsible>
            ) : null,

            pnl_shares: effectiveEntries.length > 0 && hasCp ? (              <Collapsible id="pnl_shares" title={`CUMULATIVE P&L · ${SHARES_LABEL} ACCUMULATED`}>
                <div style={{ display:"flex", gap:16 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, letterSpacing:"0.25em", color:"var(--text4)", marginBottom:10 }}>CUMULATIVE P&L</div>
                    <div style={{ height:180 }}><PnLChart entries={effectiveEntries} currentPrice={cp} /></div>
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, letterSpacing:"0.25em", color:"var(--text4)", marginBottom:10 }}>SHARES ACCUMULATED</div>
                    <SharesAccumulatedChart entries={filteredEntries} />
                  </div>
                </div>
              </Collapsible>
            ) : null,

            monthly_breakdown: effectiveEntries.length > 0 ? (
              <Collapsible id="monthly_breakdown" title="MONTHLY BREAKDOWN">
                <MonthlyBarsChart data={monthlyArr} />
              </Collapsible>
            ) : null,

            target_avg: effectiveEntries.length > 0 ? (
              <Collapsible id="target_avg" title="TARGET PRICE · AVG CALCULATOR">
                <div style={{ display:"flex", gap:24 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, letterSpacing:"0.25em", color:"var(--text4)", marginBottom:12 }}>TARGET PRICE</div>
                    <TargetPrice totalShares={totalShares} totalDollars={totalDollars} currentPrice={cp} />
                  </div>
                  <div style={{ width:1, background:"var(--border)", flexShrink:0 }}/>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, letterSpacing:"0.25em", color:"var(--text4)", marginBottom:12 }}>AVG CALCULATOR</div>
                    <AvgCalcCompact currentPrice={cp} totalShares={totalShares} totalDollars={totalDollars} avgPrice={avgPrice} />
                  </div>
                </div>
              </Collapsible>
            ) : null,

            shares_per_month_week: effectiveEntries.length > 0 ? (
              <Collapsible id="shares_per_month_week" title="SHARES · LOLLIPOP">
                <MonthlyWeeklyLollipop monthlyData={monthlyArr} weeklyData={weeklyArr} />
              </Collapsible>
            ) : null,

            shares_cards: effectiveEntries.length > 0 ? (
              <Collapsible id="shares_cards" title="SHARES PER MONTH · PER WEEK">
                <SharesPerPeriod monthlyData={monthlyArr} weeklyData={weeklyArr} isMobile={isMobile} />
              </Collapsible>
            ) : null,

            underwater: effectiveEntries.length > 0 && hasCp ? (
              <Collapsible id="underwater" title="UNDERWATER CHART">
                <UnderwaterChart entries={filteredEntries} currentPrice={cp} />
              </Collapsible>
            ) : null,

            buy_zone: effectiveEntries.length > 0 && hasCp ? (
              <Collapsible id="buy_zone" title="BUY ZONE · PRICE LADDER">
                <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:16, alignItems:"start" }}>
                  <BuyZoneHeatmap entries={filteredEntries} currentPrice={cp} />
                  <PriceLadder entries={filteredEntries} currentPrice={cp} avgPrice={avgPrice} />
                </div>
              </Collapsible>
            ) : null,
          };

          return sectionOrder.map(id => {
            if (hiddenSections[id]) return null;
            const section = chartReady ? allSections[id] : null;
            if (!section) return chartReady ? null : (
              <div key={id} style={{ height:60, background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:10, marginBottom:16, animation:"pulse 1.2s ease-in-out infinite" }}/>
            );
            const isBeingDragged = isDragging === id;
            const isOver = dragOverId === id && !isBeingDragged;
            return (
              <div key={id} style={{
                opacity: isBeingDragged ? 0.3 : 1,
                boxShadow: isOver ? `0 0 0 2px ${C.orange}, 0 8px 32px rgba(242,135,5,0.12)` : "none",
                borderRadius: 12,
                transition: "opacity 0.15s, box-shadow 0.15s",
              }}>
                <CollapsibleDraggable
                  id={id}
                  section={section}
                  onMouseDragStart={onSectionDragStart}
                />
              </div>
            );
          });
        })()}
        </>}

        {/* ── PURCHASES PAGE ── */}
        {/* ── TRADE MODAL ── */}
        {showTradeModal && (() => {
          const tickerUp = tradeTickerInput.trim().toUpperCase();
          const matchedAsset = assets.find(a => a.ticker.toUpperCase() === tickerUp);
          const isNewAsset = tickerUp.length > 0 && !matchedAsset;
          const isKnownTicker = POPULAR_STOCKS.some(s => s.ticker === tickerUp) || POPULAR_CRYPTO.some(c => c.ticker === tickerUp);
          const isUnknownTicker = isNewAsset && tickerUp.length > 0 && !isKnownTicker;
          const canSubmit = form.date && form.dollars && form.shares && tickerUp.length > 0 && (!isUnknownTicker || form.manualPrice);

          const handleSubmit = async () => {
            let targetAssetId = matchedAsset?.id;

            // Determine yahoo ticker for crypto — must be before isNewAsset block
            const cryptoMatch = POPULAR_CRYPTO.find(c => c.ticker === tickerUp);
            const yahooTicker = tradeType === "crypto"
              ? (cryptoMatch?.yahooTicker || `${tickerUp}-USD`)
              : tickerUp;

            // Create new asset if needed
            if (isNewAsset) {
              // Check plan limits
              const limit = isFreeMode ? Infinity : userPlan === "unlimited" ? Infinity : userPlan === "pro" ? 10 : 1;
              if (assets.length >= limit) {
                setShowTradeModal(false);
                setShowPaywall(true);
                return;
              }
              const newId = `asset_${tickerUp.toLowerCase()}_${Date.now()}`;
              const newAsset = { id: newId, ticker: tickerUp, name: cryptoMatch?.name || tickerUp, yahooTicker };
              const updated = [...assets, newAsset];
              setAssets(updated);
              targetAssetId = newId;
              // Save manual price if provided
              if (isUnknownTicker && form.manualPrice) {
                localStorage.setItem(`manual_price_${newId}`, form.manualPrice);
              }
            }

            // Save entry to Supabase
            const { data, error } = await supabase.from("entries").insert({
              user_id: user.id,
              asset_id: targetAssetId,
              asset_type: tradeType,
              yahoo_ticker: yahooTicker,
              date: form.date,
              dollars: parseFloat(form.dollars),
              shares: parseFloat(form.shares),
            }).select().single();

            if (!error && data) {
              if (targetAssetId === activeAssetId) {
                const updated = [...entries, { id:data.id, date:data.date, dollars:data.dollars, shares:data.shares }]
                  .sort((a,b) => a.date.localeCompare(b.date));
                setEntries(updated);
              } else if (isNewAsset) {
                // New asset — navigate to it after state has settled
                setEntries([{ id:data.id, date:data.date, dollars:data.dollars, shares:data.shares }]);
                persistAssetId(targetAssetId);
                navigateTo("portfolio");
                setLoading(false);
                setPriceLoading(false);
                // Load manual price if unknown ticker
                if (isUnknownTicker && form.manualPrice) {
                  setManualPrice(form.manualPrice);
                  setIsManual(true);
                } else {
                  setPriceLoading(true);
                  setIsManual(false);
                }
                priceInitialized.current = false;
                setPeriod("ALL");
                setChartReady(false);
                requestAnimationFrame(() => requestAnimationFrame(() => setChartReady(true)));
              } else {
                // Switch to existing asset we added to
                switchAsset(targetAssetId);
              }
            }
            setShowTradeModal(false);
            setForm(emptyForm);
            setTradeTickerInput("");
          };

          return (
            <div
              style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.82)", zIndex:500,
                display:"flex", alignItems: isMobile ? "flex-end" : "flex-start", justifyContent:"center",
                padding: isMobile ? 0 : "5vh 20px 20px", overflowY: isMobile ? "hidden" : "auto" }}>
              <div style={{ background:"#111", border:"1px solid #2a2a2a",
                borderRadius: isMobile ? "18px 18px 0 0" : 20,
                padding: isMobile ? "24px 20px 32px" : "36px 36px 32px",
                width:"100%", maxWidth: isMobile ? "100%" : 500, position:"relative",
                boxShadow:"0 40px 100px rgba(0,0,0,0.8)", flexShrink:0,
                maxHeight: isMobile ? "92vh" : "none", overflowY: isMobile ? "auto" : "visible" }}>

                {/* Header */}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:32 }}>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:15, fontWeight:800,
                    letterSpacing:"0.2em", color:"#fff" }}>+ NEW TRADE</div>
                  <button onClick={() => { setShowTradeModal(false); setForm(emptyForm); setTradeTickerInput(""); setTradeType("stock"); }}
                    style={{ background:"transparent", border:"1px solid #2a2a2a", color:"#555",
                      borderRadius:8, width:34, height:34, cursor:"pointer", fontSize:16,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      transition:"all 0.15s" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor="#555"; e.currentTarget.style.color="#aaa"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor="#2a2a2a"; e.currentTarget.style.color="#555"; }}>✕</button>
                </div>

                {/* Type selector */}
                <div style={{ marginBottom: tradeType ? 28 : 0 }}>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:800,
                    letterSpacing:"0.25em", color:"#444", marginBottom:14, textAlign:"center" }}>
                    ASSET TYPE
                  </div>
                  <div style={{ display:"flex", gap:10 }}>
                    {[{id:"stock", label:"STOCK", desc:"Stocks & ETFs"}, {id:"crypto", label:"CRYPTO", desc:"Tokens & coins"}].map(({id, label, desc}) => (
                      <button key={id}
                        onClick={() => { setTradeType(id); setTradeTickerInput(""); setShowTickerDropdown(false); setLiveSearchResults([]); }}
                        onMouseEnter={e => {
                          if (tradeType !== id) {
                            e.currentTarget.querySelector(".type-label").style.color = C.orange;
                            e.currentTarget.querySelector(".type-desc").style.color = "rgba(242,135,5,0.5)";
                          }
                        }}
                        onMouseLeave={e => {
                          if (tradeType !== id) {
                            e.currentTarget.querySelector(".type-label").style.color = "#fff";
                            e.currentTarget.querySelector(".type-desc").style.color = "#555";
                          }
                        }}
                        style={{
                          flex:1, padding:"28px 12px", borderRadius:12, cursor:"pointer",
                          border:`2px solid ${tradeType === id ? C.orange : "#222"}`,
                          background: tradeType === id ? `rgba(242,135,5,0.07)` : "#0d0d0d",
                          display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:6,
                          transition:"all 0.2s",
                        }}>
                        <span className="type-label" style={{ fontFamily:"Montserrat,sans-serif", fontSize:28, fontWeight:900,
                          color: tradeType === id ? C.orange : "#fff", letterSpacing:"0.04em",
                          lineHeight:1, transition:"color 0.2s" }}>{label}</span>
                        <span className="type-desc" style={{ fontFamily:"Montserrat,sans-serif", fontSize:10,
                          color: tradeType === id ? "rgba(242,135,5,0.5)" : "#555", letterSpacing:"0.1em",
                          transition:"color 0.2s" }}>{desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Animated fields — show only after type selected */}
                <div style={{
                  overflow:"hidden",
                  maxHeight: tradeType ? "600px" : "0px",
                  opacity: tradeType ? 1 : 0,
                  transition:"max-height 0.4s cubic-bezier(0.4,0,0.2,1), opacity 0.3s ease",
                }}>
                  <div style={{ paddingTop:4 }}>
                    {/* Ticker input with autocomplete */}
                    <div style={{ marginBottom:16, position:"relative" }}>
                      <label style={st.flabel}>{tradeType === "crypto" ? "TOKEN" : "TICKER"}</label>
                      <input
                        type="text"
                        placeholder={tradeType === "crypto" ? "BTC, ETH, SOL..." : "MSTR, AAPL, TSLA..."}
                        value={tradeTickerInput}
                        onChange={e => {
                          const val = e.target.value.toUpperCase();
                          setTradeTickerInput(val);
                          setShowTickerDropdown(true);

                          // Live search with debounce
                          clearTimeout(searchDebounceRef.current);
                          if (val.length >= 1) {
                            setLiveSearchLoading(true);
                            searchDebounceRef.current = setTimeout(async () => {
                              try {
                                if (tradeType === "crypto") {
                                  // CoinGecko search
                                  const res = await fetch(`https://api.coingecko.com/api/v3/search?query=${encodeURIComponent(val)}`);
                                  if (res.ok) {
                                    const data = await res.json();
                                    const results = (data.coins || []).slice(0, 8).map(c => ({
                                      ticker: c.symbol.toUpperCase(),
                                      name: c.name,
                                      exchange: "CRYPTO",
                                    }));
                                    setLiveSearchResults(results);
                                  }
                                } else {
                                  // Yahoo Finance search via allorigins
                                  const url = `https://query1.finance.yahoo.com/v1/finance/search?q=${encodeURIComponent(val)}&quotesCount=8&newsCount=0`;
                                  const res = await fetch(`https://api.allorigins.win/get?url=${encodeURIComponent(url)}`);
                                  if (res.ok) {
                                    const outer = await res.json();
                                    const data = JSON.parse(outer.contents);
                                    const results = (data.quotes || [])
                                      .filter(q => q.quoteType === "EQUITY" || q.quoteType === "ETF")
                                      .slice(0, 8)
                                      .map(q => ({
                                        ticker: q.symbol,
                                        name: q.shortname || q.longname || q.symbol,
                                        exchange: q.exchange,
                                      }));
                                    setLiveSearchResults(results);
                                  }
                                }
                              } catch {}
                              setLiveSearchLoading(false);
                            }, 350);
                          } else {
                            setLiveSearchResults([]);
                            setLiveSearchLoading(false);
                          }
                        }}
                        onFocus={e => { iF(e); setShowTickerDropdown(true); }}
                        onBlur={e => { iB(e); setTimeout(() => { setShowTickerDropdown(false); setLiveSearchResults([]); }, 150); }}
                        autoComplete="off"
                        style={{ ...st.input, width:"100%", fontSize:18, fontWeight:900,
                          color: matchedAsset ? C.green : isNewAsset ? C.orange : "var(--text)",
                          letterSpacing:"0.05em" }}
                      />

                      {/* Dropdown suggestions */}
                      {showTickerDropdown && tradeTickerInput.length > 0 && (() => {
                        const q = tradeTickerInput.toUpperCase();

                        // Static fallback suggestions
                        const list = tradeType === "crypto" ? POPULAR_CRYPTO : POPULAR_STOCKS;
                        const staticSuggestions = list.filter(a =>
                          a.ticker.startsWith(q) || a.name.toUpperCase().includes(q)
                        ).slice(0, 4);

                        // Merge live + static, dedupe by ticker
                        const liveTickers = new Set(liveSearchResults.map(r => r.ticker));
                        const combined = [
                          ...liveSearchResults,
                          ...staticSuggestions.filter(s => !liveTickers.has(s.ticker))
                        ].slice(0, 8);

                        if (combined.length === 0 && !liveSearchLoading) return null;
                        return (
                          <div style={{
                            position:"absolute", top:"calc(100% + 6px)", left:0, right:0, zIndex:200,
                            background:"#111", border:"1px solid #2a2a2a",
                            borderRadius:14, overflow:"hidden",
                            boxShadow:"0 16px 48px rgba(0,0,0,0.7)",
                            animation:"fadeUp 0.15s ease",
                          }}>
                            {liveSearchLoading && combined.length === 0 && (
                              <div style={{ padding:"16px 18px", fontFamily:"Montserrat,sans-serif", fontSize:10, color:"#555", letterSpacing:"0.12em" }}>
                                SEARCHING...
                              </div>
                            )}
                            {combined.map((s, idx) => (
                              <div key={s.ticker}
                                onMouseDown={e => { e.preventDefault(); setTradeTickerInput(s.ticker); setShowTickerDropdown(false); setLiveSearchResults([]); }}
                                style={{ padding:"13px 18px", cursor:"pointer",
                                  display:"flex", justifyContent:"space-between", alignItems:"center",
                                  borderBottom: idx < combined.length - 1 ? "1px solid #1a1a1a" : "none",
                                  transition:"background 0.12s, border-left 0.12s",
                                  borderLeft:"3px solid transparent",
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = "rgba(242,135,5,0.06)"; e.currentTarget.style.borderLeftColor = C.orange; }}
                                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderLeftColor = "transparent"; }}
                              >
                                <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                                  <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:14, fontWeight:900, color:C.orange, letterSpacing:"0.04em" }}>{s.ticker}</span>
                                  <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, color:"#666", maxWidth:260, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.name}</span>
                                </div>
                                {s.exchange && (
                                  <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, letterSpacing:"0.1em", color:"#333", background:"#1a1a1a", borderRadius:5, padding:"3px 8px", flexShrink:0 }}>{s.exchange}</span>
                                )}
                              </div>
                            ))}
                            {combined.length === 0 && !liveSearchLoading && (
                              <div style={{ padding:"16px 18px", fontFamily:"Montserrat,sans-serif", fontSize:11, color:"#444" }}>
                                No results — you can still type and add manually
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {matchedAsset && (
                        <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, color:C.green, marginTop:6, display:"flex", alignItems:"center", gap:6 }}>
                          <span>✓</span> Will be added to <strong>{matchedAsset.ticker}</strong>
                        </div>
                      )}
                      {isNewAsset && tickerUp.length >= 2 && (
                        <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, color:C.orange, marginTop:6, display:"flex", alignItems:"center", gap:6 }}>
                          <span>+</span> <strong>{tickerUp}</strong> will be added as a new asset
                        </div>
                      )}
                    </div>

                    {/* Fields */}
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
                      <div style={{ ...st.field, gridColumn:"1 / -1" }}>
                        <label style={st.flabel}>DATE</label>
                        <CustomDatePicker value={form.date} onChange={v => setForm({...form, date:v})} />
                      </div>
                      {[
                        {label:"Invested ($)", key:"dollars", type:"number", placeholder:"0.00"},
                        {label: isCryptoAsset ? "Tokens" : "Shares", key:"shares", type:"number", placeholder:"0.0000"},
                      ].map(({label,key,type,placeholder}) => (
                        <div key={key} style={st.field}>
                          <label style={st.flabel}>{label}</label>
                          <input type={type} placeholder={placeholder} value={form[key]}
                            onChange={e => setForm({...form, [key]:e.target.value})}
                            onFocus={iF} onBlur={iB} style={st.input} step={key==="shares"?"0.0001":"0.01"}/>
                        </div>
                      ))}
                      <div style={st.field}>
                        <label style={st.flabel}>{tradeType === "crypto" ? "Price/token" : "Price/share"}</label>
                        <div style={st.calcVal}>
                          {form.dollars && form.shares && parseFloat(form.shares) > 0
                            ? fmt$(parseFloat(form.dollars)/parseFloat(form.shares))
                            : <span style={{color:"var(--text4)"}}>— auto</span>}
                        </div>
                      </div>

                      {/* Manual price field — only for unknown tickers */}
                      {isUnknownTicker && (
                        <div style={{ ...st.field, gridColumn:"1 / -1" }}>
                          <label style={{ ...st.flabel, color: C.orange }}>
                            ⚠ CURRENT PRICE (manual) — ticker not in our list
                          </label>
                          <input
                            type="number"
                            placeholder="Enter current price e.g. 42.50"
                            value={form.manualPrice || ""}
                            onChange={e => setForm({...form, manualPrice: e.target.value})}
                            onFocus={e => e.target.style.borderColor = C.orange}
                            onBlur={e => e.target.style.borderColor = "var(--border)"}
                            style={{ ...st.input, borderColor: form.manualPrice ? C.orange : "var(--border)" }}
                            step="0.01"
                          />
                          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text3)", marginTop:4 }}>
                            You can update this price anytime from the asset page.
                          </div>
                        </div>
                      )}
                    </div>

                    <button
                      onClick={handleSubmit}
                      disabled={!canSubmit}
                      style={{...st.addBtn, width:"100%", opacity: canSubmit ? 1 : 0.3,
                        cursor: canSubmit ? "pointer" : "not-allowed"}}>
                      {isNewAsset ? `+ ADD TRADE & CREATE ${tickerUp}` : "ADD TRADE"}
                    </button>
                  </div>
                </div>

              </div>
            </div>
          );
        })()}

        {/* ── TRADES TABLE IN DASHBOARD ── */}
        {activeAssetId && effectiveEntries.length > 0 && (
          <Collapsible id="trades_table" title="ALL TRADES" defaultOpen={false}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
              <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, letterSpacing:"0.3em", color:"#888" }}>
                {effectiveEntries.length} RECORDS
              </div>
              {clearConfirm ? (
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, color:"var(--text2)" }}>Delete all?</span>
                  <button onClick={async () => { await supabase.from("entries").delete().eq("user_id", user.id).eq("asset_id", activeAssetId); setEntries([]); setClearConfirm(false); }}
                    style={{ background:"rgba(224,62,26,0.12)", border:"1px solid #E03E1A", color:"#E03E1A", borderRadius:6, padding:"5px 14px", fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, cursor:"pointer" }}>
                    Yes, delete
                  </button>
                  <button onClick={() => setClearConfirm(false)}
                    style={{ background:"transparent", border:"1px solid var(--border)", color:"var(--text2)", borderRadius:6, padding:"5px 12px", fontFamily:"Montserrat,sans-serif", fontSize:11, cursor:"pointer" }}>
                    Cancel
                  </button>
                </div>
              ) : (
                <button onClick={() => setClearConfirm(true)}
                  style={{ background:"transparent", border:"1px solid var(--border)", color:"var(--text3)", borderRadius:6, padding:"5px 14px", fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, cursor:"pointer", letterSpacing:"0.1em" }}
                  onMouseEnter={e => { e.target.style.borderColor="#E03E1A"; e.target.style.color="#E03E1A"; }}
                  onMouseLeave={e => { e.target.style.borderColor="#1c1c1c"; e.target.style.color="#444"; }}>
                   CLEAR ALL
                </button>
              )}
            </div>
            <div style={st.tWrap}>
              <table style={st.table}>
                <thead>
                  <tr>
                    <SortTh label="Date"      colKey="date"     sortState={sortState} onSort={handleSort}/>
                    <SortTh label="Invested"  colKey="dollars"  sortState={sortState} onSort={handleSort}/>
                    <SortTh label={sharesLabel} colKey="shares"   sortState={sortState} onSort={handleSort}/>
                    <SortTh label="Buy Price" colKey="buyPrice" sortState={sortState} onSort={handleSort}/>
                    <th style={st.th}>Curr. Price</th>
                    <SortTh label="P&L $"     colKey="pnl"      sortState={sortState} onSort={handleSort}/>
                    <SortTh label="P&L %"     colKey="pnlPct"   sortState={sortState} onSort={handleSort}/>
                    <th style={st.th}/>
                  </tr>
                </thead>
                <tbody>
                  {(showAllTrades ? sortedEntries : sortedEntries.slice(-10)).map((entry, i) => {
                    const buyPrice = entry.shares > 0 ? entry.dollars/entry.shares : 0;
                    const epnl = hasCp ? entry.shares*cp - entry.dollars : null;
                    const epct = hasCp && entry.dollars > 0 ? (epnl/entry.dollars)*100 : null;
                    if (editId === entry.id) return (
                      <tr key={entry.id} style={{background:"var(--bg2)"}}>
                        <td style={st.td}><input type="date"   value={editForm.date}    onChange={e => setEditForm({...editForm,date:e.target.value})}    style={st.ei}/></td>
                        <td style={st.td}><input type="number" value={editForm.dollars}  onChange={e => setEditForm({...editForm,dollars:e.target.value})}  style={st.ei} step="0.01"/></td>
                        <td style={st.td}><input type="number" value={editForm.shares}   onChange={e => setEditForm({...editForm,shares:e.target.value})}   style={st.ei} step="0.0001"/></td>
                        <td style={st.td}/><td style={st.td}/><td style={st.td}/><td style={st.td}/>
                        <td style={st.td}>
                          <button onClick={saveEdit} style={st.saveBtn}></button>
                          <button onClick={() => setEditId(null)} style={st.cancelBtn}></button>
                        </td>
                      </tr>
                    );
                    return (
                      <tr key={entry.id} style={{background:i%2===0?"var(--row-even)":"var(--row-odd)"}}>
                        <td style={st.td}>
                          <div style={{display:"flex", alignItems:"center", gap:8}}>
                            {hasCp && <div style={{width:6, height:6, borderRadius:"50%", background:epnl>=0?"#5aad27":C.red, flexShrink:0}}/>}
                            {formatDate(entry.date)}
                          </div>
                        </td>
                        <td style={st.td}>{fmt$(entry.dollars)}</td>
                        <td style={st.td}>{entry.shares.toFixed(4)}</td>
                        <td style={{...st.td, color:C.orange, fontWeight:700}}>{fmt$(buyPrice)}</td>
                        <td style={{...st.td, color:hasCp?C.cream:"#333"}}>{hasCp?fmt$(cp):"—"}</td>
                        <td style={{...st.td, color:hasCp?pnlCol(epnl):"#333", fontWeight:700}}>{hasCp?fmt$(epnl):"—"}</td>
                        <td style={{...st.td, color:hasCp?pnlCol(epct):"#333", fontWeight:700}}>{hasCp?fmtPct(epct):"—"}</td>
                        <td style={st.td}>
                          <button onClick={() => startEdit(entry)} style={st.editBtn}></button>
                          {deleteConfirm===entry.id
                            ? <><button onClick={() => deleteEntry(entry.id)} style={st.delConfBtn}>Del</button><button onClick={() => setDeleteConfirm(null)} style={st.cancelBtn}></button></>
                            : <button onClick={() => setDeleteConfirm(entry.id)} style={st.delBtn}></button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{borderTop:`2px solid ${C.blackBorder}`}}>
                    <td style={{...st.td, fontWeight:700, color:C.cream}}>TOTAL</td>
                    <td style={{...st.td, fontWeight:700, color:C.cream}}>{fmt$(totalDollars)}</td>
                    <td style={{...st.td, fontWeight:700, color:C.cream}}>{totalShares.toFixed(4)}</td>
                    <td style={{...st.td, color:C.orange, fontWeight:700}}>{fmt$(avgPrice)}</td>
                    <td style={{...st.td, color:hasCp?C.cream:"#333"}}>{hasCp?fmt$(cp):"—"}</td>
                    <td style={{...st.td, fontWeight:700, fontSize:15, color:hasCp?pnlCol(portfolioPnl):"#333"}}>{hasCp?fmt$(portfolioPnl):"—"}</td>
                    <td style={{...st.td, fontWeight:700, fontSize:15, color:hasCp?pnlCol(portfolioPct):"#333"}}>{hasCp?fmtPct(portfolioPct):"—"}</td>
                    <td style={st.td}/>
                  </tr>
                </tfoot>
              </table>
            </div>
            {sortedEntries.length > 10 && (
              <button onClick={() => setShowAllTrades(s => !s)} style={{
                width:"100%", marginTop:10,
                background: showAllTrades ? "transparent" : "rgba(242,135,5,0.07)",
                border: `1px solid ${showAllTrades ? "var(--border)" : "rgba(242,135,5,0.3)"}`,
                borderRadius:8, padding:"10px",
                fontFamily:"Montserrat,sans-serif", fontSize:10, fontWeight:700,
                letterSpacing:"0.15em", color: showAllTrades ? "var(--text4)" : C.orange,
                cursor:"pointer", transition:"all 0.15s",
              }}>
                {showAllTrades ? `▲ SHOW LAST 10` : `▼ SHOW ALL ${sortedEntries.length} RECORDS`}
              </button>
            )}
          </Collapsible>
        )}

        {/* ── CALENDAR PAGE ── */}






        {/* ── ADMIN PAGE ── */}
        {activePage === "watchlist" && (
          <WatchlistPage supabase={supabase} user={user} isMobile={isMobile} demoItems={isDemo ? DEMO_WATCHLIST : null} />
        )}

        {activePage === "retirement" && (
          <RetirementPage portfolioTotalValue={portfolioTotalValue} isMobile={isMobile} />
        )}

        {activePage === "dividends" && (
          <DividendPage supabase={supabase} user={user} isMobile={isMobile} />
        )}

        {activePage === "markets" && (
          marketsSelected
            ? <MarketTickerDetail
                symbol={marketsSelected.symbol}
                name={marketsSelected.name}
                mode={marketsSelected.mode}
                isMobile={isMobile}
                onBack={closeMarketTicker}
              />
            : <CryptoMarketsPage
                isMobile={isMobile}
                onSelectTicker={t => openMarketTicker(t)}
              />
        )}

        {activePage === "admin" && (user?.email === "jeliodimitrov@gmail.com" || user?.user_metadata?.email === "jeliodimitrov@gmail.com") && (
          <>
          {isMobile && (
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16, paddingBottom:12, borderBottom:"1px solid var(--border)" }}>
              <button onClick={() => navigateTo("portfolio")}
                style={{ background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:7,
                  width:34, height:34, display:"flex", alignItems:"center", justifyContent:"center",
                  cursor:"pointer", flexShrink:0, color:"var(--text3)" }}>
                <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
                  <path d="M7.5 2L3.5 6L7.5 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:15, fontWeight:900, color:"var(--text)" }}>⚙️ Admin</span>
            </div>
          )}
          <AdminPage supabase={supabase} onBannerChange={val => setDashBanner(val)} />
          </>
        )}



        {/* ── PORTFOLIO PAGE ── */}
        {activePage === "portfolio" && !activeAssetId && (
          <PortfolioPage assets={effectiveAssets} user={user} dashHidden={dashHidden} setDashHidden={setDashHidden} dashBanner={dashBanner} isMobile={isMobile} />
        )}





        </div>
        </div>

        {/* ── NEW ASSET MODAL ── */}
        {showAssetModal && (
          <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.8)", zIndex:1000,
            display:"flex", alignItems: isMobile ? "flex-end" : "center", justifyContent:"center" }}
            onClick={e => { if (e.target === e.currentTarget) { setShowTradeModal(false); setTradeTickerInput(""); setForm(emptyForm); } }}>
            <div style={{ background:"var(--bg2)", border:`1px solid ${C.orange}`,
              borderRadius: isMobile ? "18px 18px 0 0" : 12,
              padding: isMobile ? "24px 20px 32px" : "32px 36px",
              width: isMobile ? "100%" : 380, fontFamily:"Montserrat,sans-serif",
              maxHeight: isMobile ? "85vh" : "none", overflowY: isMobile ? "auto" : "visible" }}>
              <div style={{ fontSize:12, fontWeight:700, letterSpacing:"0.3em", color:C.orange, marginBottom:24 }}>
                NEW ASSET
              </div>

              {/* Stock / Crypto toggle */}
              <div style={{ display:"flex", gap:8, marginBottom:20 }}>
                {["stock","crypto"].map(type => (
                  <button key={type} onClick={() => { setNewAssetType(type); setNewTicker(""); }}
                    style={{
                      flex:1, padding:"9px", borderRadius:8, cursor:"pointer",
                      fontFamily:"Montserrat,sans-serif", fontSize:10, fontWeight:700, letterSpacing:"0.15em",
                      border:`1px solid ${newAssetType === type ? C.orange : "var(--border)"}`,
                      background: newAssetType === type ? "rgba(242,135,5,0.1)" : "transparent",
                      color: newAssetType === type ? C.orange : "var(--text3)",
                      transition:"all 0.15s",
                    }}>
                    {type === "stock" ? "📊 STOCK" : "🪙 CRYPTO"}
                  </button>
                ))}
              </div>

              <div style={{ display:"flex", flexDirection:"column", gap:16, marginBottom:24 }}>
                <div>
                  <label style={{ fontSize:8, fontWeight:700, letterSpacing:"0.25em", color:"var(--text4)", display:"block", marginBottom:8 }}>
                    {newAssetType === "crypto" ? "TOKEN" : "TICKER"}
                  </label>
                  <input autoFocus value={newTicker} onChange={e => setNewTicker(e.target.value.toUpperCase())}
                    placeholder={newAssetType === "crypto" ? "e.g. BTC, ETH, SOL" : "e.g. TSLA, AAPL"}
                    onKeyDown={e => e.key === "Enter" && createAsset()}
                    style={{ width:"100%", background:"var(--bg4)", border:`1px solid ${newTicker ? C.orange : "var(--border)"}`,
                      borderRadius:8, padding:"12px 16px", color:"var(--text)", fontFamily:"Montserrat,sans-serif",
                      fontSize:20, fontWeight:900, outline:"none", letterSpacing:"0.1em", colorScheme:"dark" }}/>
                </div>
                <div>
                  <label style={{ fontSize:8, fontWeight:700, letterSpacing:"0.25em", color:"var(--text4)", display:"block", marginBottom:8 }}>NAME (optional)</label>
                  <input value={newName} onChange={e => setNewAssetName(e.target.value)}
                    placeholder={newAssetType === "crypto" ? "e.g. Bitcoin" : "e.g. Tesla"}
                    onKeyDown={e => e.key === "Enter" && createAsset()}
                    style={{ width:"100%", background:"var(--bg4)", border:"1px solid var(--border)",
                      borderRadius:8, padding:"12px 16px", color:"var(--text)", fontFamily:"Montserrat,sans-serif",
                      fontSize:14, outline:"none", colorScheme:"dark" }}/>
                </div>
              </div>
              <div style={{ display:"flex", gap:10 }}>
                <button onClick={createAsset} disabled={!newTicker.trim()}
                  style={{ flex:1, background: newTicker.trim() ? C.orange : "#333", color: newTicker.trim() ? "#000" : "#666",
                    border:"none", borderRadius:8, padding:"13px", fontFamily:"Montserrat,sans-serif",
                    fontSize:12, fontWeight:700, letterSpacing:"0.2em", cursor: newTicker.trim() ? "pointer" : "not-allowed" }}>
                  CREATE ASSET
                </button>
                <button onClick={() => { setShowTradeModal(false); setTradeTickerInput(""); setForm(emptyForm); }}
                  style={{ background:"transparent", border:"1px solid var(--border)", color:"var(--text3)",
                    borderRadius:8, padding:"13px 20px", fontFamily:"Montserrat,sans-serif", fontSize:12, cursor:"pointer" }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── PAYWALL MODAL ── */}
      {showPaywall && (
        <div onClick={() => setShowPaywall(false)}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.85)", zIndex:1000,
            display:"flex", alignItems: isMobile ? "flex-end" : "center", justifyContent:"center", padding: isMobile ? 0 : 16 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background:"#0a0a0a", border:"1px solid #222",
              borderRadius: isMobile ? "18px 18px 0 0" : 14,
              padding: isMobile ? "28px 20px 36px" : "40px 32px",
              width:"100%", maxWidth: isMobile ? "100%" : 440, textAlign:"center", fontFamily:"Montserrat,sans-serif" }}>
            <div style={{ fontSize:28, marginBottom:12 }}>🔒</div>
            <div style={{ fontSize:14, fontWeight:800, letterSpacing:"0.05em", color:"var(--text)", marginBottom:8 }}>
              {userPlan === "free" ? "FREE PLAN LIMIT REACHED" : "PRO PLAN LIMIT REACHED"}
            </div>
            <div style={{ fontSize:12, color:"var(--text3)", marginBottom:32, lineHeight:1.6 }}>
              {userPlan === "free"
                ? "The free plan allows 1 asset. Upgrade to PRO for up to 10 assets."
                : "PRO plan allows up to 10 assets. Upgrade to UNLIMITED for unlimited assets."}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              {userPlan === "free" && (
                <button onClick={async () => {
                  const res = await fetch("/api/create-checkout", {
                    method:"POST",
                    headers:{"Content-Type":"application/json"},
                    body: JSON.stringify({
                      priceId: "price_1TFaflHc8CTKusxmYfo4zYJQ",
                      userId: user.id,
                      userEmail: user.email,
                    })
                  });
                  const { url } = await res.json();
                  if (url) window.location.href = url;
                }} style={{ background:C.orange, color:"#000", border:"none", borderRadius:8,
                  padding:"14px", fontSize:12, fontWeight:800, letterSpacing:"0.2em", cursor:"pointer" }}>
                  UPGRADE TO PRO — $3.99/mo
                </button>
              )}
              <button onClick={async () => {
                const res = await fetch("/api/create-checkout", {
                  method:"POST",
                  headers:{"Content-Type":"application/json"},
                  body: JSON.stringify({
                    priceId: "price_1TFabTHc8CTKusxmaf3KTf6T",
                    userId: user.id,
                    userEmail: user.email,
                  })
                });
                const { url } = await res.json();
                if (url) window.location.href = url;
              }} style={{ background:"#1a1a1a", color:C.orange, border:`1px solid ${C.orange}`,
                borderRadius:8, padding:"14px", fontSize:12, fontWeight:800, letterSpacing:"0.2em", cursor:"pointer" }}>
                UPGRADE TO UNLIMITED — $9.99/mo
              </button>
              <button onClick={() => setShowPaywall(false)}
                style={{ background:"transparent", border:"1px solid var(--border)", color:"var(--text3)",
                  borderRadius:8, padding:"12px", fontSize:11, cursor:"pointer", letterSpacing:"0.1em" }}>
                MAYBE LATER
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE ASSET MODAL ── */}
      {showDeleteModal && (
        <div onClick={() => setShowDeleteModal(false)}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.85)", zIndex:600,
            display:"flex", alignItems: isMobile ? "flex-end" : "center", justifyContent:"center", padding: isMobile ? 0 : 20 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background:"#0a0a0a", border:"1px solid #222",
              borderRadius: isMobile ? "18px 18px 0 0" : 14,
              padding: isMobile ? "28px 20px 36px" : "36px 32px",
              width:"100%", maxWidth: isMobile ? "100%" : 400, textAlign:"center" }}>

            <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:13, fontWeight:700,
              color:"#555", letterSpacing:"0.2em", marginBottom:20 }}>
              DELETE {activeAsset?.ticker}
            </div>

            <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:22, fontWeight:900,
              color:"#fff", lineHeight:1.3, marginBottom:28 }}>
              This action cannot<br/>be undone.
            </div>

            <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, color:"#555",
              lineHeight:1.7, marginBottom:32 }}>
              All trades for <span style={{ color:"#888" }}>{activeAsset?.ticker}</span> will be permanently deleted.
            </div>

            <button onClick={async () => {
                setShowDeleteModal(false);
                await deleteAsset(activeAssetId);
                navigateTo("portfolio");
              }}
              style={{ width:"100%", padding:"14px 0", borderRadius:8, cursor:"pointer",
                background:C.red, border:"none", color:"#fff",
                fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700,
                letterSpacing:"0.15em", marginBottom:12 }}>
              DELETE FOREVER
            </button>

            <button onClick={() => setShowDeleteModal(false)}
              style={{ width:"100%", padding:"12px 0", borderRadius:8, cursor:"pointer",
                background:"transparent", border:"1px solid #222",
                color:"#555", fontFamily:"Montserrat,sans-serif",
                fontSize:11, fontWeight:700, letterSpacing:"0.1em" }}>
              CANCEL
            </button>
          </div>
        </div>
      )}

      {/* ── VERSION FOOTER ── */}
      {!isMobile && (
        <div style={{
          margin:"0 24px 24px",
          padding:"14px 24px",
          background:"#161616", border:"1px solid #2c2c2c",
          borderRadius:10,
          display:"flex", alignItems:"center", justifyContent:"space-between",
        }}>
          <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700,
            letterSpacing:"0.15em", color:"#888" }}>
            Compaundia © 2025 · All rights reserved · v1.0.0
          </span>
          <a href="https://x.com/Compaundia" target="_blank" rel="noopener noreferrer"
            style={{
              display:"flex", alignItems:"center", gap:5,
              fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700,
              letterSpacing:"0.12em", color:"#888",
              textDecoration:"none", transition:"color 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.color = "#F28705"}
            onMouseLeave={e => e.currentTarget.style.color = "#888"}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.912-5.622Zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            FOLLOW US
          </a>
        </div>
      )}

      {/* ── MOBILE BOTTOM NAV ── */}
      {isMobile && (
        <>
        {/* Drawer backdrop */}
        {mobileDrawer && (
          <div onClick={() => setMobileDrawer(null)} style={{
            position:"fixed", inset:0, zIndex:250,
            background:"rgba(0,0,0,0.55)", backdropFilter:"blur(2px)",
          }}/>
        )}

        {/* Assets drawer */}
        {mobileDrawer === "assets" && (
          <div style={{
            position:"fixed", bottom:62, left:0, right:0, zIndex:260,
            background:"#000", borderTop:"none",
            borderRadius:"16px 16px 0 0", padding:"16px 0 8px",
            maxHeight:"55vh", overflowY:"auto",
            animation:"slideUp 0.28s cubic-bezier(0.34,1.2,0.64,1)",
          }}>
            <div style={{ display:"flex", justifyContent:"center", paddingTop:6, paddingBottom:4 }}>
              <div style={{ width:36, height:4, borderRadius:2, background:"rgba(255,255,255,0.15)" }}/>
            </div>
            <div style={{ padding:"0 16px 12px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:800, letterSpacing:"0.25em", color:"#666" }}>ASSETS</span>
              <button onClick={() => { setMobileDrawer(null); setShowAssetModal(true); }}
                style={{ background:`rgba(242,135,5,0.12)`, border:`1px solid ${C.orange}`, color:C.orange,
                  borderRadius:6, padding:"5px 12px", fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, cursor:"pointer" }}>
                + ADD
              </button>
            </div>
            {assets.length === 0 && (
              <div style={{ padding:"20px 16px", textAlign:"center", fontFamily:"Montserrat,sans-serif", fontSize:12, color:"#555" }}>
                No assets yet. Add your first one!
              </div>
            )}
            {assets.map(asset => {
              const isActive = asset.id === activeAssetId;
              return (
                <button key={asset.id} onClick={() => { switchAsset(asset.id); setMobileDrawer(null); }}
                  style={{
                    width:"100%", background:"transparent",
                    border:"none", padding:"14px 20px", cursor:"pointer",
                    display:"flex", alignItems:"center", justifyContent:"space-between",
                    borderBottom:"1px solid rgba(255,255,255,0.05)",
                  }}>
                  <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                    <div style={{ width:36, height:36, borderRadius:8,
                      background:"rgba(255,255,255,0.05)",
                      border:"1px solid rgba(255,255,255,0.08)",
                      display:"flex", alignItems:"center", justifyContent:"center" }}>
                      <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, fontWeight:900,
                        color: isActive ? C.orange : "#888" }}>{asset.ticker.slice(0,4)}</span>
                    </div>
                    <div style={{ textAlign:"left" }}>
                      <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:13, fontWeight:700,
                        color: isActive ? C.orange : "#fff" }}>{asset.ticker}</div>
                      {asset.name && asset.name !== asset.ticker && (
                        <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:10, color:"#555" }}>
                          {asset.name.length > 22 ? asset.name.slice(0,22)+"…" : asset.name}
                        </div>
                      )}
                    </div>
                  </div>
                  {isActive && <span style={{ color:C.orange, fontSize:16 }}>✓</span>}
                </button>
              );
            })}
          </div>
        )}

        {/* More drawer */}
        {mobileDrawer === "more" && (
          <div style={{
            position:"fixed", bottom:62, left:0, right:0, zIndex:260,
            background:"#000", borderTop:"none",
            borderRadius:"16px 16px 0 0", padding:"16px 0 8px",
            animation:"slideUp 0.28s cubic-bezier(0.34,1.2,0.64,1)",
          }}>
            <div style={{ display:"flex", justifyContent:"center", paddingTop:6, paddingBottom:4 }}>
              <div style={{ width:36, height:4, borderRadius:2, background:"rgba(255,255,255,0.15)" }}/>
            </div>
            <div style={{ padding:"0 16px 12px" }}>
              <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:800, letterSpacing:"0.25em", color:"#666" }}>MORE</span>
            </div>
            {/* User email */}
            {user && (
              <div style={{ padding:"12px 20px", borderBottom:"1px solid rgba(255,255,255,0.05)",
                display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:"#555", letterSpacing:"0.08em" }}>LOGGED IN AS</div>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:12, color:"#888", marginTop:2 }}>{user.email}</div>
                </div>
                <button onClick={() => { supabase.auth.signOut(); setMobileDrawer(null); }}
                  style={{ background:"rgba(224,62,26,0.1)", border:"1px solid rgba(224,62,26,0.4)",
                    color:C.red, borderRadius:7, padding:"7px 14px", fontFamily:"Montserrat,sans-serif",
                    fontSize:10, fontWeight:700, cursor:"pointer" }}>LOGOUT</button>
              </div>
            )}
            {/* Theme */}
            <button onClick={() => { setTheme(t => t === "dark" ? "light" : "dark"); }}
              style={{ width:"100%", background:"transparent", border:"none", padding:"14px 20px",
                cursor:"pointer", display:"flex", alignItems:"center", gap:14,
                borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
              <div style={{ width:40, height:40, borderRadius:10, background:"rgba(255,255,255,0.05)",
                border:"1px solid rgba(255,255,255,0.08)", display:"flex", alignItems:"center",
                justifyContent:"center", fontSize:20, flexShrink:0 }}>
                {theme === "dark" ? "☀️" : "🌙"}
              </div>
              <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:13, fontWeight:700, color:"#fff" }}>
                {theme === "dark" ? "Light Mode" : "Dark Mode"}
              </span>
            </button>
            
            {/* Admin */}
            {(user?.email === "jeliodimitrov@gmail.com" || user?.user_metadata?.email === "jeliodimitrov@gmail.com") && (
              <button onClick={() => { navigateTo("admin"); persistAssetId(null); setMobileDrawer(null); }}
                style={{ width:"100%", background:"transparent",
                  border:"none", padding:"14px 20px", cursor:"pointer",
                  display:"flex", alignItems:"center", gap:14 }}>
                <div style={{ width:40, height:40, borderRadius:10,
                  background:"rgba(255,255,255,0.05)",
                  border:"1px solid rgba(255,255,255,0.08)",
                  display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>⚙️</div>
                <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:13, fontWeight:700,
                  color: activePage === "admin" ? C.orange : "#fff" }}>Admin</span>
              </button>
            )}
          </div>
        )}

        {/* Bottom tab bar */}
        <div style={{
          position:"fixed", bottom:0, left:0, right:0, zIndex:300,
          background:"rgba(0,0,0,0.9)",
          borderTop:"1px solid rgba(255,255,255,0.06)",
          backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)",
          paddingBottom:"env(safe-area-inset-bottom)",
          display:"flex", alignItems:"stretch", height:62,
        }}>
          {/* Dashboard */}
          {(() => {
            const isActive = activePage === "portfolio" && !activeAssetId;
            return (
              <button onClick={() => { navigateTo("portfolio"); persistAssetId(null); setMobileDrawer(null); }}
                style={{ flex:1, background:"transparent", border:"none", cursor:"pointer",
                  display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:3,
                  borderTop: isActive ? `2px solid ${C.orange}` : "2px solid transparent" }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                  stroke={isActive ? C.orange : "#888"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="9" height="11" rx="2"/><rect x="13" y="2" width="9" height="7" rx="2"/>
                  <rect x="2" y="15" width="9" height="7" rx="2"/><rect x="13" y="11" width="9" height="11" rx="2"/>
                </svg>
                <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:400, letterSpacing:"0.06em",
                  color: isActive ? C.orange : "#888" }}>Dashboard</span>
              </button>
            );
          })()}

          {/* Assets */}
          {(() => {
            const isActive = !!activeAssetId || mobileDrawer === "assets";
            return (
              <button onClick={() => setMobileDrawer(d => d === "assets" ? null : "assets")}
                style={{ flex:1, background:"transparent", border:"none", cursor:"pointer",
                  display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:3,
                  borderTop: isActive ? `2px solid ${C.orange}` : "2px solid transparent" }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                  stroke={isActive ? C.orange : "#888"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
                <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:400, letterSpacing:"0.06em",
                  color: isActive ? C.orange : "#888" }}>Portfolio</span>
              </button>
            );
          })()}

          {/* More */}
          {(() => {
            const isActive = activePage === "admin" || mobileDrawer === "more";
            return (
              <button onClick={() => setMobileDrawer(d => d === "more" ? null : "more")}
                style={{ flex:1, background:"transparent", border:"none", cursor:"pointer",
                  display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:3,
                  borderTop: isActive ? `2px solid ${C.orange}` : "2px solid transparent" }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                  stroke={isActive ? C.orange : "#888"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/>
                </svg>
                <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, letterSpacing:"0.06em",
                  color: isActive ? C.orange : "#888" }}>More</span>
              </button>
            );
          })()}

          {/* Add Trade — FAB far right */}
          <button onClick={() => { setShowTradeModal(true); setTradeTickerInput(""); setTradeType(null); setMobileDrawer(null); }}
            style={{ width:62, flexShrink:0, background:"transparent", border:"none", cursor:"pointer",
              display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:3,
              borderTop:"2px solid transparent" }}>
            <div style={{ width:40, height:40, borderRadius:"50%", background:C.orange,
              display:"flex", alignItems:"center", justifyContent:"center",
              boxShadow:`0 4px 14px rgba(242,135,5,0.45)` }}>
              <span style={{ fontSize:22, fontWeight:900, color:"#000", lineHeight:1 }}>+</span>
            </div>
          </button>
        </div>
        </>
      )}
      {/* Login modal — shown when not authenticated */}
      {showLoginModal && <LoginModal />}
    </>
  );
}

const st = {
  page:       { minHeight:"100vh", background:"var(--bg)", padding:"36px 24px 60px", fontFamily:"Montserrat,sans-serif", color:"var(--text)" },
  header:     { marginBottom:24 },
  tickerRow:  { display:"flex", alignItems:"center", gap:16 },
  ticker:     { fontSize:68, fontWeight:900, color:"var(--text)", letterSpacing:"-0.03em", lineHeight:1 },
  saving:     { fontSize:11, color:"#F28705", letterSpacing:"0.2em", fontWeight:400 },
  sub:        { fontSize:11, fontWeight:700, letterSpacing:"0.45em", color:"var(--text4)", textTransform:"uppercase", marginTop:6 },
  topRow:     { display:"flex", gap:16, marginBottom:24, alignItems:"stretch", minHeight:220 },
  priceBlock: { background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:10, padding:"22px 26px", display:"flex", flexDirection:"column", justifyContent:"flex-start", minWidth:230, flexShrink:0 },
  priceLabel: { fontSize:11, fontWeight:700, letterSpacing:"0.3em", color:"#888", marginBottom:10 },
  spinner:    { width:14, height:14, border:"2px solid var(--border2)", borderTopColor:"#F28705", borderRadius:"50%", animation:"spin 0.8s linear infinite", flexShrink:0 },
  priceVal:   { fontSize:34, fontWeight:900, color:"var(--text)", letterSpacing:"-0.02em", fontFamily:"Montserrat,sans-serif" },
  beBox:      { marginTop:18, paddingTop:16, borderTop:"1px solid var(--border)" },
  beLabel:    { fontSize:11, fontWeight:700, letterSpacing:"0.3em", color:"#888", marginBottom:6, fontFamily:"Montserrat,sans-serif" },
  beVal:      { fontSize:22, fontWeight:700, color:"var(--text)", fontFamily:"Montserrat,sans-serif" },
  refreshBtn: { background:"transparent", border:"1px solid var(--border)", color:"#F28705", borderRadius:7, padding:"7px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"Montserrat,sans-serif", letterSpacing:"0.1em", alignSelf:"flex-start" },
  chartBlock: { flex:1, background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:10, padding:"16px 16px 8px", display:"flex", flexDirection:"column", minHeight:220, overflow:"hidden", width:0, minWidth:0 },
  chartLabel: { fontSize:11, fontWeight:700, letterSpacing:"0.3em", color:"#888", marginBottom:4, flexShrink:0, fontFamily:"Montserrat,sans-serif" },
  cards:      { display:"flex", gap:12, marginBottom:24, flexWrap:"wrap" },
  card:       { flex:1, minWidth:120, background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:8, padding:"10px 14px" },
  clabel:     { fontSize:8, fontWeight:700, letterSpacing:"0.2em", color:"var(--text4)", textTransform:"uppercase", marginBottom:4, fontFamily:"Montserrat,sans-serif" },
  cval:       { fontSize:15, fontWeight:700, color:"var(--text)", fontFamily:"Montserrat,sans-serif" },
  formBox:    { background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:10, padding:"24px", marginBottom:24 },
  formHead:   { fontSize:10, fontWeight:700, letterSpacing:"0.3em", color:"#F28705", marginBottom:20, fontFamily:"Montserrat,sans-serif" },
  formGrid:   { display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(160px, 1fr))", gap:16, marginBottom:20 },
  field:      { display:"flex", flexDirection:"column", gap:8 },
  flabel:     { fontSize:10, fontWeight:700, letterSpacing:"0.18em", color:"#888", textTransform:"uppercase", fontFamily:"Montserrat,sans-serif", marginBottom:2 },
  input:      { background:"#0d0d0d", border:"1px solid #2a2a2a", borderRadius:10, padding:"13px 16px", color:"#f0f0f0", fontFamily:"Montserrat,sans-serif", fontSize:15, fontWeight:500, outline:"none", colorScheme:"dark", transition:"border-color 0.15s" },
  calcVal:    { background:"#0d0d0d", border:"1px solid #2a2a2a", borderRadius:10, padding:"13px 16px", fontFamily:"Montserrat,sans-serif", fontSize:15, fontWeight:700, color:"#F28705", minHeight:48, display:"flex", alignItems:"center" },
  addBtn:     { background:C.orange, color:"#000", border:"none", borderRadius:10, padding:"15px 32px", fontFamily:"Montserrat,sans-serif", fontSize:12, fontWeight:800, letterSpacing:"0.2em", cursor:"pointer" },
  tWrap:      { overflowX:"auto", borderRadius:10, border:"1px solid var(--border)" },
  table:      { width:"100%", borderCollapse:"collapse" },
  th:         { padding:"13px 16px", fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase", color:"var(--text4)", textAlign:"left", background:"var(--bg3)", whiteSpace:"nowrap", borderBottom:"1px solid var(--border)" },
  td:         { padding:"13px 16px", fontFamily:"Montserrat,sans-serif", fontSize:13, fontWeight:400, color:"var(--text)", borderBottom:"1px solid var(--border)", whiteSpace:"nowrap" },
  ei:         { background:"var(--bg2)", border:"1px solid #F28705", borderRadius:5, padding:"6px 10px", color:"var(--text)", fontFamily:"Montserrat,sans-serif", fontSize:13, outline:"none", width:"100%", colorScheme:"dark" },
  editBtn:    { background:"transparent", border:"1px solid var(--border)", color:"var(--text3)", borderRadius:5, padding:"4px 9px", cursor:"pointer", marginRight:5, fontFamily:"Montserrat,sans-serif", fontSize:13 },
  delBtn:     { background:"transparent", border:"1px solid var(--border)", color:"var(--text3)", borderRadius:5, padding:"4px 9px", cursor:"pointer", fontFamily:"Montserrat,sans-serif", fontSize:12 },
  saveBtn:    { background:"rgba(242,135,5,0.12)", border:"1px solid #F28705", color:"#F28705", borderRadius:5, padding:"4px 10px", cursor:"pointer", marginRight:5, fontFamily:"Montserrat,sans-serif", fontSize:13, fontWeight:700 },
  delConfBtn: { background:"rgba(224,62,26,0.12)", border:"1px solid #E03E1A", color:"#E03E1A", borderRadius:5, padding:"4px 10px", cursor:"pointer", marginRight:5, fontFamily:"Montserrat,sans-serif", fontSize:12, fontWeight:700 },
  cancelBtn:  { background:"transparent", border:"1px solid var(--border)", color:"var(--text3)", borderRadius:5, padding:"4px 9px", cursor:"pointer", fontFamily:"Montserrat,sans-serif", fontSize:12 },
};

export default function PortfolioTracker() {
  return (
    <ErrorBoundary>
      <PortfolioTrackerInner />
    </ErrorBoundary>
  );
}
